(function () {
  "use strict";

  var DEFAULTS = {
    bgThreshold: 245,
    biyeDepthPct: 0.014,
    bordurDepthPct: 0.034,
    textureScale: 1,
    bodyDetailStrength: 1,
    textureDetailStrength: 0,
    textureChromaDetailStrength: 0,
    textureToneStrength: 1,
    textureColorStrength: 1,
    textureLowPassRatio: 0.1,
    textureContrastRatio: 0.62,
    maxTextureContrastRatio: 1.04,
    maxTextureContrastBoost: 1.65,
    maxColorCorrection: 18,
    renderLightnessLift: 0,
    renderPipeline: "",
    textureRepeatX: 2.2,
    textureRepeatY: 3.15,
    textureDetailMix: 0.85,
    textureShadingStrength: 0.18,
    textureSharpen: 0.42,
    textureMicroDetailStrength: 0,
    textureMicroDetailLowPassRatio: 0.055,
    zoomTextureMicroDetailStrength: 0,
    textureColorLock: 1,
    maxCanvasWidth: 1600,
    maxCanvasHeight: 2200
  };
  var BUILD = "v269-region-biye-accent";
  window.__MASSIMO_OZEL_OLCU_PREVIEW_BUILD__ = BUILD;
  var TEXTURE_SOURCE_MAX_INSET = 0.32;
  var AR_CLEAN_TEXTURE_MIN_INSET = 0.38;
  var AR_CLEAN_TEXTURE_MAX_INSET = 0.44;
  var EDGE_CM_DEFAULTS = {
    biye: 3,
    bordur: 6,
    ciftOuter: 4,
    ciftInner: 2
  };

  function loadImage(src) {
    return new Promise(function (resolve, reject) {
      if (!src) return reject(new Error("Missing image source"));
      var img = new Image();
      if (/^(https?:)?\/\//i.test(src)) img.crossOrigin = "anonymous";
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error("Image load failed: " + src)); };
      img.src = src;
    });
  }

  function clamp(v, min, max) {
    return Math.max(min, Math.min(max, v));
  }

  function finiteNumber(value, fallback) {
    var n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  }

  function arCleanTextureOptions(options, edgeType) {
    var out = Object.assign({}, options || {});
    out.__massimoArCleanTexture = false;
    return out;
  }

  function brightness(r, g, b) {
    return (r + g + b) / 3;
  }

  function sampleBackground(data, w, h) {
    var step = Math.max(1, Math.round(Math.min(w, h) / 80));
    var r = 0;
    var g = 0;
    var b = 0;
    var n = 0;

    function add(x, y) {
      var i = (y * w + x) * 4;
      r += data[i];
      g += data[i + 1];
      b += data[i + 2];
      n++;
    }

    for (var x = 0; x < w; x += step) {
      add(x, 0);
      add(x, h - 1);
    }
    for (var y = 0; y < h; y += step) {
      add(0, y);
      add(w - 1, y);
    }

    if (!n) return { r: 255, g: 255, b: 255, br: 255 };
    r = r / n;
    g = g / n;
    b = b / n;
    return { r: r, g: g, b: b, br: brightness(r, g, b) };
  }

  function isBackgroundPixel(data, idx, bg, threshold) {
    var r = data[idx];
    var g = data[idx + 1];
    var b = data[idx + 2];
    var max = Math.max(r, g, b);
    var min = Math.min(r, g, b);
    var br = brightness(r, g, b);
    var neutral = (max - min) < 34;
    var brightBg = br >= threshold && neutral;
    var dr = r - bg.r;
    var dg = g - bg.g;
    var db = b - bg.b;
    var closeToEdgeColor = (dr * dr + dg * dg + db * db) < 34 * 34;
    return brightBg || (closeToEdgeColor && br >= bg.br - 34);
  }

  function fitSize(w, h, maxW, maxH) {
    var s = Math.min(maxW / Math.max(1, w), maxH / Math.max(1, h), 1);
    return {
      width: Math.max(1, Math.round(w * s)),
      height: Math.max(1, Math.round(h * s)),
      scale: s
    };
  }

  function analyzeCarpet(canvas, ctx, threshold) {
    var w = canvas.width;
    var h = canvas.height;
    var img;
    try {
      img = ctx.getImageData(0, 0, w, h);
    } catch (e) {
      return fallbackBounds(w, h);
    }

    var data = img.data;
    var bg = sampleBackground(data, w, h);
    var bgMask = new Uint8Array(w * h);
    var queue = [];
    var head = 0;

    function seed(x, y) {
      var p = y * w + x;
      if (bgMask[p]) return;
      if (!isBackgroundPixel(data, p * 4, bg, threshold)) return;
      bgMask[p] = 1;
      queue.push(p);
    }

    for (var sx = 0; sx < w; sx++) {
      seed(sx, 0);
      seed(sx, h - 1);
    }
    for (var sy = 1; sy < h - 1; sy++) {
      seed(0, sy);
      seed(w - 1, sy);
    }

    while (head < queue.length) {
      var p = queue[head++];
      var px = p % w;
      var py = Math.floor(p / w);
      for (var dy = -1; dy <= 1; dy++) {
        for (var dx = -1; dx <= 1; dx++) {
          if (!dx && !dy) continue;
          var nx = px + dx;
          var ny = py + dy;
          if (nx < 0 || nx >= w || ny < 0 || ny >= h) continue;
          var ni = ny * w + nx;
          if (bgMask[ni]) continue;
          if (isBackgroundPixel(data, ni * 4, bg, threshold)) {
            bgMask[ni] = 1;
            queue.push(ni);
          }
        }
      }
    }

    var minX = w;
    var minY = h;
    var maxX = 0;
    var maxY = 0;
    var count = 0;
    var carpetMask = new Uint8Array(w * h);

    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        var maskIndex = y * w + x;
        if (!bgMask[maskIndex]) {
          carpetMask[maskIndex] = 1;
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
          count++;
        }
      }
    }

    var sampled = Math.max(1, w * h);
    var coverage = count / sampled;
    if (coverage < 0.04 || coverage > 0.92 || minX >= maxX || minY >= maxY) {
      return fallbackBounds(w, h);
    }

    var pad = Math.round(Math.min(w, h) * 0.01);
    return {
      x: clamp(minX - pad, 0, w - 1),
      y: clamp(minY - pad, 0, h - 1),
      w: clamp(maxX - minX + pad * 2, 1, w),
      h: clamp(maxY - minY + pad * 2, 1, h),
      coverage: coverage,
      backgroundCoverage: queue.length / sampled,
      mask: carpetMask,
      maskWidth: w,
      maskHeight: h,
      fallback: false
    };
  }

  function fallbackBounds(w, h) {
    var marginX = Math.round(w * 0.08);
    var marginY = Math.round(h * 0.08);
    return {
      x: marginX,
      y: marginY,
      w: Math.max(1, w - marginX * 2),
      h: Math.max(1, h - marginY * 2),
      coverage: 1,
      fallback: true
    };
  }

  function expandBounds(bounds, canvasW, canvasH, pct) {
    if (!bounds) return bounds;
    var grow = Math.round(Math.min(bounds.w, bounds.h) * pct);
    var x = clamp(bounds.x - grow, 0, canvasW - 1);
    var y = clamp(bounds.y - grow, 0, canvasH - 1);
    var maxRight = clamp(bounds.x + bounds.w + grow, 1, canvasW);
    var maxBottom = clamp(bounds.y + bounds.h + grow, 1, canvasH);
    return Object.assign({}, bounds, {
      x: x,
      y: y,
      w: Math.max(1, maxRight - x),
      h: Math.max(1, maxBottom - y)
    });
  }

  function lineContentRatio(data, w, h, bg, threshold, axis, pos) {
    var hits = 0;
    var total = 0;
    var step = Math.max(1, Math.round(Math.min(w, h) / 360));
    if (axis === "x") {
      for (var y = 0; y < h; y += step) {
        total++;
        if (!isBackgroundPixel(data, (y * w + pos) * 4, bg, threshold)) hits++;
      }
    } else {
      for (var x = 0; x < w; x += step) {
        total++;
        if (!isBackgroundPixel(data, (pos * w + x) * 4, bg, threshold)) hits++;
      }
    }
    return total ? hits / total : 0;
  }

  function firstContentLine(data, w, h, bg, threshold, axis, fromEnd) {
    var limit = axis === "x" ? w : h;
    var stable = 0;
    var candidate = null;
    var minRatio = 0.08;

    for (var i = 0; i < limit; i++) {
      var pos = fromEnd ? limit - 1 - i : i;
      var ratio = lineContentRatio(data, w, h, bg, threshold, axis, pos);
      if (ratio >= minRatio) {
        if (candidate === null) candidate = pos;
        stable++;
        if (stable >= 2) return candidate;
      } else {
        candidate = null;
        stable = 0;
      }
    }
    return fromEnd ? limit - 1 : 0;
  }

  function detectRectCarpetBoundsFromImage(imageData, canvasW, canvasH, threshold) {
    if (!imageData || !imageData.data || !canvasW || !canvasH) return null;
    var data = imageData.data;
    var bg = sampleBackground(data, canvasW, canvasH);
    var x1 = firstContentLine(data, canvasW, canvasH, bg, threshold, "x", false);
    var x2 = firstContentLine(data, canvasW, canvasH, bg, threshold, "x", true);
    var y1 = firstContentLine(data, canvasW, canvasH, bg, threshold, "y", false);
    var y2 = firstContentLine(data, canvasW, canvasH, bg, threshold, "y", true);
    if (x2 <= x1 || y2 <= y1) return null;

    var pad = 1;
    var x = clamp(x1 - pad, 0, canvasW - 1);
    var y = clamp(y1 - pad, 0, canvasH - 1);
    var right = clamp(x2 + pad + 1, 1, canvasW);
    var bottom = clamp(y2 + pad + 1, 1, canvasH);
    var width = right - x;
    var height = bottom - y;
    if (width < canvasW * 0.5 || height < canvasH * 0.5) return null;

    return {
      x: x,
      y: y,
      w: width,
      h: height,
      coverage: (width * height) / Math.max(1, canvasW * canvasH),
      backgroundCoverage: null,
      fallback: "rect-scan"
    };
  }

  function debugBounds(bounds) {
    if (!bounds) return null;
    return {
      x: bounds.x,
      y: bounds.y,
      w: bounds.w,
      h: bounds.h,
      coverage: bounds.coverage,
      backgroundCoverage: bounds.backgroundCoverage,
      fallback: bounds.fallback,
      hasMask: !!bounds.mask
    };
  }

  function createCanvas(w, h) {
    var canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(w || 1));
    canvas.height = Math.max(1, Math.round(h || 1));
    return canvas;
  }

  function drawMaskToLayer(layerCtx, analysis, inset) {
    if (!analysis || !analysis.mask || !analysis.maskWidth || !analysis.maskHeight) return false;
    var w = analysis.maskWidth;
    var h = analysis.maskHeight;
    var maskCanvas = createCanvas(w, h);
    var maskCtx = maskCanvas.getContext("2d", { willReadFrequently: true });
    var imgData = maskCtx.createImageData(w, h);
    var data = imgData.data;
    var safeInset = Math.max(0, Math.round(finiteNumber(inset, 0)));
    var innerX = analysis.x + safeInset;
    var innerY = analysis.y + safeInset;
    var innerRight = analysis.x + analysis.w - safeInset;
    var innerBottom = analysis.y + analysis.h - safeInset;

    for (var i = 0; i < analysis.mask.length; i++) {
      if (!analysis.mask[i]) continue;
      var x = i % w;
      var y = Math.floor(i / w);
      if (safeInset && (x < innerX || x > innerRight || y < innerY || y > innerBottom)) continue;
      var p = i * 4 + 3;
      data[p] = 255;
    }
    maskCtx.putImageData(imgData, 0, 0);
    layerCtx.globalCompositeOperation = "destination-in";
    layerCtx.drawImage(maskCanvas, 0, 0, layerCtx.canvas.width, layerCtx.canvas.height);
    layerCtx.globalCompositeOperation = "source-over";
    return true;
  }

  function clearInnerRect(layerCtx, bounds, depth) {
    var safeDepth = Math.max(0, Math.round(finiteNumber(depth, 0)));
    if (!safeDepth) return;
    layerCtx.globalCompositeOperation = "destination-out";
    layerCtx.fillStyle = "#000";
    layerCtx.fillRect(
      bounds.x + safeDepth,
      bounds.y + safeDepth,
      Math.max(1, bounds.w - safeDepth * 2),
      Math.max(1, bounds.h - safeDepth * 2)
    );
    layerCtx.globalCompositeOperation = "source-over";
  }

  function paintPreviewStage(ctx, w, h) {
    ctx.clearRect(0, 0, w, h);
    if (ctx && ctx.canvas && ctx.canvas.__massimoArTransparentSurface) return;
    ctx.fillStyle = "#f6f2eb";
    ctx.fillRect(0, 0, w, h);
  }

  function clearEdgeBandToStage(ctx, bounds, depth) {
    if (!bounds || depth < 1) return;
    ctx.save();
    clipRectEdgeBand(ctx, bounds.x, bounds.y, bounds.w, bounds.h, depth, 0);
    ctx.fillStyle = "#f6f2eb";
    ctx.fillRect(bounds.x, bounds.y, bounds.w, bounds.h);
    ctx.restore();
  }

  function drawCarpetShadow(ctx, bounds) {
    if (!bounds) return;
    if (ctx && ctx.canvas && ctx.canvas.__massimoArTransparentSurface) return;
    ctx.save();
    ctx.shadowColor = "rgba(35, 31, 27, 0.18)";
    ctx.shadowBlur = 24;
    ctx.shadowOffsetY = 12;
    ctx.fillStyle = "rgba(35, 31, 27, 0.16)";
    drawRoundedRect(ctx, bounds.x + 5, bounds.y + 8, Math.max(1, bounds.w - 10), Math.max(1, bounds.h - 12), 8);
    ctx.fill();
    ctx.restore();
  }

  function fabricImageKey(fabricImg) {
    return String(
      (fabricImg && (fabricImg.currentSrc || fabricImg.src)) ||
      (fabricImg && fabricImg.getAttribute && fabricImg.getAttribute("src")) ||
      ""
    ).toLowerCase();
  }

  function fabricTextureProfile(fabricImg) {
    var key = fabricImageKey(fabricImg);
    if (key.indexOf("canvas_14") >= 0 || key.indexOf("canvas-14") >= 0 || key.indexOf("canvas%2014") >= 0) {
      return { cropRatio: 0.34, cropXRatio: 0.42, cropYRatio: 0.58 };
    }
    return { cropRatio: 0.78, cropXRatio: 0.5, cropYRatio: 0.5 };
  }

  function cropLightingScore(fabricImg, cropX, cropY, crop) {
    try {
      var size = 34;
      var sample = createCanvas(size, size);
      var sctx = sample.getContext("2d", { willReadFrequently: true });
      sctx.imageSmoothingEnabled = true;
      sctx.imageSmoothingQuality = "high";
      sctx.drawImage(fabricImg, cropX, cropY, crop, crop, 0, 0, size, size);
      var data = sctx.getImageData(0, 0, size, size).data;
      var rows = new Array(size);
      var cols = new Array(size);
      var vals = [];
      var sum = 0;
      var min = 255;
      var max = 0;
      for (var i = 0; i < size; i++) {
        rows[i] = { sum: 0, n: 0 };
        cols[i] = { sum: 0, n: 0 };
      }
      for (var y = 0; y < size; y++) {
        for (var x = 0; x < size; x++) {
          var p = (y * size + x) * 4;
          if (data[p + 3] < 16) continue;
          var yy = pixelLuma(data, p);
          vals.push(yy);
          sum += yy;
          min = Math.min(min, yy);
          max = Math.max(max, yy);
          rows[y].sum += yy;
          rows[y].n++;
          cols[x].sum += yy;
          cols[x].n++;
        }
      }
      if (!vals.length) return 9999;
      var avg = sum / vals.length;
      var variance = 0;
      var bright = 0;
      for (var v = 0; v < vals.length; v++) {
        var d = vals[v] - avg;
        variance += d * d;
        if (vals[v] > avg + 28 || vals[v] > 224) bright++;
      }
      function rangeOf(list) {
        var lo = 255;
        var hi = 0;
        for (var j = 0; j < list.length; j++) {
          if (!list[j].n) continue;
          var a = list[j].sum / list[j].n;
          lo = Math.min(lo, a);
          hi = Math.max(hi, a);
        }
        return hi - lo;
      }
      var yStd = Math.sqrt(variance / vals.length);
      return yStd * 0.8
        + (max - min) * 0.18
        + rangeOf(rows) * 1.15
        + rangeOf(cols) * 0.7
        + (bright / vals.length) * 120;
    } catch (err) {
      return 9999;
    }
  }

  function pickNeutralAmorfEdgeCrop(fabricImg, sourceW, sourceH, crop, xRatio, yRatio) {
    var maxX = Math.max(0, sourceW - crop);
    var maxY = Math.max(0, sourceH - crop);
    var fallback = {
      x: maxX * clamp(finiteNumber(xRatio, 0.5), 0, 1),
      y: maxY * clamp(finiteNumber(yRatio, 0.5), 0, 1),
      score: null
    };
    if (!fabricImg || maxX < 1 && maxY < 1) return fallback;
    var key = Math.round(sourceW) + "x" + Math.round(sourceH) + ":" + Math.round(crop);
    fabricImg.__massimoNeutralAmorfCrop = fabricImg.__massimoNeutralAmorfCrop || {};
    if (fabricImg.__massimoNeutralAmorfCrop[key]) return fabricImg.__massimoNeutralAmorfCrop[key];
    var ratios = [
      [xRatio, yRatio],
      [0.16, 0.16], [0.5, 0.16], [0.84, 0.16],
      [0.16, 0.5], [0.84, 0.5],
      [0.16, 0.84], [0.5, 0.84], [0.84, 0.84],
      [0.32, 0.32], [0.68, 0.32], [0.32, 0.68], [0.68, 0.68]
    ];
    var best = null;
    for (var i = 0; i < ratios.length; i++) {
      var rx = clamp(finiteNumber(ratios[i][0], 0.5), 0, 1);
      var ry = clamp(finiteNumber(ratios[i][1], 0.5), 0, 1);
      var cx = maxX * rx;
      var cy = maxY * ry;
      var score = cropLightingScore(fabricImg, cx, cy, crop);
      if (!best || score < best.score) best = { x: cx, y: cy, score: score, xRatio: rx, yRatio: ry };
    }
    fabricImg.__massimoNeutralAmorfCrop[key] = best || fallback;
    return fabricImg.__massimoNeutralAmorfCrop[key];
  }

  function fabricTileLowPassData(tile, w, h, ratio, minSize) {
    var lowW = Math.max(minSize || 6, Math.round(w * ratio));
    var lowH = Math.max(minSize || 6, Math.round(h * ratio));
    var small = createCanvas(lowW, lowH);
    var sctx = small.getContext("2d", { willReadFrequently: true });
    sctx.imageSmoothingEnabled = true;
    sctx.imageSmoothingQuality = "high";
    sctx.drawImage(tile, 0, 0, lowW, lowH);

    var low = createCanvas(w, h);
    var lctx = low.getContext("2d", { willReadFrequently: true });
    lctx.imageSmoothingEnabled = true;
    lctx.imageSmoothingQuality = "high";
    lctx.drawImage(small, 0, 0, w, h);
    return lctx.getImageData(0, 0, w, h).data;
  }

  function normalizeFabricTileLighting(tile) {
    if (!tile || tile.width < 8 || tile.height < 8) return false;
    try {
      var w = tile.width;
      var h = tile.height;
      var ctx = tile.getContext("2d", { willReadFrequently: true });
      var image = ctx.getImageData(0, 0, w, h);
      var data = image.data;
      var lightingData = fabricTileLowPassData(tile, w, h, 0.08, 7);
      var softData = fabricTileLowPassData(tile, w, h, 0.46, 28);
      var lumaSum = 0;
      var count = 0;

      for (var i = 0; i < data.length; i += 4) {
        if (data[i + 3] < 16) continue;
        lumaSum += pixelLuma(lightingData, i);
        count++;
      }
      if (!count) return false;

      var targetLuma = lumaSum / count;
      var fineDetailStrength = 1.08;
      var midDetailStrength = 0.12;
      var maxShift = 118;
      for (var p = 0; p < data.length; p += 4) {
        if (data[p + 3] < 16) continue;
        var currentLuma = pixelLuma(data, p);
        var softLuma = pixelLuma(softData, p);
        var lightingLuma = pixelLuma(lightingData, p);
        var fineDetail = currentLuma - softLuma;
        var midDetail = softLuma - lightingLuma;
        var target = targetLuma + fineDetail * fineDetailStrength + midDetail * midDetailStrength;
        var shift = clamp(target - currentLuma, -maxShift, maxShift);
        data[p] = clamp(data[p] + shift, 0, 255);
        data[p + 1] = clamp(data[p + 1] + shift, 0, 255);
        data[p + 2] = clamp(data[p + 2] + shift, 0, 255);
      }
      ctx.putImageData(image, 0, 0);
      return true;
    } catch (err) {
      return false;
    }
  }

  function suppressAmorfEdgeTileReflections(tile) {
    if (!tile || tile.width < 8 || tile.height < 8) return false;
    try {
      var w = tile.width;
      var h = tile.height;
      var ctx = tile.getContext("2d", { willReadFrequently: true });
      var image = ctx.getImageData(0, 0, w, h);
      var data = image.data;
      var lightingData = fabricTileLowPassData(tile, w, h, 0.055, 12);
      var luma = 0;
      var count = 0;
      for (var i = 0; i < data.length; i += 4) {
        if (data[i + 3] < 16) continue;
        luma += pixelLuma(lightingData, i);
        count++;
      }
      if (!count) return false;
      var avg = luma / count;
      for (var p = 0; p < data.length; p += 4) {
        if (data[p + 3] < 16) continue;
        var currentY = pixelLuma(data, p);
        var lightingY = pixelLuma(lightingData, p);
        var glare = Math.max(0, lightingY - avg);
        var correction = clamp((avg - lightingY) * 0.82 - glare * 0.18, -46, 34);
        if (currentY > avg + 18) correction -= Math.min(18, (currentY - avg - 18) * 0.18);
        data[p] = clamp(data[p] + correction, 0, 255);
        data[p + 1] = clamp(data[p + 1] + correction, 0, 255);
        data[p + 2] = clamp(data[p + 2] + correction, 0, 255);
      }
      ctx.putImageData(image, 0, 0);
      return true;
    } catch (err) {
      return false;
    }
  }

  function lumaStatsFromImageData(data, step) {
    var r = 0;
    var g = 0;
    var b = 0;
    var y = 0;
    var y2 = 0;
    var n = 0;
    var safeStep = Math.max(1, Math.round(finiteNumber(step, 1)));
    for (var i = 0; i < data.length; i += 4 * safeStep) {
      if (data[i + 3] < 24) continue;
      var rr = data[i];
      var gg = data[i + 1];
      var bb = data[i + 2];
      if (rr > 248 && gg > 248 && bb > 248) continue;
      var yy = rr * 0.299 + gg * 0.587 + bb * 0.114;
      r += rr;
      g += gg;
      b += bb;
      y += yy;
      y2 += yy * yy;
      n++;
    }
    if (!n) return null;
    var yAvg = y / n;
    return {
      r: r / n,
      g: g / n,
      b: b / n,
      y: yAvg,
      yStd: Math.sqrt(Math.max(0, y2 / n - yAvg * yAvg)),
      n: n
    };
  }

  function roundDebugNumber(value, digits) {
    var n = Number(value);
    if (!Number.isFinite(n)) return null;
    var factor = Math.pow(10, digits == null ? 2 : digits);
    return Math.round(n * factor) / factor;
  }

  function fabricImageStats(fabricImg) {
    if (!fabricImg) return null;
    if (fabricImg.__massimoFabricStats) return fabricImg.__massimoFabricStats;
    try {
      var sourceW = finiteNumber(fabricImg.naturalWidth || fabricImg.width, 0);
      var sourceH = finiteNumber(fabricImg.naturalHeight || fabricImg.height, 0);
      if (sourceW < 1 || sourceH < 1) return null;
      var maxSide = 420;
      var scale = Math.min(1, maxSide / Math.max(sourceW, sourceH));
      var w = Math.max(1, Math.round(sourceW * scale));
      var h = Math.max(1, Math.round(sourceH * scale));
      var sample = createCanvas(w, h);
      var sctx = sample.getContext("2d", { willReadFrequently: true });
      sctx.imageSmoothingEnabled = true;
      sctx.imageSmoothingQuality = "high";
      sctx.drawImage(fabricImg, 0, 0, w, h);
      var stats = lumaStatsFromImageData(sctx.getImageData(0, 0, w, h).data, 1);
      fabricImg.__massimoFabricStats = stats;
      return stats;
    } catch (err) {
      return null;
    }
  }

  function canvasRectLumaStats(canvas, x, y, w, h) {
    if (!canvas || w < 1 || h < 1) return null;
    try {
      var sx = clamp(Math.round(finiteNumber(x, 0)), 0, canvas.width - 1);
      var sy = clamp(Math.round(finiteNumber(y, 0)), 0, canvas.height - 1);
      var sw = Math.max(1, Math.min(canvas.width - sx, Math.round(finiteNumber(w, 1))));
      var sh = Math.max(1, Math.min(canvas.height - sy, Math.round(finiteNumber(h, 1))));
      var cctx = canvas.getContext("2d", { willReadFrequently: true });
      var data = cctx.getImageData(sx, sy, sw, sh).data;
      var step = Math.max(1, Math.round(Math.sqrt((sw * sh) / 220000)));
      return lumaStatsFromImageData(data, step);
    } catch (err) {
      return null;
    }
  }

  function recordEdgeTextureDebug(ctx, fabricImg, mode, patternInfo, bandStats, meta) {
    if (!ctx || !ctx.canvas || !fabricImg) return;
    var sourceW = finiteNumber(fabricImg.naturalWidth || fabricImg.width, 0);
    var sourceH = finiteNumber(fabricImg.naturalHeight || fabricImg.height, 0);
    var sourceStats = fabricImageStats(fabricImg);
    var last = {};
    try { last = window.__MASSIMO_OZEL_OLCU_LAST__ || {}; } catch (err) { last = {}; }
    var debug = {
      mode: mode || "edge-fabric-texture",
      build: BUILD,
      edgeType: meta && meta.edgeType || last.edgeType || "",
      shapeType: meta && meta.shapeType || last.shapeType || "",
      shapePreset: meta && meta.shapePreset || last.shapePreset || "",
      fabricSize: { width: sourceW, height: sourceH },
      tileSize: patternInfo && patternInfo.tileSize || null,
      crop: patternInfo && patternInfo.crop || null,
      directional: !!(patternInfo && patternInfo.directional),
      sourceResolutionLimited: sourceW < 3840 || sourceH < 3840,
      sourceYStd: roundDebugNumber(sourceStats && sourceStats.yStd, 2),
      bandYStd: roundDebugNumber(bandStats && bandStats.yStd, 2),
      bandPixels: bandStats && bandStats.n || 0
    };
    ctx.canvas.__massimoEdgeTextureDebug = debug;
    try {
      if (window.__MASSIMO_OZEL_OLCU_LAST__) {
        var previous = window.__MASSIMO_OZEL_OLCU_LAST__.edgeTexture;
        var parts = previous && previous.parts ? previous.parts.slice() : (previous ? [previous] : []);
        parts.push(debug);
        window.__MASSIMO_OZEL_OLCU_LAST__.edgeTexture = Object.assign({}, debug, { parts: parts });
      }
    } catch (err) {
      // Debug output must never affect rendering.
    }
  }

  function makePatternInfo(ctx, fabricImg, targetDepth, qualityMode) {
    if (!fabricImg) return null;
    var tile = document.createElement("canvas");
    var safeDepth = finiteNumber(targetDepth, 42);
    var sourceW = finiteNumber(fabricImg.naturalWidth || fabricImg.width, 0);
    var sourceH = finiteNumber(fabricImg.naturalHeight || fabricImg.height, 0);
    if (sourceW < 1 || sourceH < 1) return null;
    try {
      var profile = fabricTextureProfile(fabricImg);
      var highDetail = qualityMode === "edge-hd" || qualityMode === "amorf-edge";
      var isAmorfEdge = qualityMode === "amorf-edge";
      var cropRatio = clamp(finiteNumber(profile.cropRatio, 0.78), 0.2, 1);
      if (isAmorfEdge) cropRatio = Math.min(cropRatio, 0.46);
      var crop = Math.min(sourceW, sourceH) * cropRatio;
      var cropPick = isAmorfEdge
        ? pickNeutralAmorfEdgeCrop(fabricImg, sourceW, sourceH, crop, profile.cropXRatio, profile.cropYRatio)
        : null;
      var cropX = cropPick ? cropPick.x : (sourceW - crop) * clamp(finiteNumber(profile.cropXRatio, 0.5), 0, 1);
      var cropY = cropPick ? cropPick.y : (sourceH - crop) * clamp(finiteNumber(profile.cropYRatio, 0.5), 0, 1);
      // v202: rect (edge-hd) bordur için 2x küçük tile (160x230 doğal perspektifi),
      // amorf-edge için ORIJINAL multiplier (curved shape'te küçük tile texture'ı kaybediyor).
      var maxTile = Math.max(140, Math.min(highDetail ? 760 : 320, Math.floor(crop)));
      var multiplier = isAmorfEdge ? 8.4 : (highDetail ? 4.2 : 3.6);
      var minSize = isAmorfEdge ? 260 : (highDetail ? 200 : 120);
      var size = Math.max(minSize, Math.min(maxTile, Math.round(safeDepth * multiplier)));
      if (!Number.isFinite(size) || size < 1) return null;
      tile.width = size;
      tile.height = size;
      var tctx = tile.getContext("2d", { willReadFrequently: true });
      tctx.fillStyle = "#9a8d7d";
      tctx.fillRect(0, 0, size, size);
      tctx.imageSmoothingEnabled = size < crop;
      if (tctx.imageSmoothingEnabled) tctx.imageSmoothingQuality = "high";
      tctx.drawImage(fabricImg, cropX, cropY, crop, crop, 0, 0, size, size);
      normalizeFabricTileLighting(tile);
      if (isAmorfEdge) suppressAmorfEdgeTileReflections(tile);
    } catch (err) {
      return null;
    }
    try {
      return {
        pattern: ctx.createPattern(tile, "repeat"),
        tileSize: tile.width,
        sourceWidth: sourceW,
        sourceHeight: sourceH,
        crop: crop ? {
          x: Math.round(cropX),
          y: Math.round(cropY),
          size: Math.round(crop),
          neutralScore: cropPick && cropPick.score != null ? roundDebugNumber(cropPick.score, 2) : null
        } : null,
        qualityMode: qualityMode || "",
        directional: false
      };
    } catch (err) {
      return null;
    }
  }

  function makePattern(ctx, fabricImg, targetDepth, qualityMode) {
    var info = makePatternInfo(ctx, fabricImg, targetDepth, qualityMode);
    return info && info.pattern;
  }

  function fillFabricPatternArea(ctx, patternInfo, x, y, w, h, angle, originX, originY) {
    if (!patternInfo || !patternInfo.pattern || w <= 0 || h <= 0) return false;
    var tileSize = Math.max(1, finiteNumber(patternInfo.tileSize, 260));
    var span = Math.max(ctx.canvas.width || 1, ctx.canvas.height || 1) + tileSize * 6;
    ctx.save();
    ctx.beginPath();
    ctx.rect(Math.floor(x), Math.floor(y), Math.ceil(w), Math.ceil(h));
    ctx.clip();
    ctx.translate(Math.round(originX), Math.round(originY));
    if (angle) ctx.rotate(angle);
    ctx.fillStyle = patternInfo.pattern;
    ctx.fillRect(-span, -span, span * 2, span * 2);
    ctx.restore();
    return true;
  }

  function drawDirectionalFabricTextureFill(ctx, patternInfo, x, y, w, h, targetDepth) {
    if (!patternInfo || !patternInfo.pattern || w < 4 || h < 4) return false;
    var depth = Math.max(1, Math.round(finiteNumber(targetDepth, Math.min(w, h) * 0.04)));
    depth = Math.min(depth, Math.max(1, Math.floor(Math.min(w, h) / 2)));
    var overlap = Math.max(1, Math.min(4, Math.round(depth * 0.08)));
    var sideH = Math.max(0, h - depth * 2);
    var offset = Math.round((patternInfo.tileSize || 260) * 0.37);
    var painted = false;
    painted = fillFabricPatternArea(ctx, patternInfo, x, y, w, depth + overlap, 0, x, y) || painted;
    painted = fillFabricPatternArea(ctx, patternInfo, x, y + h - depth - overlap, w, depth + overlap, 0, x + offset, y + h - depth) || painted;
    if (sideH > 0) {
      painted = fillFabricPatternArea(ctx, patternInfo, x, y + depth - overlap, depth + overlap, sideH + overlap * 2, Math.PI / 2, x + depth, y + depth + offset) || painted;
      painted = fillFabricPatternArea(ctx, patternInfo, x + w - depth - overlap, y + depth - overlap, depth + overlap, sideH + overlap * 2, Math.PI / 2, x + w - depth, y + depth) || painted;
    }
    patternInfo.directional = painted;
    return painted;
  }

  function drawFabricTextureFill(ctx, fabricImg, x, y, w, h, targetDepth, qualityMode) {
    var info = makePatternInfo(ctx, fabricImg, targetDepth, qualityMode);
    if (!info || !info.pattern) return false;
    ctx.save();
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    // v203: amorf-edge için directional path bypass — directional strips amorf ring'in
    // çoğunlukla DIŞINDA kalıyor, mask ile silinince texture görünmüyor. Simple pattern fill
    // bounding box'ı tamamen kaplar, mask amorf ring'e clip eder → texture görünür.
    var directional = qualityMode === "edge-hd";
    if (!directional || !drawDirectionalFabricTextureFill(ctx, info, x, y, w, h, targetDepth)) {
      ctx.translate(Math.round(x), Math.round(y));
      ctx.fillStyle = info.pattern;
      ctx.fillRect(0, 0, Math.ceil(w), Math.ceil(h));
    }
    ctx.restore();
    return info;
  }

  function drawBiyeFabricTextureFill(ctx, fabricImg, x, y, w, h, targetDepth) {
    return drawFabricTextureFill(ctx, fabricImg, x, y, w, h, targetDepth, "edge-hd");
  }

  function enhanceEdgeFabricTexture(ctx, x, y, w, h, edgeDepth, strength) {
    if (!ctx || !ctx.canvas || w < 1 || h < 1) return false;
    var sx = clamp(Math.round(finiteNumber(x, 0)), 0, ctx.canvas.width - 1);
    var sy = clamp(Math.round(finiteNumber(y, 0)), 0, ctx.canvas.height - 1);
    var sw = Math.max(1, Math.min(ctx.canvas.width - sx, Math.round(finiteNumber(w, 1))));
    var sh = Math.max(1, Math.min(ctx.canvas.height - sy, Math.round(finiteNumber(h, 1))));
    try {
      var image = ctx.getImageData(sx, sy, sw, sh);
      var data = image.data;
      var luma = 0;
      var count = 0;
      for (var i = 0; i < data.length; i += 4) {
        if (data[i + 3] < 24) continue;
        luma += pixelLuma(data, i);
        count++;
      }
      if (!count) return false;
      var avg = luma / count;
      var factor = clamp(finiteNumber(strength, 1.06), 1, 1.12);
      var maxShift = Math.max(3, Math.min(10, finiteNumber(edgeDepth, 24) * 0.14));
      for (var p = 0; p < data.length; p += 4) {
        if (data[p + 3] < 24) continue;
        var yy = pixelLuma(data, p);
        var shift = clamp((yy - avg) * (factor - 1), -maxShift, maxShift);
        data[p] = clamp(data[p] + shift, 0, 255);
        data[p + 1] = clamp(data[p + 1] + shift, 0, 255);
        data[p + 2] = clamp(data[p + 2] + shift, 0, 255);
      }
      ctx.putImageData(image, sx, sy);
      return true;
    } catch (err) {
      return false;
    }
  }

  function drawEdgeFabricPerspectiveShading(ctx, x, y, w, h, edgeDepth) {
    if (!ctx || !ctx.canvas || w < 2 || h < 2 || edgeDepth < 2) return false;
    var d = Math.max(2, Math.round(finiteNumber(edgeDepth, 0)));
    var innerX = x + d;
    var innerY = y + d;
    var innerW = Math.max(1, w - d * 2);
    var innerH = Math.max(1, h - d * 2);
    ctx.save();
    clipRectEdgeBand(ctx, x, y, w, h, d, 0);

    var top = ctx.createLinearGradient(0, y, 0, y + d);
    top.addColorStop(0, "rgba(255,255,255,0.075)");
    top.addColorStop(0.52, "rgba(255,255,255,0.025)");
    top.addColorStop(1, "rgba(45,34,27,0.035)");
    ctx.fillStyle = top;
    ctx.fillRect(x, y, w, d);

    var bottom = ctx.createLinearGradient(0, y + h - d, 0, y + h);
    bottom.addColorStop(0, "rgba(255,255,255,0.018)");
    bottom.addColorStop(0.58, "rgba(36,28,23,0.04)");
    bottom.addColorStop(1, "rgba(28,22,18,0.075)");
    ctx.fillStyle = bottom;
    ctx.fillRect(x, y + h - d, w, d);

    var left = ctx.createLinearGradient(x, 0, x + d, 0);
    left.addColorStop(0, "rgba(255,255,255,0.045)");
    left.addColorStop(1, "rgba(40,32,26,0.025)");
    ctx.fillStyle = left;
    ctx.fillRect(x, y, d, h);

    var right = ctx.createLinearGradient(x + w - d, 0, x + w, 0);
    right.addColorStop(0, "rgba(255,255,255,0.012)");
    right.addColorStop(1, "rgba(30,24,20,0.06)");
    ctx.fillStyle = right;
    ctx.fillRect(x + w - d, y, d, h);

    ctx.strokeStyle = "rgba(255,250,240,0.07)";
    ctx.lineWidth = Math.max(0.5, Math.min(1.4, d * 0.018));
    ctx.strokeRect(x + 0.5, y + 0.5, Math.max(1, w - 1), Math.max(1, h - 1));
    ctx.strokeStyle = "rgba(35,27,22,0.10)";
    ctx.lineWidth = Math.max(0.7, Math.min(1.6, d * 0.022));
    ctx.strokeRect(innerX + 0.5, innerY + 0.5, Math.max(1, innerW - 1), Math.max(1, innerH - 1));
    ctx.restore();
    return true;
  }

  // v195: bordür iç kenarında 3-5px soft feather — gerçek dikilmiş kumaş gibi halıyla kaynaşma
  function drawBordurInnerFeather(ctx, bounds, edgeDepth) {
    if (!ctx || !bounds || edgeDepth < 6) return;
    var x = bounds.x, y = bounds.y, w = bounds.w, h = bounds.h;
    var d = Math.round(edgeDepth);
    var fW = Math.max(2, Math.min(7, Math.round(d * 0.18)));
    var innerX = x + d, innerY = y + d;
    var innerW = Math.max(1, w - d * 2), innerH = Math.max(1, h - d * 2);
    ctx.save();
    ctx.globalCompositeOperation = "destination-out";
    // v197: Köşelerde çakışmasın — üst/alt sadece orta segment (sol+sağ köşeler hariç).
    // Sol/sağ feather'lar tam yükseklikte köşeleri tek geçişle yumuşatır → köşede beyaz artifact yok.
    var sidePad = fW + 1;
    // Üst iç sınır (köşeler hariç)
    if (innerW > sidePad * 2) {
      var gT = ctx.createLinearGradient(0, innerY - fW, 0, innerY + fW);
      gT.addColorStop(0, "rgba(0,0,0,0)");
      gT.addColorStop(0.5, "rgba(0,0,0,0.30)");
      gT.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = gT;
      ctx.fillRect(innerX + sidePad, innerY - fW, innerW - sidePad * 2, fW * 2);
    }
    // Alt iç sınır (köşeler hariç)
    if (innerW > sidePad * 2) {
      var gB = ctx.createLinearGradient(0, innerY + innerH - fW, 0, innerY + innerH + fW);
      gB.addColorStop(0, "rgba(0,0,0,0)");
      gB.addColorStop(0.5, "rgba(0,0,0,0.30)");
      gB.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = gB;
      ctx.fillRect(innerX + sidePad, innerY + innerH - fW, innerW - sidePad * 2, fW * 2);
    }
    // Sol iç sınır (tam yükseklik, köşeler dahil)
    var gL = ctx.createLinearGradient(innerX - fW, 0, innerX + fW, 0);
    gL.addColorStop(0, "rgba(0,0,0,0)");
    gL.addColorStop(0.5, "rgba(0,0,0,0.30)");
    gL.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = gL;
    ctx.fillRect(innerX - fW, innerY - fW, fW * 2, innerH + fW * 2);
    // Sağ iç sınır (tam yükseklik, köşeler dahil)
    var gR = ctx.createLinearGradient(innerX + innerW - fW, 0, innerX + innerW + fW, 0);
    gR.addColorStop(0, "rgba(0,0,0,0)");
    gR.addColorStop(0.5, "rgba(0,0,0,0.30)");
    gR.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = gR;
    ctx.fillRect(innerX + innerW - fW, innerY - fW, fW * 2, innerH + fW * 2);
    ctx.restore();
  }

  function applyEdgeFabricFinish(ctx, x, y, w, h, edgeDepth, strength) {
    var enhanced = enhanceEdgeFabricTexture(ctx, x, y, w, h, edgeDepth, strength);
    var shaded = drawEdgeFabricPerspectiveShading(ctx, x, y, w, h, edgeDepth);
    return enhanced || shaded;
  }

  function applyAmorfEdgeFabricFinish(ctx, x, y, w, h, edgeDepth, strength) {
    return enhanceEdgeFabricTexture(ctx, x, y, w, h, edgeDepth, Math.min(finiteNumber(strength, 1.02), 1.025));
  }

  function drawBiyeRectCordShading(ctx, x, y, w, h, radius, edgeDepth) {
    var d = Math.max(2, finiteNumber(edgeDepth, 0));
    var crownW = Math.max(1, Math.min(9, d * 0.34));

    ctx.save();
    ctx.lineJoin = "round";
    ctx.lineCap = "round";

    ctx.shadowColor = "rgba(255,255,255,0.08)";
    ctx.shadowBlur = Math.max(1, d * 0.08);
    ctx.strokeStyle = "rgba(255,255,255,0.08)";
    ctx.lineWidth = crownW;
    drawRoundedRect(
      ctx,
      x + d * 0.44,
      y + d * 0.44,
      Math.max(1, w - d * 0.88),
      Math.max(1, h - d * 0.88),
      Math.max(1, radius - d * 0.44)
    );
    ctx.stroke();
    ctx.restore();
  }

  function drawShapeBiyeCordShading(ctx, geometry, edgeDepth) {
    var d = Math.max(2, finiteNumber(edgeDepth, 0));
    ctx.save();
    ctx.lineJoin = "round";
    ctx.lineCap = "round";

    ctx.shadowColor = "rgba(255,255,255,0.08)";
    ctx.shadowBlur = Math.max(1, d * 0.08);
    ctx.strokeStyle = "rgba(255,255,255,0.08)";
    ctx.lineWidth = Math.max(1, Math.min(9, d * 0.34));
    traceShapePath(ctx, geometry, d * 0.44);
    ctx.stroke();
    ctx.restore();
  }

  function drawFabricFill(ctx, baseColor) {
    ctx.fillStyle = baseColor || "#897b6d";
    ctx.fill("evenodd");
  }

  function clipRectEdgeBand(ctx, x, y, w, h, depth, radius) {
    ctx.beginPath();
    drawRoundedRect(ctx, x, y, w, h, radius);
    ctx.rect(x + depth, y + depth, Math.max(1, w - depth * 2), Math.max(1, h - depth * 2));
    ctx.clip("evenodd");
  }

  function normalizeShapeType(value) {
    var shape = String(value || "dikdortgen").toLowerCase();
    if (shape === "daire" || shape === "yuvarlak" || shape === "circle") return "yuvarlak";
    if (shape === "oval" || shape === "ellipse") return "oval";
    if (shape === "amorf" || shape === "organic") return "amorf";
    return "dikdortgen";
  }

  function normalizeShapeConfig(shape) {
    shape = shape || {};
    var type = normalizeShapeType(shape.type || shape.shapeType);
    var radii = Array.isArray(shape.radii) ? shape.radii.slice(0) : null;
    return {
      type: type,
      label: shape.label || "",
      presetKey: shape.presetKey || "",
      presetLabel: shape.presetLabel || "",
      radii: radii,
      outlineCommands: normalizePathCommands(shape.outlineCommands),
      widthCm: finiteNumber(shape.widthCm, 160),
      lengthCm: finiteNumber(shape.lengthCm, 230)
    };
  }

  function normalizePathCommands(commands) {
    if (!Array.isArray(commands) || !commands.length) return null;
    var out = [];
    commands.forEach(function (cmd) {
      if (!Array.isArray(cmd) || !cmd.length) return;
      var type = String(cmd[0] || "").toUpperCase();
      var need = type === "M" || type === "L" ? 2 : type === "Q" ? 4 : type === "C" ? 6 : type === "Z" ? 0 : -1;
      if (need < 0 || cmd.length < need + 1) return;
      if (type === "Z") {
        out.push(["Z"]);
        return;
      }
      var next = [type];
      for (var i = 1; i <= need; i++) {
        next.push(clamp(finiteNumber(cmd[i], 0), 0, 1));
      }
      out.push(next);
    });
    return out.length >= 3 ? out : null;
  }

  function isShapeMode(shape) {
    return normalizeShapeType(shape && (shape.type || shape.shapeType)) !== "dikdortgen";
  }

  function defaultShapeRadii(type) {
    var arr = [];
    for (var i = 0; i < 72; i++) {
      var a = i / 72 * Math.PI * 2;
      var v = 1;
      if (type === "amorf") {
        v = 0.88 + Math.sin(a * 2.1 + 0.4) * 0.07 + Math.cos(a * 3.4 - 0.2) * 0.05 + Math.sin(a * 5.2) * 0.025;
      }
      arr.push(clamp(v, 0.72, 1.05));
    }
    return arr;
  }

  function smoothAmorfRenderRadii(radii) {
    if (!Array.isArray(radii) || radii.length < 8) return radii;
    var current = radii.map(function (value) {
      return clamp(finiteNumber(value, 1), 0.68, 1.12);
    });
    for (var pass = 0; pass < 7; pass++) {
      var next = [];
      for (var i = 0; i < current.length; i++) {
        var p2 = current[(i - 2 + current.length) % current.length];
        var p1 = current[(i - 1 + current.length) % current.length];
        var c = current[i];
        var n1 = current[(i + 1) % current.length];
        var n2 = current[(i + 2) % current.length];
        next.push(clamp((p2 + p1 * 2 + c * 6 + n1 * 2 + n2) / 12, 0.68, 1.12));
      }
      current = next;
    }
    for (var damp = 0; damp < current.length; damp++) {
      current[damp] = 1 + (current[damp] - 1) * 0.72;
    }
    var maxStep = 0.022;
    for (var guard = 0; guard < 2; guard++) {
      for (var f = 1; f < current.length; f++) {
        current[f] = clamp(current[f], current[f - 1] - maxStep, current[f - 1] + maxStep);
      }
      current[0] = clamp(current[0], current[current.length - 1] - maxStep, current[current.length - 1] + maxStep);
      for (var b = current.length - 2; b >= 0; b--) {
        current[b] = clamp(current[b], current[b + 1] - maxStep, current[b + 1] + maxStep);
      }
      current[current.length - 1] = clamp(current[current.length - 1], current[0] - maxStep, current[0] + maxStep);
    }
    return current;
  }

  function shapeRadiusAt(geometry, angle) {
    var radii = geometry.radii || [];
    if (!radii.length) return 1;
    var tau = Math.PI * 2;
    var t = ((angle + tau) % tau) / tau * radii.length;
    var i0 = Math.floor(t) % radii.length;
    var i1 = (i0 + 1) % radii.length;
    var f = t - Math.floor(t);
    f = f * f * (3 - 2 * f);
    return finiteNumber(radii[i0], 1) * (1 - f) + finiteNumber(radii[i1], 1) * f;
  }

  function shapeUnitExtents(radii, scaleX, scaleY) {
    var steps = 720;
    var minX = Infinity;
    var maxX = -Infinity;
    var minY = Infinity;
    var maxY = -Infinity;
    var sx = Math.max(0.01, finiteNumber(scaleX, 1));
    var sy = Math.max(0.01, finiteNumber(scaleY, 1));
    for (var i = 0; i < steps; i++) {
      var angle = (Math.PI * 2 * i) / steps;
      var radial = shapeRadiusAt({ radii: radii }, angle);
      var x = Math.cos(angle) * radial * sx;
      var y = Math.sin(angle) * radial * sy;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
    if (!isFinite(minX) || !isFinite(maxX) || !isFinite(minY) || !isFinite(maxY)) {
      return { minX: -1, maxX: 1, minY: -1, maxY: 1, width: 2, height: 2 };
    }
    return {
      minX: minX,
      maxX: maxX,
      minY: minY,
      maxY: maxY,
      width: Math.max(0.001, maxX - minX),
      height: Math.max(0.001, maxY - minY)
    };
  }

  function amorfPhysicalTransform(radii, widthCm, lengthCm) {
    var width = Math.max(1, finiteNumber(widthCm, 160));
    var length = Math.max(1, finiteNumber(lengthCm, 230));
    var desiredRatio = clamp(width / length, 0.12, 8);
    var raw = shapeUnitExtents(radii, 1, 1);
    var rawRatio = raw.width / Math.max(0.001, raw.height);
    var scaleRatio = desiredRatio / Math.max(0.001, rawRatio);
    var sx = 1;
    var sy = 1;
    if (scaleRatio >= 1) {
      sy = 1 / scaleRatio;
    } else {
      sx = scaleRatio;
    }
    return {
      x: clamp(sx, 0.06, 8),
      y: clamp(sy, 0.06, 8),
      desiredRatio: desiredRatio,
      rawRatio: rawRatio
    };
  }

  function amorfFitBox(geometry, inset) {
    var ext = geometry._shapeExtents || shapeUnitExtents(geometry.radii);
    var safeInset = Math.max(0, finiteNumber(inset, 0));
    var availableW = Math.max(2, geometry.w - safeInset * 2);
    var availableH = Math.max(2, geometry.h - safeInset * 2);
    var scale = Math.max(1, Math.min(availableW / ext.width, availableH / ext.height));
    var width = ext.width * scale;
    var height = ext.height * scale;
    var centerX = geometry.cx - ((ext.minX + ext.maxX) / 2) * scale;
    var centerY = geometry.cy - ((ext.minY + ext.maxY) / 2) * scale;
    return {
      ext: ext,
      scale: scale,
      cx: centerX,
      cy: centerY,
      x: centerX + ext.minX * scale,
      y: centerY + ext.minY * scale,
      w: width,
      h: height
    };
  }

  function safeAmorfBounds(bounds, canvasW, canvasH) {
    var x = finiteNumber(bounds && bounds.x, 0);
    var y = finiteNumber(bounds && bounds.y, 0);
    var w = finiteNumber(bounds && bounds.w, canvasW);
    var h = finiteNumber(bounds && bounds.h, canvasH);
    var shortest = Math.max(1, Math.min(canvasW || w || 1, canvasH || h || 1));
    var canvasMargin = Math.max(8, Math.round(shortest * 0.006));
    var innerMargin = Math.max(canvasMargin + 4, Math.round(shortest * 0.017));
    var minX = canvasMargin;
    var minY = canvasMargin;
    var maxW = Math.max(2, (canvasW || (x + w)) - canvasMargin * 2);
    var maxH = Math.max(2, (canvasH || (y + h)) - canvasMargin * 2);
    x = Math.max(minX, x + innerMargin);
    y = Math.max(minY, y + innerMargin);
    w = Math.max(2, w - innerMargin * 2);
    h = Math.max(2, h - innerMargin * 2);
    if (x + w > minX + maxW) w = Math.max(2, minX + maxW - x);
    if (y + h > minY + maxH) h = Math.max(2, minY + maxH - y);
    if (w < 2 || h < 2) {
      x = minX;
      y = minY;
      w = maxW;
      h = maxH;
    }
    return {
      x: x,
      y: y,
      w: w,
      h: h,
      fitMargin: {
        canvas: canvasMargin,
        inner: innerMargin
      }
    };
  }

  function shapePoint(geometry, angle, inset) {
    var safeInset = Math.max(0, finiteNumber(inset, 0));
    if (geometry.type === "amorf") {
      var fit = amorfFitBox(geometry, 0);
      var amorfScaleX = Math.max(0.01, finiteNumber(geometry._amorfScaleX, 1));
      var amorfScaleY = Math.max(0.01, finiteNumber(geometry._amorfScaleY, 1));
      var cos = Math.cos(angle);
      var sin = Math.sin(angle);
      var dirScale = Math.sqrt((cos * amorfScaleX) * (cos * amorfScaleX) + (sin * amorfScaleY) * (sin * amorfScaleY));
      var radialInset = safeInset / Math.max(1, fit.scale * Math.max(0.01, dirScale));
      var radial = Math.max(0.01, shapeRadiusAt(geometry, angle) - radialInset);
      return {
        x: fit.cx + cos * fit.scale * radial * amorfScaleX,
        y: fit.cy + sin * fit.scale * radial * amorfScaleY
      };
    }
    var rx = Math.max(1, geometry.w / 2 - safeInset);
    var ry = Math.max(1, geometry.h / 2 - safeInset);
    return {
      x: geometry.cx + Math.cos(angle) * rx,
      y: geometry.cy + Math.sin(angle) * ry
    };
  }

  function isAmorfGeometry(geometry) {
    return !!(geometry && geometry.type === "amorf");
  }

  function hasOutlineCommands(geometry) {
    return !!(geometry && Array.isArray(geometry.outlineCommands) && geometry.outlineCommands.length >= 3);
  }

  function outlineCommandBody(geometry, inset) {
    var safeInset = Math.max(0, finiteNumber(inset, 0));
    return {
      x: geometry.x + safeInset,
      y: geometry.y + safeInset,
      w: Math.max(1, geometry.w - safeInset * 2),
      h: Math.max(1, geometry.h - safeInset * 2)
    };
  }

  function bodyPoint(body, nx, ny) {
    return {
      x: body.x + finiteNumber(nx, 0) * body.w,
      y: body.y + finiteNumber(ny, 0) * body.h
    };
  }

  function sampleOutlineCommands(commands, body) {
    var points = [];
    var cursor = null;
    var start = null;
    function add(pt) {
      if (!pt) return;
      points.push(pt);
      cursor = pt;
      if (!start) start = pt;
    }
    (commands || []).forEach(function (cmd) {
      var type = cmd[0];
      if (type === "M") {
        add(bodyPoint(body, cmd[1], cmd[2]));
      } else if (type === "L") {
        add(bodyPoint(body, cmd[1], cmd[2]));
      } else if (type === "Q" && cursor) {
        var q0 = cursor;
        var q1 = bodyPoint(body, cmd[1], cmd[2]);
        var q2 = bodyPoint(body, cmd[3], cmd[4]);
        for (var qi = 1; qi <= 8; qi++) {
          var qt = qi / 8;
          var qmt = 1 - qt;
          add({
            x: qmt * qmt * q0.x + 2 * qmt * qt * q1.x + qt * qt * q2.x,
            y: qmt * qmt * q0.y + 2 * qmt * qt * q1.y + qt * qt * q2.y
          });
        }
      } else if (type === "C" && cursor) {
        var c0 = cursor;
        var c1 = bodyPoint(body, cmd[1], cmd[2]);
        var c2 = bodyPoint(body, cmd[3], cmd[4]);
        var c3 = bodyPoint(body, cmd[5], cmd[6]);
        for (var ci = 1; ci <= 10; ci++) {
          var ct = ci / 10;
          var cmt = 1 - ct;
          add({
            x: cmt * cmt * cmt * c0.x + 3 * cmt * cmt * ct * c1.x + 3 * cmt * ct * ct * c2.x + ct * ct * ct * c3.x,
            y: cmt * cmt * cmt * c0.y + 3 * cmt * cmt * ct * c1.y + 3 * cmt * ct * ct * c2.y + ct * ct * ct * c3.y
          });
        }
      } else if (type === "Z" && start) {
        cursor = start;
      }
    });
    return points;
  }

  function amorfShapePathPoints(geometry, inset) {
    if (hasOutlineCommands(geometry)) {
      return sampleOutlineCommands(geometry.outlineCommands, outlineCommandBody(geometry, inset));
    }
    var radiiCount = (geometry.radii || []).length || 72;
    var steps = Math.max(96, Math.min(192, radiiCount * 2));
    var points = [];
    for (var i = 0; i < steps; i++) {
      points.push(shapePoint(geometry, i / steps * Math.PI * 2, inset));
    }
    return points;
  }

  function traceSmoothClosedPath(ctx, points, tension) {
    if (!points || points.length < 3) return false;
    var t = clamp(finiteNumber(tension, 0.78), 0.2, 1.15);
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (var i = 0; i < points.length; i++) {
      var p0 = points[(i - 1 + points.length) % points.length];
      var p1 = points[i];
      var p2 = points[(i + 1) % points.length];
      var p3 = points[(i + 2) % points.length];
      var c1x = p1.x + (p2.x - p0.x) * t / 6;
      var c1y = p1.y + (p2.y - p0.y) * t / 6;
      var c2x = p2.x - (p3.x - p1.x) * t / 6;
      var c2y = p2.y - (p3.y - p1.y) * t / 6;
      ctx.bezierCurveTo(c1x, c1y, c2x, c2y, p2.x, p2.y);
    }
    ctx.closePath();
    return true;
  }

  function insideShape(geometry, px, py, inset) {
    var safeInset = Math.max(0, finiteNumber(inset, 0));
    if (geometry.type === "amorf") {
      var fit = amorfFitBox(geometry, 0);
      var amorfScaleX = Math.max(0.01, finiteNumber(geometry._amorfScaleX, 1));
      var amorfScaleY = Math.max(0.01, finiteNumber(geometry._amorfScaleY, 1));
      var dx = (px - fit.cx) / Math.max(1, fit.scale);
      var dy = (py - fit.cy) / Math.max(1, fit.scale);
      dx /= amorfScaleX;
      dy /= amorfScaleY;
      var distance = Math.sqrt(dx * dx + dy * dy);
      if (distance > 1) return false;
      var angle = Math.atan2(dy, dx);
      var cos = Math.cos(angle);
      var sin = Math.sin(angle);
      var dirScale = Math.sqrt((cos * amorfScaleX) * (cos * amorfScaleX) + (sin * amorfScaleY) * (sin * amorfScaleY));
      var radialInset = safeInset / Math.max(1, fit.scale * Math.max(0.01, dirScale));
      var limit = Math.max(0.01, shapeRadiusAt(geometry, angle) - radialInset);
      return distance <= limit;
    }
    var rx = Math.max(1, geometry.w / 2 - safeInset);
    var ry = Math.max(1, geometry.h / 2 - safeInset);
    var dx = (px - geometry.cx) / rx;
    var dy = (py - geometry.cy) / ry;
    var distance = Math.sqrt(dx * dx + dy * dy);
    if (geometry.type === "dikdortgen") {
      return Math.abs(px - geometry.cx) <= rx && Math.abs(py - geometry.cy) <= ry;
    }
    if (distance > 1) return false;
    return true;
  }

  function makeShapeGeometry(shape, bounds, canvasW, canvasH) {
    var cfg = normalizeShapeConfig(shape);
    var x = finiteNumber(bounds && bounds.x, 0);
    var y = finiteNumber(bounds && bounds.y, 0);
    var w = finiteNumber(bounds && bounds.w, canvasW);
    var h = finiteNumber(bounds && bounds.h, canvasH);
    var cx = x + w / 2;
    var cy = y + h / 2;
    var radii = cfg.type === "amorf" && !cfg.outlineCommands
      ? smoothAmorfRenderRadii(cfg.radii || defaultShapeRadii(cfg.type))
      : (cfg.radii || defaultShapeRadii(cfg.type));
    var shapeExtents = null;
    var amorfTransform = null;
    var fitMargin = null;
    if (cfg.type === "amorf") {
      var safe = safeAmorfBounds(bounds, canvasW, canvasH);
      x = safe.x;
      y = safe.y;
      w = safe.w;
      h = safe.h;
      cx = x + w / 2;
      cy = y + h / 2;
      if (cfg.outlineCommands) {
        amorfTransform = { x: 1, y: 1, desiredRatio: cfg.widthCm / Math.max(1, cfg.lengthCm) };
      } else {
        amorfTransform = amorfPhysicalTransform(radii, cfg.widthCm, cfg.lengthCm);
        shapeExtents = shapeUnitExtents(radii, amorfTransform.x, amorfTransform.y);
      }
      fitMargin = safe.fitMargin;
    }
    if (cfg.type === "yuvarlak") {
      var size = Math.min(w, h);
      x = cx - size / 2;
      y = cy - size / 2;
      w = size;
      h = size;
    }
    return {
      type: cfg.type,
      template: cfg.template,
      x: x,
      y: y,
      w: w,
      h: h,
      cx: cx,
      cy: cy,
      radii: radii,
      outlineCommands: cfg.outlineCommands,
      presetKey: cfg.presetKey,
      presetLabel: cfg.presetLabel,
      label: cfg.label,
      _shapeExtents: shapeExtents,
      _amorfScaleX: amorfTransform ? amorfTransform.x : 1,
      _amorfScaleY: amorfTransform ? amorfTransform.y : 1,
      _physicalAspectRatio: amorfTransform ? amorfTransform.desiredRatio : (cfg.widthCm / Math.max(1, cfg.lengthCm)),
      fitMargin: fitMargin,
      widthCm: cfg.widthCm,
      lengthCm: cfg.lengthCm,
      width: cfg.widthCm,
      length: cfg.lengthCm,
      canvasW: canvasW,
      canvasH: canvasH,
      product: cfg.product || null
    };
  }

  function insetShapeGeometry(geometry, inset) {
    var safeInset = Math.max(0, Math.round(finiteNumber(inset, 0)));
    if (!geometry || safeInset <= 0) return geometry;
    var right = finiteNumber(geometry.x + geometry.w, geometry.canvasW);
    var bottom = finiteNumber(geometry.y + geometry.h, geometry.canvasH);
    var x = clamp(geometry.x + safeInset, 0, Math.max(1, geometry.canvasW - 1));
    var y = clamp(geometry.y + safeInset, 0, Math.max(1, geometry.canvasH - 1));
    var w = Math.max(1, Math.round(right - safeInset - x));
    var h = Math.max(1, Math.round(bottom - safeInset - y));
    if (geometry.type === "yuvarlak") {
      var d = Math.max(1, Math.min(w, h));
      x = Math.round(x + (w - d) / 2);
      y = Math.round(y + (h - d) / 2);
      w = d;
      h = d;
    }
    return Object.assign({}, geometry, {
      x: x,
      y: y,
      w: w,
      h: h,
      cx: x + w / 2,
      cy: y + h / 2
    });
  }

  function shapePathMaskAnalysis(geometry, inset) {
    var canvasW = geometry.canvasW;
    var canvasH = geometry.canvasH;
    var safeInset = Math.max(0, Math.round(finiteNumber(inset, 0)));
    var points = amorfShapePathPoints(geometry, safeInset);
    var minPointX = Infinity;
    var minPointY = Infinity;
    var maxPointX = -Infinity;
    var maxPointY = -Infinity;
    for (var p = 0; p < points.length; p++) {
      if (points[p].x < minPointX) minPointX = points[p].x;
      if (points[p].x > maxPointX) maxPointX = points[p].x;
      if (points[p].y < minPointY) minPointY = points[p].y;
      if (points[p].y > maxPointY) maxPointY = points[p].y;
    }
    var pad = 8;
    var minX = clamp(Math.floor(minPointX - pad), 0, canvasW - 1);
    var minY = clamp(Math.floor(minPointY - pad), 0, canvasH - 1);
    var maxX = clamp(Math.ceil(maxPointX + pad), 1, canvasW);
    var maxY = clamp(Math.ceil(maxPointY + pad), 1, canvasH);
    var w = Math.max(1, maxX - minX);
    var h = Math.max(1, maxY - minY);
    var maskCanvas = createCanvas(canvasW, canvasH);
    var mctx = maskCanvas.getContext("2d", { willReadFrequently: true });
    var mask = new Uint8Array(canvasW * canvasH);
    var count = 0;

    mctx.fillStyle = "#000";
    traceShapePath(mctx, geometry, safeInset);
    mctx.fill();

    var data = mctx.getImageData(minX, minY, w, h).data;
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        if (data[(y * w + x) * 4 + 3] <= 8) continue;
        mask[(minY + y) * canvasW + (minX + x)] = 1;
        count++;
      }
    }

    return {
      x: minX,
      y: minY,
      w: w,
      h: h,
      coverage: count / Math.max(1, canvasW * canvasH),
      fallback: "shape-" + geometry.type + "-path",
      mask: mask,
      maskWidth: canvasW,
      maskHeight: canvasH,
      _canvasWidth: canvasW,
      _canvasHeight: canvasH
    };
  }

  function shapeMaskAnalysis(geometry, inset) {
    var canvasW = geometry.canvasW;
    var canvasH = geometry.canvasH;
    if (isAmorfGeometry(geometry)) {
      return shapePathMaskAnalysis(geometry, inset);
    }
    var mask = new Uint8Array(canvasW * canvasH);
    var safeInset = Math.max(0, Math.round(finiteNumber(inset, 0)));
    var minX = clamp(Math.floor(geometry.x + safeInset - 2), 0, canvasW - 1);
    var minY = clamp(Math.floor(geometry.y + safeInset - 2), 0, canvasH - 1);
    var maxX = clamp(Math.ceil(geometry.x + geometry.w - safeInset + 2), 1, canvasW);
    var maxY = clamp(Math.ceil(geometry.y + geometry.h - safeInset + 2), 1, canvasH);
    var count = 0;

    for (var y = minY; y < maxY; y++) {
      for (var x = minX; x < maxX; x++) {
        if (!insideShape(geometry, x + 0.5, y + 0.5, safeInset)) continue;
        mask[y * canvasW + x] = 1;
        count++;
      }
    }

    return {
      x: minX,
      y: minY,
      w: Math.max(1, maxX - minX),
      h: Math.max(1, maxY - minY),
      coverage: count / Math.max(1, canvasW * canvasH),
      fallback: "shape-" + geometry.type,
      mask: mask,
      maskWidth: canvasW,
      maskHeight: canvasH,
      _canvasWidth: canvasW,
      _canvasHeight: canvasH
    };
  }

  function drawAnalysisMask(ctx, analysis, mode) {
    if (!analysis || !analysis.mask) return false;
    var maskCanvas = createCanvas(analysis.maskWidth, analysis.maskHeight);
    var mctx = maskCanvas.getContext("2d", { willReadFrequently: true });
    var img = mctx.createImageData(analysis.maskWidth, analysis.maskHeight);
    var data = img.data;
    for (var i = 0; i < analysis.mask.length; i++) {
      if (analysis.mask[i]) data[i * 4 + 3] = 255;
    }
    mctx.putImageData(img, 0, 0);
    ctx.save();
    ctx.globalCompositeOperation = mode || "destination-in";
    ctx.drawImage(maskCanvas, 0, 0);
    ctx.restore();
    return true;
  }

  function clonePlain(value) {
    try { return JSON.parse(JSON.stringify(value)); } catch (err) { return value; }
  }

  function canvasAlphaCoverage(canvas, threshold) {
    var thr = finiteNumber(threshold, 16);
    if (!canvas || !canvas.width || !canvas.height) return 0;
    try {
      var ctx = canvas.getContext("2d", { willReadFrequently: true });
      var data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      var step = Math.max(1, Math.floor(Math.min(canvas.width, canvas.height) / 900));
      var total = 0;
      var kept = 0;
      for (var y = 0; y < canvas.height; y += step) {
        for (var x = 0; x < canvas.width; x += step) {
          total++;
          if (data[(y * canvas.width + x) * 4 + 3] > thr) kept++;
        }
      }
      return kept / Math.max(1, total);
    } catch (err) {
      return 0;
    }
  }

  function measureCanvasAlphaBBox(canvas, threshold, padPx) {
    var thr = finiteNumber(threshold, 16);
    if (!canvas || !canvas.width || !canvas.height) return null;
    try {
      var w = canvas.width;
      var h = canvas.height;
      var ctx = canvas.getContext("2d", { willReadFrequently: true });
      var data = ctx.getImageData(0, 0, w, h).data;
      var minX = w;
      var minY = h;
      var maxX = -1;
      var maxY = -1;
      for (var y = 0; y < h; y++) {
        for (var x = 0; x < w; x++) {
          if (data[(y * w + x) * 4 + 3] <= thr) continue;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
      if (maxX < 0) return null;
      var pad = Math.max(0, Math.round(finiteNumber(padPx, 0)));
      minX = Math.max(0, minX - pad);
      minY = Math.max(0, minY - pad);
      maxX = Math.min(w - 1, maxX + pad);
      maxY = Math.min(h - 1, maxY + pad);
      return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
    } catch (err) {
      return null;
    }
  }

  function canvasCornerAlpha(canvas) {
    if (!canvas || !canvas.width || !canvas.height) return null;
    try {
      var ctx = canvas.getContext("2d", { willReadFrequently: true });
      var w = canvas.width;
      var h = canvas.height;
      var data = ctx.getImageData(0, 0, w, h).data;
      function alphaAt(x, y) {
        return data[(y * w + x) * 4 + 3];
      }
      return {
        tl: alphaAt(0, 0),
        tr: alphaAt(w - 1, 0),
        bl: alphaAt(0, h - 1),
        br: alphaAt(w - 1, h - 1)
      };
    } catch (err) {
      return null;
    }
  }

  function arGeometryFromLast(shapeConfig, last, canvas) {
    var info = last && last.shape;
    if (!info || !canvas || !canvas.width || !canvas.height) return null;
    var type = normalizeShapeType(info.type || shapeConfig && shapeConfig.type);
    if (type === "dikdortgen") return null;
    var x = finiteNumber(info.x, 0);
    var y = finiteNumber(info.y, 0);
    var w = Math.max(1, finiteNumber(info.w, canvas.width));
    var h = Math.max(1, finiteNumber(info.h, canvas.height));
    var radii = Array.isArray(shapeConfig && shapeConfig.radii)
      ? shapeConfig.radii.slice(0)
      : defaultShapeRadii(type);
    var geometry = {
      type: type,
      x: x,
      y: y,
      w: w,
      h: h,
      cx: x + w / 2,
      cy: y + h / 2,
      radii: radii,
      presetKey: info.presetKey || shapeConfig && shapeConfig.presetKey || "",
      presetLabel: info.presetLabel || shapeConfig && shapeConfig.presetLabel || "",
      widthCm: finiteNumber(info.widthCm, shapeConfig && shapeConfig.widthCm || 160),
      lengthCm: finiteNumber(info.lengthCm, shapeConfig && shapeConfig.lengthCm || 230),
      width: finiteNumber(info.widthCm, shapeConfig && shapeConfig.widthCm || 160),
      length: finiteNumber(info.lengthCm, shapeConfig && shapeConfig.lengthCm || 230),
      canvasW: canvas.width,
      canvasH: canvas.height,
      fitMargin: info.fitMargin || null
    };
    if (type === "amorf") geometry._shapeExtents = shapeUnitExtents(radii);
    return geometry;
  }

  function scaledPreviewLastForAr(previewLast, sourceCanvas, targetCanvas) {
    var info = previewLast && previewLast.shape;
    if (!info || !sourceCanvas || !targetCanvas || !sourceCanvas.width || !sourceCanvas.height || !targetCanvas.width || !targetCanvas.height) return null;
    var sx = targetCanvas.width / sourceCanvas.width;
    var sy = targetCanvas.height / sourceCanvas.height;
    var scaled = clonePlain(previewLast);
    var fitMargin = info.fitMargin ? {
      canvas: finiteNumber(info.fitMargin.canvas, 0) * Math.min(sx, sy),
      inner: finiteNumber(info.fitMargin.inner, 0) * Math.min(sx, sy)
    } : null;
    scaled.shape = Object.assign({}, info, {
      x: finiteNumber(info.x, 0) * sx,
      y: finiteNumber(info.y, 0) * sy,
      w: finiteNumber(info.w, targetCanvas.width) * sx,
      h: finiteNumber(info.h, targetCanvas.height) * sy,
      fitMargin: fitMargin
    });
    return scaled;
  }

  function arGeometryPoints(geometry) {
    if (!geometry) return [];
    if (geometry.type === "amorf") return amorfShapePathPoints(geometry, 0);
    var steps = geometry.type === "yuvarlak" || geometry.type === "oval" ? 144 : 4;
    var points = [];
    if (steps === 4) {
      return [
        { x: geometry.x, y: geometry.y },
        { x: geometry.x + geometry.w, y: geometry.y },
        { x: geometry.x + geometry.w, y: geometry.y + geometry.h },
        { x: geometry.x, y: geometry.y + geometry.h }
      ];
    }
    for (var i = 0; i < steps; i++) {
      points.push(shapePoint(geometry, i / steps * Math.PI * 2, 0));
    }
    return points;
  }

  function applyArGeometryClip(canvas, geometry) {
    if (!canvas || !geometry) return false;
    var ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.save();
    traceShapePath(ctx, geometry, 0);
    ctx.globalCompositeOperation = "destination-in";
    ctx.fillStyle = "#000";
    ctx.fill();
    ctx.restore();
    return true;
  }

  function buildArMaskArtifact(canvas, geometry) {
    var points = arGeometryPoints(geometry);
    var pointBounds = null;
    if (points.length) {
      var minX = Infinity;
      var minY = Infinity;
      var maxX = -Infinity;
      var maxY = -Infinity;
      points.forEach(function (point) {
        if (point.x < minX) minX = point.x;
        if (point.x > maxX) maxX = point.x;
        if (point.y < minY) minY = point.y;
        if (point.y > maxY) maxY = point.y;
      });
      pointBounds = isFinite(minX) && isFinite(minY) && isFinite(maxX) && isFinite(maxY)
        ? { x: minX, y: minY, w: maxX - minX, h: maxY - minY }
        : null;
    }
    var shapeBoundsPx = geometry ? Object.assign({
      type: geometry.type,
      presetKey: geometry.presetKey || "",
      presetLabel: geometry.presetLabel || "",
      geometryBox: { x: geometry.x, y: geometry.y, w: geometry.w, h: geometry.h }
    }, pointBounds || { x: geometry.x, y: geometry.y, w: geometry.w, h: geometry.h }) : null;
    return {
      source: "massimo-preview-transparent-ar",
      meshSource: geometry ? "preview-geometry" : "rectangular-preview",
      deterministic: !!geometry,
      coverage: canvasAlphaCoverage(canvas, 16),
      alphaCoverage220: canvasAlphaCoverage(canvas, 220),
      alphaBBox: measureCanvasAlphaBBox(canvas, 16, 2),
      alphaBBox220: measureCanvasAlphaBBox(canvas, 220, 2),
      cornerAlpha: canvasCornerAlpha(canvas),
      shapeBoundsPx: shapeBoundsPx,
      geometry: geometry ? clonePlain(Object.assign({}, geometry, { _shapeExtents: null })) : null,
      silhouettePointCount: points.length,
      silhouettePointsPx: points.map(function (point) {
        return { x: point.x, y: point.y };
      }),
      silhouettePoints: points.map(function (point) {
        return { nx: point.x / Math.max(1, canvas.width), ny: point.y / Math.max(1, canvas.height) };
      })
    };
  }

  function traceShapePath(ctx, geometry, inset) {
    var safeInset = Math.max(0, finiteNumber(inset, 0));
    if (geometry.type === "oval" || geometry.type === "yuvarlak") {
      var rx = Math.max(1, geometry.w / 2 - safeInset);
      var ry = Math.max(1, geometry.h / 2 - safeInset);
      if (geometry.type === "yuvarlak") {
        rx = ry = Math.max(1, Math.min(geometry.w, geometry.h) / 2 - safeInset);
      }
      ctx.beginPath();
      ctx.ellipse(geometry.cx, geometry.cy, rx, ry, 0, 0, Math.PI * 2);
      ctx.closePath();
      return;
    }

    if (isAmorfGeometry(geometry)) {
      if (hasOutlineCommands(geometry)) {
        ctx.beginPath();
        traceCommandPath(ctx, geometry.outlineCommands, outlineCommandBody(geometry, safeInset));
        return;
      }
      traceSmoothClosedPath(ctx, amorfShapePathPoints(geometry, safeInset), 0.78);
      return;
    }

    var steps = Math.max(48, Math.min(144, (geometry.radii || []).length || 72));
    ctx.beginPath();
    for (var i = 0; i <= steps; i++) {
      var angle = i / steps * Math.PI * 2;
      var p = shapePoint(geometry, angle, safeInset);
      if (i === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    }
    ctx.closePath();
  }

  function drawShapeShadow(ctx, geometry) {
    if (ctx && ctx.canvas && ctx.canvas.__massimoArTransparentSurface) return;
    ctx.save();
    ctx.shadowColor = "rgba(35,31,27,0.18)";
    ctx.shadowBlur = 24;
    ctx.shadowOffsetY = 12;
    ctx.fillStyle = "rgba(35,31,27,0.14)";
    traceShapePath(ctx, geometry, 0);
    ctx.fill();
    ctx.restore();
  }

  function drawShapeBodyPattern(ctx, geometry, edgeDepth, edgeType, patternImg, options) {
    var bodyInset = edgeType === "overlok" ? 0 : edgeDepth;
    var body = shapeMaskAnalysis(geometry, bodyInset);
    var textureBody = body;
    if (edgeType === "biye" && isAmorfGeometry(geometry)) {
      textureBody = shapeMaskAnalysis(geometry, 0);
    }
    var bodyOptions = options;
    var bodyEdgeType = edgeType || "overlok";
    if (edgeType === "overlok"
      && !(options && (options.renderPipeline === "photoSurfaceHD" || isBarcelonaSurfacePipeline(options) || isBarcelonaSingleSurfacePipeline(options)))) {
      if (usesBodyRepeatSurface(options)) {
        bodyOptions = overlokCleanBodyOptions(options);
      } else {
        bodyEdgeType = "cift_bordur";
        bodyOptions = Object.assign({}, options || {}, {
          __massimoOverlokToneTransferBody: true
        });
      }
    }
    if (!drawTextureTransfer(ctx, textureBody, 0, bodyEdgeType, patternImg, bodyOptions)) {
      drawBodyPattern(ctx, textureBody, 0, bodyEdgeType, patternImg, bodyOptions);
    }
    return body;
  }

  function copyCleanBodyEdges(ctx, x, y, w, h, guard) {
    var safeGuard = Math.max(2, Math.round(finiteNumber(guard, 0)));
    var ix = Math.round(finiteNumber(x, 0));
    var iy = Math.round(finiteNumber(y, 0));
    var iw = Math.max(1, Math.round(finiteNumber(w, 0)));
    var ih = Math.max(1, Math.round(finiteNumber(h, 0)));
    safeGuard = Math.min(safeGuard, Math.max(1, Math.floor(Math.min(iw, ih) / 3)));
    if (iw < safeGuard * 2 || ih < safeGuard * 2) return;
    var source = createCanvas(ctx.canvas.width, ctx.canvas.height);
    source.getContext("2d", { willReadFrequently: true }).drawImage(ctx.canvas, 0, 0);
    ctx.save();
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(source, ix, iy + safeGuard, iw, safeGuard, ix, iy, iw, safeGuard);
    ctx.drawImage(source, ix, iy + ih - safeGuard * 2, iw, safeGuard, ix, iy + ih - safeGuard, iw, safeGuard);
    ctx.drawImage(source, ix + safeGuard, iy, safeGuard, ih, ix, iy, safeGuard, ih);
    ctx.drawImage(source, ix + iw - safeGuard * 2, iy, safeGuard, ih, ix + iw - safeGuard, iy, safeGuard, ih);
    ctx.restore();
  }

  function healBiyeRectBodyEdges(ctx, bounds, edgeDepth) {
    if (!bounds) return;
    var inset = Math.max(0, Math.round(finiteNumber(edgeDepth, 0)));
    var x = bounds.x + inset;
    var y = bounds.y + inset;
    var w = Math.max(1, bounds.w - inset * 2);
    var h = Math.max(1, bounds.h - inset * 2);
    var guard = Math.max(18, Math.round(inset * 2.8));
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();
    copyCleanBodyEdges(ctx, x, y, w, h, guard);
    ctx.restore();
  }

  function healBiyeShapeBodyEdges(ctx, geometry, edgeDepth) {
    if (!geometry) return;
    if (isAmorfGeometry(geometry)) {
      return;
    }
    var inset = Math.max(0, Math.round(finiteNumber(edgeDepth, 0)));
    var x = geometry.x + inset;
    var y = geometry.y + inset;
    var w = Math.max(1, geometry.w - inset * 2);
    var h = Math.max(1, geometry.h - inset * 2);
    var guard = Math.max(18, Math.round(inset * 2.8));
    ctx.save();
    traceShapePath(ctx, geometry, inset);
    ctx.clip();
    copyCleanBodyEdges(ctx, x, y, w, h, guard);
    ctx.restore();
  }

  function copyOverlokCleanBodyEdges(ctx, x, y, w, h, guard) {
    var safeGuard = Math.max(8, Math.round(finiteNumber(guard, 0)));
    var ix = Math.round(finiteNumber(x, 0));
    var iy = Math.round(finiteNumber(y, 0));
    var iw = Math.max(1, Math.round(finiteNumber(w, 0)));
    var ih = Math.max(1, Math.round(finiteNumber(h, 0)));
    safeGuard = Math.min(safeGuard, Math.max(1, Math.floor(Math.min(iw, ih) / 3)));
    if (iw < safeGuard * 2 || ih < safeGuard * 2) return;
    var source = createCanvas(ctx.canvas.width, ctx.canvas.height);
    source.getContext("2d", { willReadFrequently: true }).drawImage(ctx.canvas, 0, 0);
    var innerX = ix + safeGuard;
    var innerY = iy + safeGuard;
    var innerW = Math.max(1, iw - safeGuard * 2);
    var innerH = Math.max(1, ih - safeGuard * 2);
    ctx.save();
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(source, innerX, innerY, innerW, safeGuard, ix, iy, iw, safeGuard);
    ctx.drawImage(source, innerX, iy + ih - safeGuard * 2, innerW, safeGuard, ix, iy + ih - safeGuard, iw, safeGuard);
    ctx.drawImage(source, innerX, innerY, safeGuard, innerH, ix, iy, safeGuard, ih);
    ctx.drawImage(source, ix + iw - safeGuard * 2, innerY, safeGuard, innerH, ix + iw - safeGuard, iy, safeGuard, ih);
    ctx.restore();
  }

  function overlokEdgeHealGuard(bounds) {
    var minSide = Math.min(finiteNumber(bounds && bounds.w, 0), finiteNumber(bounds && bounds.h, 0));
    return Math.max(96, Math.round(minSide * 0.09));
  }

  function healOverlokRectBodyEdges(ctx, bounds) {
    if (!bounds) return;
    var x = finiteNumber(bounds.x, 0);
    var y = finiteNumber(bounds.y, 0);
    var w = Math.max(1, finiteNumber(bounds.w, 0));
    var h = Math.max(1, finiteNumber(bounds.h, 0));
    var guard = overlokEdgeHealGuard(bounds);
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();
    copyOverlokCleanBodyEdges(ctx, x, y, w, h, guard);
    ctx.restore();
  }

  function healOverlokShapeBodyEdges(ctx, geometry) {
    if (!geometry) return;
    var shapeType = normalizeShapeType(geometry.type);
    if (shapeType === "amorf" || shapeType === "oval" || shapeType === "yuvarlak") {
      return;
    }
    var guard = overlokEdgeHealGuard(geometry);
    ctx.save();
    traceShapePath(ctx, geometry, 0);
    ctx.clip();
    copyOverlokCleanBodyEdges(ctx, geometry.x, geometry.y, geometry.w, geometry.h, guard);
    ctx.restore();
    /* v223: Amorf dar tepe/dip streak heal */
    if (isAmorfGeometry(geometry)) {
      healAmorfTipsWithInnerBand(ctx, geometry);
    }
  }

  function repairBarcelonaOverlokShapeSideEdges(ctx, geometry, options, force) {
    if (!ctx || !ctx.canvas || !geometry || (!force && !options)) return;
    if (!force && (!isBarcelonaSurfacePipeline(options) || isBarcelonaSingleSurfacePipeline(options))) return;
    var shapeType = normalizeShapeType(geometry.type);
    if (shapeType !== "amorf" && shapeType !== "oval" && shapeType !== "yuvarlak") return;
    var analysis = shapeMaskAnalysis(geometry, 0);
    if (!analysis || !analysis.mask || !analysis.maskWidth || !analysis.maskHeight) return;
    var canvasW = ctx.canvas.width;
    var canvasH = ctx.canvas.height;
    if (!canvasW || !canvasH || analysis.maskWidth !== canvasW || analysis.maskHeight !== canvasH) return;
    var minSide = Math.min(finiteNumber(geometry.w, 0), finiteNumber(geometry.h, 0));
    var sideBand = force
      ? Math.round(clamp(Math.round(minSide * (shapeType === "oval" ? 0.062 : 0.052)), 18, 82))
      : shapeType === "oval"
      ? Math.round(clamp(Math.round(minSide * 0.085), 24, 118))
      : Math.round(clamp(Math.round(minSide * 0.048), 16, 72));
    var sidePull = Math.round(Math.max(sideBand + 24, sideBand * 1.9));
    var minRowWidth = sidePull + sideBand + 4;
    var image;
    try {
      image = ctx.getImageData(0, 0, canvasW, canvasH);
    } catch (_) {
      return;
    }
    var data = image.data;
    var source = new Uint8ClampedArray(data);
    var minY = clamp(Math.floor(analysis.y), 0, canvasH - 1);
    var maxY = clamp(Math.ceil(analysis.y + analysis.h), 0, canvasH);
    var rows = 0;
    for (var y = minY; y < maxY; y++) {
      var rowStart = y * canvasW;
      var left = -1;
      var right = -1;
      var scanMinX = clamp(Math.floor(analysis.x), 0, canvasW - 1);
      var scanMaxX = clamp(Math.ceil(analysis.x + analysis.w), 1, canvasW);
      for (var x = scanMinX; x < scanMaxX; x++) {
        if (!analysis.mask[rowStart + x]) continue;
        if (left < 0) left = x;
        right = x;
      }
      if (left < 0 || right <= left) continue;
      var rowWidth = right - left + 1;
      if (rowWidth < minRowWidth) continue;
      var rowBand = Math.min(sideBand, Math.max(0, Math.floor((rowWidth - sidePull - 2) / 2)));
      if (rowBand < 2) continue;
      for (var o = 0; o < rowBand; o++) {
        var leftDest = left + o;
        var leftSource = left + sidePull + o;
        var rightDest = right - rowBand + 1 + o;
        var rightSource = right - sidePull - rowBand + 1 + o;
        if (leftSource <= right && analysis.mask[rowStart + leftDest] && analysis.mask[rowStart + leftSource]) {
          var ld = (rowStart + leftDest) * 4;
          var ls = (rowStart + leftSource) * 4;
          data[ld] = source[ls];
          data[ld + 1] = source[ls + 1];
          data[ld + 2] = source[ls + 2];
          data[ld + 3] = source[ls + 3];
        }
        if (rightSource >= left && analysis.mask[rowStart + rightDest] && analysis.mask[rowStart + rightSource]) {
          var rd = (rowStart + rightDest) * 4;
          var rs = (rowStart + rightSource) * 4;
          data[rd] = source[rs];
          data[rd + 1] = source[rs + 1];
          data[rd + 2] = source[rs + 2];
          data[rd + 3] = source[rs + 3];
        }
      }
      rows++;
    }

    source = new Uint8ClampedArray(data);
    var topBand = force
      ? Math.round(clamp(Math.round(minSide * (shapeType === "oval" ? 0.052 : 0.04)), 16, 72))
      : shapeType === "oval"
      ? Math.round(clamp(Math.round(minSide * 0.046), 18, 66))
      : Math.round(clamp(Math.round(minSide * 0.02), 10, 32));
    var topPull = Math.round(Math.max(topBand + 22, topBand * 2));
    var minColHeight = topPull + topBand + 4;
    var cols = 0;
    var minX = clamp(Math.floor(analysis.x), 0, canvasW - 1);
    var maxX = clamp(Math.ceil(analysis.x + analysis.w), 0, canvasW);
    for (var cx = minX; cx < maxX; cx++) {
      var top = -1;
      var bottom = -1;
      for (var cy = minY; cy < maxY; cy++) {
        var maskIndex = cy * canvasW + cx;
        if (!analysis.mask[maskIndex]) continue;
        if (top < 0) top = cy;
        bottom = cy;
      }
      if (top < 0 || bottom <= top) continue;
      var colHeight = bottom - top + 1;
      if (colHeight < minColHeight) continue;
      var colBand = Math.min(topBand, Math.max(0, Math.floor((colHeight - topPull - 2) / 2)));
      if (colBand < 2) continue;
      for (var co = 0; co < colBand; co++) {
        var topDest = top + co;
        var topSource = top + topPull + co;
        var bottomDest = bottom - colBand + 1 + co;
        var bottomSource = bottom - topPull - colBand + 1 + co;
        if (topSource <= bottom && analysis.mask[topDest * canvasW + cx] && analysis.mask[topSource * canvasW + cx]) {
          var td = (topDest * canvasW + cx) * 4;
          var ts = (topSource * canvasW + cx) * 4;
          data[td] = source[ts];
          data[td + 1] = source[ts + 1];
          data[td + 2] = source[ts + 2];
          data[td + 3] = source[ts + 3];
        }
        if (bottomSource >= top && analysis.mask[bottomDest * canvasW + cx] && analysis.mask[bottomSource * canvasW + cx]) {
          var bd = (bottomDest * canvasW + cx) * 4;
          var bs = (bottomSource * canvasW + cx) * 4;
          data[bd] = source[bs];
          data[bd + 1] = source[bs + 1];
          data[bd + 2] = source[bs + 2];
          data[bd + 3] = source[bs + 3];
        }
      }
      cols++;
    }
    if (!rows && !cols) return;
    ctx.putImageData(image, 0, 0);
    ctx.canvas.__massimoTextureDebug = Object.assign({}, ctx.canvas.__massimoTextureDebug || {}, {
      shapeSideEdgeRepair: {
        mode: force ? "overlok-body-outer-edge-clean" : "barcelona-overlok-side-edge",
        sideBand: sideBand,
        sidePull: sidePull,
        topBand: topBand,
        topPull: topPull,
        rows: rows,
        cols: cols
      }
    });
  }

  function healAmorfTipsWithInnerBand(ctx, geometry) {
    var x = Math.round(finiteNumber(geometry.x, 0));
    var y = Math.round(finiteNumber(geometry.y, 0));
    var w = Math.max(1, Math.round(finiteNumber(geometry.w, 0)));
    var h = Math.max(1, Math.round(finiteNumber(geometry.h, 0)));
    if (w < 16 || h < 16) return;
    var cw = ctx.canvas.width;
    var ch = ctx.canvas.height;
    var probeImg;
    try {
      probeImg = ctx.getImageData(Math.max(0, x), Math.max(0, y), Math.min(cw - x, w), Math.min(ch - y, h));
    } catch (_) { return; }
    var pw = probeImg.width;
    var ph = probeImg.height;
    var pData = probeImg.data;
    var topY = -1, bottomY = -1;
    var probeX = Math.max(0, Math.min(pw - 1, Math.round(pw / 2)));
    for (var py = 0; py < ph; py++) {
      if (pData[(py * pw + probeX) * 4 + 3] > 12) { topY = py; break; }
    }
    for (var py2 = ph - 1; py2 >= 0; py2--) {
      if (pData[(py2 * pw + probeX) * 4 + 3] > 12) { bottomY = py2; break; }
    }
    if (topY < 0 || bottomY < 0 || bottomY - topY < 32) return;
    var maskTopAbs = y + topY;
    var maskBottomAbs = y + bottomY;
    var maskH = maskBottomAbs - maskTopAbs;
    var tipBand = Math.max(60, Math.round(maskH * 0.20));
    var blurPx = Math.max(3, Math.round(Math.min(w, h) * 0.010));
    var source = createCanvas(ctx.canvas.width, ctx.canvas.height);
    source.getContext("2d", { willReadFrequently: true }).drawImage(ctx.canvas, 0, 0);
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, maskTopAbs, w, tipBand);
    ctx.clip();
    ctx.beginPath();
    traceShapePath(ctx, geometry, 0);
    ctx.clip();
    try {
      ctx.filter = "blur(" + blurPx + "px)";
      ctx.drawImage(source, x, maskTopAbs, w, tipBand, x, maskTopAbs, w, tipBand);
      ctx.filter = "none";
    } catch (_) {}
    ctx.restore();
    var bottomStart = maskBottomAbs - tipBand;
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, bottomStart, w, tipBand);
    ctx.clip();
    ctx.beginPath();
    traceShapePath(ctx, geometry, 0);
    ctx.clip();
    try {
      ctx.filter = "blur(" + blurPx + "px)";
      ctx.drawImage(source, x, bottomStart, w, tipBand, x, bottomStart, w, tipBand);
      ctx.filter = "none";
    } catch (_) {}
    ctx.restore();
  }

  function drawAmorfSoftEdgeMaterial(ctx, geometry, fabricColor, fabricImg, overlayOnly) {
    var baseColor = averageFabricImageColor(fabricImg, fabricColor || "#897b6d");
    if (!overlayOnly) {
      ctx.fillStyle = baseColor;
      ctx.fillRect(geometry.x, geometry.y, geometry.w, geometry.h);
    }

    var crown = ctx.createLinearGradient(geometry.x, geometry.y, geometry.x, geometry.y + geometry.h);
    crown.addColorStop(0, overlayOnly ? "rgba(255,255,255,0.035)" : "rgba(255,255,255,0.11)");
    crown.addColorStop(0.42, overlayOnly ? "rgba(255,255,255,0.01)" : "rgba(255,255,255,0.02)");
    crown.addColorStop(0.72, overlayOnly ? "rgba(0,0,0,0.018)" : "rgba(0,0,0,0.035)");
    crown.addColorStop(1, overlayOnly ? "rgba(0,0,0,0.035)" : "rgba(0,0,0,0.08)");
    ctx.save();
    ctx.fillStyle = crown;
    ctx.fillRect(geometry.x, geometry.y, geometry.w, geometry.h);
    ctx.restore();
  }

  function strokeAmorfRingCleanEdges(ctx, mask, geometry, outerInset, innerInset, color, biyeMode) {
    if (!ctx || !geometry) return;
    var depth = Math.max(1, finiteNumber(innerInset, 1) - finiteNumber(outerInset, 0));
    var line = biyeMode
      ? Math.max(1.25, Math.min(2.5, depth * 0.055))
      : Math.max(1.4, Math.min(3.2, depth * 0.045));
    ctx.save();
    ctx.globalAlpha = biyeMode ? 0.62 : 0.7;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.strokeStyle = color || "#8f8272";
    ctx.lineWidth = line;
    traceShapePath(ctx, geometry, finiteNumber(outerInset, 0) + line * 0.35);
    ctx.stroke();
    traceShapePath(ctx, geometry, Math.max(0, finiteNumber(innerInset, 0) - line * 0.35));
    ctx.stroke();
    ctx.restore();
    if (mask) {
      ctx.save();
      ctx.globalCompositeOperation = "destination-in";
      ctx.drawImage(mask, 0, 0);
      ctx.restore();
    }
  }

  function drawAmorfShapeRingMask(ctx, geometry, outerInset, innerInset) {
    var safeOuter = Math.max(0, finiteNumber(outerInset, 0));
    var safeInner = Math.max(safeOuter + 1, finiteNumber(innerInset, safeOuter + 1));
    var innerCut = Math.max(safeOuter + 0.5, safeInner - 0.75);

    ctx.save();
    ctx.fillStyle = "#000";
    traceShapePath(ctx, geometry, safeOuter);
    ctx.fill();

    ctx.globalCompositeOperation = "destination-out";
    traceShapePath(ctx, geometry, innerCut);
    ctx.fill();
    ctx.restore();
  }

  function drawAmorfShapeRingBand(ctx, geometry, outerInset, innerInset, fabricColor, fabricImg, opacity, biyeMode) {
    var layer = createCanvas(geometry.canvasW, geometry.canvasH);
    var lctx = layer.getContext("2d", { willReadFrequently: true });
    var mask = createCanvas(geometry.canvasW, geometry.canvasH);
    var mctx = mask.getContext("2d", { willReadFrequently: true });
    var safeOuter = Math.max(0, finiteNumber(outerInset, 0));
    var safeInner = Math.max(safeOuter + 1, finiteNumber(innerInset, safeOuter + 1));
    var depth = Math.max(1, safeInner - safeOuter);

    lctx.fillStyle = averageFabricImageColor(fabricImg, fabricColor || "#897b6d");
    lctx.fillRect(geometry.x, geometry.y, geometry.w, geometry.h);
    var patternInfo = fabricImg
      ? drawFabricTextureFill(lctx, fabricImg, geometry.x, geometry.y, geometry.w, geometry.h, depth, "amorf-edge")
      : null;
    drawAmorfSoftEdgeMaterial(lctx, geometry, fabricColor, fabricImg, !!patternInfo);
    if (biyeMode) drawShapeBiyeCordShading(lctx, geometry, depth);

    drawAmorfShapeRingMask(mctx, geometry, safeOuter, safeInner);

    lctx.save();
    lctx.globalCompositeOperation = "destination-in";
    lctx.drawImage(mask, 0, 0);
    lctx.restore();
    strokeAmorfRingCleanEdges(lctx, mask, geometry, safeOuter, safeInner, averageFabricImageColor(fabricImg, fabricColor || "#897b6d"), biyeMode);
    if (patternInfo) {
      applyAmorfEdgeFabricFinish(lctx, geometry.x, geometry.y, geometry.w, geometry.h, depth, biyeMode ? 1.018 : 1.022);
      strokeAmorfRingCleanEdges(lctx, mask, geometry, safeOuter, safeInner, averageFabricImageColor(fabricImg, fabricColor || "#897b6d"), biyeMode);
      recordEdgeTextureDebug(
        ctx,
        fabricImg,
        biyeMode ? "amorf-biye-fabric-texture" : "amorf-bordur-fabric-texture",
        patternInfo,
        canvasRectLumaStats(layer, geometry.x, geometry.y, geometry.w, geometry.h)
      );
    }

    ctx.save();
    ctx.globalAlpha = opacity == null ? 1 : opacity;
    ctx.drawImage(layer, 0, 0);
    ctx.restore();

    // v210: amorf bordür dikiş — ÇOK İNCE sürekli çizgi (eğriyi takip eder), bordür içine 8% çekik.
    if (!biyeMode && depth >= 8) {
      var amorfInset = Math.max(1, Math.round(depth * 0.08));
      ctx.save();
      ctx.strokeStyle = "rgba(32, 26, 20, 0.26)";
      ctx.lineWidth = Math.max(0.6, Math.min(1.1, depth * 0.022));
      ctx.lineCap = "butt";
      traceShapePath(ctx, geometry, safeInner - amorfInset);
      ctx.stroke();
      ctx.restore();
    }
  }

  function drawShapeBand(ctx, geometry, outerInset, innerInset, fabricColor, fabricImg, opacity) {
    if (isAmorfGeometry(geometry)) {
      drawAmorfShapeRingBand(ctx, geometry, outerInset, innerInset, fabricColor, fabricImg, opacity, false);
      return;
    }
    var outer = shapeMaskAnalysis(geometry, outerInset);
    var inner = shapeMaskAnalysis(geometry, innerInset);
    var layer = createCanvas(geometry.canvasW, geometry.canvasH);
    var lctx = layer.getContext("2d", { willReadFrequently: true });
    lctx.fillStyle = averageFabricImageColor(fabricImg, fabricColor || "#897b6d");
    lctx.fillRect(geometry.x, geometry.y, geometry.w, geometry.h);
    var patternInfo = null;
    if (fabricImg) {
      patternInfo = drawFabricTextureFill(lctx, fabricImg, geometry.x, geometry.y, geometry.w, geometry.h, Math.max(8, innerInset - outerInset), "edge-hd");
    }
    drawAnalysisMask(lctx, outer, "destination-in");
    drawAnalysisMask(lctx, inner, "destination-out");
    if (patternInfo) {
      applyEdgeFabricFinish(lctx, geometry.x, geometry.y, geometry.w, geometry.h, Math.max(8, innerInset - outerInset), 1.06);
      recordEdgeTextureDebug(
        ctx,
        fabricImg,
        "shape-bordur-fabric-texture",
        patternInfo,
        canvasRectLumaStats(layer, geometry.x, geometry.y, geometry.w, geometry.h)
      );
    }
    ctx.save();
    ctx.globalAlpha = opacity == null ? 1 : opacity;
    ctx.drawImage(layer, 0, 0);
    ctx.restore();
  }

  function drawShapeBiyeBand(ctx, geometry, edgeDepth, fabricColor, fabricImg) {
    if (isAmorfGeometry(geometry)) {
      drawAmorfShapeRingBand(ctx, geometry, 0, edgeDepth, fabricColor, fabricImg, 1, true);
      return;
    }
    var layer = createCanvas(geometry.canvasW, geometry.canvasH);
    var lctx = layer.getContext("2d", { willReadFrequently: true });
    var mask = createCanvas(geometry.canvasW, geometry.canvasH);
    var mctx = mask.getContext("2d", { willReadFrequently: true });
    var safeDepth = Math.max(2, Math.round(finiteNumber(edgeDepth, 0)));

    lctx.fillStyle = averageFabricImageColor(fabricImg, fabricColor || "#897b6d");
    lctx.fillRect(geometry.x, geometry.y, geometry.w, geometry.h);
    var patternInfo = null;
    if (fabricImg) {
      patternInfo = drawBiyeFabricTextureFill(lctx, fabricImg, geometry.x, geometry.y, geometry.w, geometry.h, safeDepth);
    }
    drawShapeBiyeCordShading(lctx, geometry, safeDepth);

    mctx.save();
    mctx.strokeStyle = "#000";
    mctx.lineWidth = Math.max(2, safeDepth * 2.08);
    mctx.lineJoin = "round";
    mctx.lineCap = "round";
    traceShapePath(mctx, geometry, 0);
    mctx.stroke();
    mctx.restore();
    drawAnalysisMask(mctx, shapeMaskAnalysis(geometry, 0), "destination-in");

    lctx.save();
    lctx.globalCompositeOperation = "destination-in";
    lctx.drawImage(mask, 0, 0);
    lctx.restore();
    if (patternInfo) {
      applyEdgeFabricFinish(lctx, geometry.x, geometry.y, geometry.w, geometry.h, safeDepth, 1.05);
      recordEdgeTextureDebug(
        ctx,
        fabricImg,
        "shape-biye-fabric-texture",
        patternInfo,
        canvasRectLumaStats(layer, geometry.x, geometry.y, geometry.w, geometry.h)
      );
    }

    ctx.drawImage(layer, 0, 0);
  }

  function drawShapeBiyeCordDetails(ctx, geometry, edgeDepth) {
    return edgeDepth;
  }

  function drawShapeEdgeBand(ctx, geometry, depth, edgeType, fabric, fabricImg, doubleFabrics, doubleImgs, options) {
    if (isDoubleBordur(edgeType)) {
      var depths = doubleBordurDepths(depth);
      if (doubleFabrics && doubleFabrics.outer) {
        drawShapeBand(ctx, geometry, 0, depths.outer, swatchColor(doubleFabrics.outer), doubleImgs && doubleImgs.outer, 1);
      }
      if (doubleFabrics && doubleFabrics.inner) {
        drawShapeBand(ctx, geometry, depths.outer, depths.total, swatchColor(doubleFabrics.inner), doubleImgs && doubleImgs.inner, 1);
      }
      ctx.save();
      ctx.strokeStyle = "rgba(48,38,31,0.44)";
      ctx.lineWidth = 1.25;
      traceShapePath(ctx, geometry, depths.outer);
      ctx.stroke();
      traceShapePath(ctx, geometry, depths.total);
      ctx.stroke();
      ctx.restore();
      return depths.total;
    }

    var edgeDepth = edgeType === "biye"
      ? biyeVisualDepth(geometry, options)
      : bordurVisualDepth(depth);
    if (edgeType === "biye") {
      if (fabric) {
        drawShapeBiyeBand(ctx, geometry, edgeDepth, swatchColor(fabric), fabricImg);
      }
      drawShapeBiyeCordDetails(ctx, geometry, edgeDepth);
      return edgeDepth;
    }
    if (fabric) {
      drawShapeBand(ctx, geometry, 0, edgeDepth, swatchColor(fabric), fabricImg, 1);
    }
    ctx.save();
    ctx.strokeStyle = edgeType === "biye" ? "rgba(48,38,31,0.46)" : "rgba(70,58,48,0.34)";
    ctx.lineWidth = edgeType === "biye" ? 1.15 : 1.35;
    traceShapePath(ctx, geometry, edgeDepth);
    ctx.stroke();
    ctx.restore();
    return edgeDepth;
  }

  function drawShapeOverlok(ctx, geometry) {
    /* v220: Overlok kenar dikişi — şekil için. */
    if (!geometry || !ctx) return;
    var minSide = Math.min(finiteNumber(geometry.w, 0), finiteNumber(geometry.h, 0));
    if (minSide < 8) return;
    var lineWidth = Math.max(0.85, Math.min(2.35, minSide * 0.0019));
    ctx.save();
    try {
      traceShapePath(ctx, geometry, 0);
      ctx.clip();
    } catch (_) {}
    ctx.lineWidth = lineWidth;
    ctx.strokeStyle = "rgba(46,38,30,0.20)";
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    try {
      traceShapePath(ctx, geometry, lineWidth * 1.15);
      ctx.stroke();
    } catch (_) {}
    ctx.lineWidth = Math.max(0.65, lineWidth * 0.42);
    ctx.strokeStyle = "rgba(232,222,206,0.18)";
    try {
      traceShapePath(ctx, geometry, lineWidth * 2.05);
      ctx.stroke();
    } catch (_) {}
    ctx.restore();
  }

  function normalizeDesignConfig(design) {
    design = design || {};
    var regions = Array.isArray(design.regions) ? design.regions : [];
    return {
      active: !!design.active && regions.length > 0,
      templateId: design.templateId || "",
      label: design.label || "",
      topology: design.topology || "",
      premium: !!design.premium,
      premiumVariant: design.premiumVariant || null,
      designTopology: design.designTopology || null,
      regionCount: finiteNumber(design.regionCount, regions.length),
      outerShape: design.outerShape || "rect",
      stitchesVisible: design.stitchesVisible !== false,
      cutoutOutsideRegions: !!design.cutoutOutsideRegions,
      clipCutoutToShape: !!design.clipCutoutToShape,
      preserveRegionGaps: !!design.preserveRegionGaps,
      cleanRegionTexture: !!design.cleanRegionTexture,
      cleanRegionBaseLuma: !!design.cleanRegionBaseLuma,
      pieceEdgeMode: design.pieceEdgeMode || "",
      pieceEdgeScope: design.pieceEdgeScope || "",
      regions: regions,
      assignments: design.assignments || {},
      widthCm: finiteNumber(design.widthCm, 160),
      lengthCm: finiteNumber(design.lengthCm, 230)
    };
  }

  function designOuterShapeMode(design) {
    var outer = String(design && design.outerShape || "rect").toLowerCase();
    if (!outer || outer === "rect" || outer === "rectangle" || outer === "dikdortgen") return "dikdortgen";
    if (outer === "oval" || outer === "ellipse") return "oval";
    if (outer === "yuvarlak" || outer === "daire" || outer === "circle" || outer === "round") return "yuvarlak";
    return "amorf";
  }

  function designBounds(bounds, edgeDepth) {
    var inset = Math.max(0, Math.round(finiteNumber(edgeDepth, 0)));
    return {
      x: bounds.x + inset,
      y: bounds.y + inset,
      w: Math.max(1, bounds.w - inset * 2),
      h: Math.max(1, bounds.h - inset * 2)
    };
  }

  function physicalAspectBounds(bounds, widthCm, lengthCm, canvasW, canvasH) {
    if (!bounds) return bounds;
    var selectedW = Math.max(1, finiteNumber(widthCm, 160));
    var selectedL = Math.max(1, finiteNumber(lengthCm, 230));
    var desiredRatio = clamp(selectedW / selectedL, 0.08, 8);
    var sourceW = Math.max(1, finiteNumber(bounds.w, canvasW || 1));
    var sourceH = Math.max(1, finiteNumber(bounds.h, canvasH || 1));
    var sourceRatio = sourceW / Math.max(1, sourceH);
    if (!Number.isFinite(sourceRatio) || Math.abs(sourceRatio - desiredRatio) < 0.015) {
      return bounds;
    }

    var nextW = sourceW;
    var nextH = sourceH;
    if (sourceRatio > desiredRatio) {
      nextW = Math.max(1, sourceH * desiredRatio);
    } else {
      nextH = Math.max(1, sourceW / desiredRatio);
    }
    var nextX = finiteNumber(bounds.x, 0) + (sourceW - nextW) / 2;
    var nextY = finiteNumber(bounds.y, 0) + (sourceH - nextH) / 2;
    var maxCanvasW = Math.max(1, finiteNumber(canvasW, nextX + nextW));
    var maxCanvasH = Math.max(1, finiteNumber(canvasH, nextY + nextH));
    nextX = clamp(nextX, 0, Math.max(0, maxCanvasW - nextW));
    nextY = clamp(nextY, 0, Math.max(0, maxCanvasH - nextH));

    return {
      x: nextX,
      y: nextY,
      w: nextW,
      h: nextH,
      coverage: bounds.coverage,
      fallback: bounds.fallback || "physical-aspect",
      physicalAspectAdjusted: true,
      physicalAspectRatio: desiredRatio,
      sourceAspectRatio: sourceRatio,
      widthCm: selectedW,
      lengthCm: selectedL
    };
  }

  function regionPx(body, nx, ny) {
    return {
      x: body.x + finiteNumber(nx, 0) * body.w,
      y: body.y + finiteNumber(ny, 0) * body.h
    };
  }

  function drawRegionRoundRect(ctx, x, y, w, h, r, corners) {
    corners = corners || ["top-left", "top-right", "bottom-right", "bottom-left"];
    function has(name) { return corners.indexOf(name) >= 0; }
    r = Math.max(0, Math.min(r, w / 2, h / 2));
    var tl = has("top-left") ? r : 0;
    var tr = has("top-right") ? r : 0;
    var br = has("bottom-right") ? r : 0;
    var bl = has("bottom-left") ? r : 0;
    ctx.moveTo(x + tl, y);
    ctx.lineTo(x + w - tr, y);
    if (tr) ctx.quadraticCurveTo(x + w, y, x + w, y + tr);
    ctx.lineTo(x + w, y + h - br);
    if (br) ctx.quadraticCurveTo(x + w, y + h, x + w - br, y + h);
    ctx.lineTo(x + bl, y + h);
    if (bl) ctx.quadraticCurveTo(x, y + h, x, y + h - bl);
    ctx.lineTo(x, y + tl);
    if (tl) ctx.quadraticCurveTo(x, y, x + tl, y);
  }

  function cornerRadiusFromMode(mode, w, h) {
    if (mode === "oval") return Math.min(w, h) * 0.28;
    if (mode === "round") return Math.min(w, h) * 0.12;
    return 0;
  }

  function edgeControlOffset(mode, size) {
    if (mode === "curveIn" || mode === "curveOut") return Math.max(8, size * 0.18);
    return 0;
  }

  function traceRegionBox(ctx, x, y, w, h, cornerModes, edgeModes) {
    cornerModes = Object.assign({
      topLeft: "square",
      topRight: "square",
      bottomRight: "square",
      bottomLeft: "square"
    }, cornerModes || {});
    edgeModes = Object.assign({
      top: "straight",
      right: "straight",
      bottom: "straight",
      left: "straight"
    }, edgeModes || {});
    var rtl = cornerRadiusFromMode(cornerModes.topLeft, w, h);
    var rtr = cornerRadiusFromMode(cornerModes.topRight, w, h);
    var rbr = cornerRadiusFromMode(cornerModes.bottomRight, w, h);
    var rbl = cornerRadiusFromMode(cornerModes.bottomLeft, w, h);
    var topOffset = edgeControlOffset(edgeModes.top, h) * (edgeModes.top === "curveOut" ? -1 : 1);
    var rightOffset = edgeControlOffset(edgeModes.right, w) * (edgeModes.right === "curveOut" ? 1 : -1);
    var bottomOffset = edgeControlOffset(edgeModes.bottom, h) * (edgeModes.bottom === "curveOut" ? 1 : -1);
    var leftOffset = edgeControlOffset(edgeModes.left, w) * (edgeModes.left === "curveOut" ? -1 : 1);

    ctx.moveTo(x + rtl, y);
    if (edgeModes.top === "straight") ctx.lineTo(x + w - rtr, y);
    else ctx.quadraticCurveTo(x + w / 2, y + topOffset, x + w - rtr, y);
    if (rtr) ctx.quadraticCurveTo(x + w, y, x + w, y + rtr);
    if (edgeModes.right === "straight") ctx.lineTo(x + w, y + h - rbr);
    else ctx.quadraticCurveTo(x + w + rightOffset, y + h / 2, x + w, y + h - rbr);
    if (rbr) ctx.quadraticCurveTo(x + w, y + h, x + w - rbr, y + h);
    if (edgeModes.bottom === "straight") ctx.lineTo(x + rbl, y + h);
    else ctx.quadraticCurveTo(x + w / 2, y + h + bottomOffset, x + rbl, y + h);
    if (rbl) ctx.quadraticCurveTo(x, y + h, x, y + h - rbl);
    if (edgeModes.left === "straight") ctx.lineTo(x, y + rtl);
    else ctx.quadraticCurveTo(x + leftOffset, y + h / 2, x, y + rtl);
    if (rtl) ctx.quadraticCurveTo(x, y, x + rtl, y);
  }

  function designRegionRect(region, body, design) {
    var widthCm = Math.max(1, finiteNumber(design && design.widthCm, 160));
    var lengthCm = Math.max(1, finiteNumber(design && design.lengthCm, 230));
    if (region && region.xCm != null && region.widthCm != null) {
      return {
        x: body.x + finiteNumber(region.xCm, 0) / widthCm * body.w,
        y: body.y + finiteNumber(region.yCm, 0) / lengthCm * body.h,
        w: finiteNumber(region.widthCm, widthCm) / widthCm * body.w,
        h: finiteNumber(region.heightCm, lengthCm) / lengthCm * body.h
      };
    }
    var path = (region && region.path) || {};
    return {
      x: body.x + finiteNumber(path.x, 0) * body.w,
      y: body.y + finiteNumber(path.y, 0) * body.h,
      w: finiteNumber(path.w, 1) * body.w,
      h: finiteNumber(path.h, 1) * body.h
    };
  }

  function cmBounds(region, design) {
    var widthCm = Math.max(1, finiteNumber(design && design.widthCm, 160));
    var lengthCm = Math.max(1, finiteNumber(design && design.lengthCm, 230));
    var xCm = finiteNumber(region && region.xCm, 0);
    var yCm = finiteNumber(region && region.yCm, 0);
    var wCm = finiteNumber(region && region.widthCm, widthCm);
    var hCm = finiteNumber(region && region.heightCm, lengthCm);
    return {
      x: xCm,
      y: yCm,
      w: wCm,
      h: hCm,
      left: xCm <= 0.05,
      top: yCm <= 0.05,
      right: xCm + wCm >= widthCm - 0.05,
      bottom: yCm + hCm >= lengthCm - 0.05
    };
  }

  function topologyEnabled(design) {
    return !!(design && (design.topology === "partition-no-hole-v1" || design.topology === "production-template-v1"));
  }

  function topologyRegionModes(region, design) {
    var cm = cmBounds(region, design);
    var corners = Object.assign({
      topLeft: "square",
      topRight: "square",
      bottomRight: "square",
      bottomLeft: "square"
    }, (region && region.cornerModes) || {});
    var edges = Object.assign({
      top: "straight",
      right: "straight",
      bottom: "straight",
      left: "straight"
    }, (region && region.edgeModes) || {});

    return {
      cornerModes: {
        topLeft: cm.left && cm.top ? corners.topLeft : "square",
        topRight: cm.right && cm.top ? corners.topRight : "square",
        bottomRight: cm.right && cm.bottom ? corners.bottomRight : "square",
        bottomLeft: cm.left && cm.bottom ? corners.bottomLeft : "square"
      },
      edgeModes: {
        top: cm.top ? edges.top : "straight",
        right: cm.right ? edges.right : "straight",
        bottom: cm.bottom ? edges.bottom : "straight",
        left: cm.left ? edges.left : "straight"
      }
    };
  }

  function expandTopologyRect(rect, region, design) {
    var cm = cmBounds(region, design);
    var overlap = 1.25;
    var out = {
      x: rect.x,
      y: rect.y,
      w: rect.w,
      h: rect.h
    };
    if (!cm.left) {
      out.x -= overlap;
      out.w += overlap;
    }
    if (!cm.top) {
      out.y -= overlap;
      out.h += overlap;
    }
    if (!cm.right) out.w += overlap;
    if (!cm.bottom) out.h += overlap;
    return out;
  }

  function traceCommandPath(ctx, commands, body) {
    (commands || []).forEach(function (cmd) {
      var type = cmd[0];
      if (type === "M") {
        var m = regionPx(body, cmd[1], cmd[2]);
        ctx.moveTo(m.x, m.y);
      } else if (type === "L") {
        var l = regionPx(body, cmd[1], cmd[2]);
        ctx.lineTo(l.x, l.y);
      } else if (type === "Q") {
        var c = regionPx(body, cmd[1], cmd[2]);
        var e = regionPx(body, cmd[3], cmd[4]);
        ctx.quadraticCurveTo(c.x, c.y, e.x, e.y);
      } else if (type === "C") {
        var c1 = regionPx(body, cmd[1], cmd[2]);
        var c2 = regionPx(body, cmd[3], cmd[4]);
        var ce = regionPx(body, cmd[5], cmd[6]);
        ctx.bezierCurveTo(c1.x, c1.y, c2.x, c2.y, ce.x, ce.y);
      } else if (type === "Z") {
        ctx.closePath();
      }
    });
  }

  function traceDesignRegionPath(ctx, region, body, design) {
    var path = (region && region.path) || {};
    var rect = designRegionRect(region, body, design);
    var x = rect.x;
    var y = rect.y;
    var w = rect.w;
    var h = rect.h;
    ctx.beginPath();
    if (path.type === "commands" && Array.isArray(path.commands) && path.commands.length) {
      traceCommandPath(ctx, path.commands, body);
      ctx.closePath();
      return;
    }
    if (path.type === "polygon" && Array.isArray(path.points) && path.points.length) {
      path.points.forEach(function (pt, idx) {
        var p = regionPx(body, pt[0], pt[1]);
        if (idx === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      });
      ctx.closePath();
      return;
    }
    if (topologyEnabled(design) && region && region.xCm != null && region.widthCm != null) {
      var topologyRect = expandTopologyRect(rect, region, design);
      var modes = topologyRegionModes(region, design);
      traceRegionBox(ctx, topologyRect.x, topologyRect.y, topologyRect.w, topologyRect.h, modes.cornerModes, modes.edgeModes);
      ctx.closePath();
      return;
    }
    if (region && (region.cornerModes || region.edgeModes || path.type === "cmBox")) {
      traceRegionBox(ctx, x, y, w, h, region.cornerModes, region.edgeModes);
      ctx.closePath();
      return;
    }
    if (path.type === "roundRect") {
      drawRegionRoundRect(ctx, x, y, w, h, Math.min(w, h) * finiteNumber(path.r, 0.18), path.corners);
      ctx.closePath();
      return;
    }
    ctx.rect(x, y, w, h);
    ctx.closePath();
  }

  function designRegionAnalysis(region, body, canvasW, canvasH, design, clipAnalysis) {
    var maskCanvas = createCanvas(canvasW, canvasH);
    var mctx = maskCanvas.getContext("2d", { willReadFrequently: true });
    mctx.fillStyle = "#000";
    traceDesignRegionPath(mctx, region, body, design);
    mctx.fill();
    var img = mctx.getImageData(0, 0, canvasW, canvasH);
    var data = img.data;
    var mask = new Uint8Array(canvasW * canvasH);
    var minX = canvasW;
    var minY = canvasH;
    var maxX = 0;
    var maxY = 0;
    var count = 0;
    for (var y = 0; y < canvasH; y++) {
      for (var x = 0; x < canvasW; x++) {
        var i = y * canvasW + x;
        if (data[i * 4 + 3] < 16) continue;
        if (clipAnalysis && clipAnalysis.mask && !clipAnalysis.mask[i]) continue;
        mask[i] = 1;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
        count++;
      }
    }
    if (!count) {
      return {
        x: body.x,
        y: body.y,
        w: body.w,
        h: body.h,
        mask: mask,
        maskWidth: canvasW,
        maskHeight: canvasH,
        _canvasWidth: canvasW,
        _canvasHeight: canvasH
      };
    }
    return {
      x: minX,
      y: minY,
      w: Math.max(1, maxX - minX + 1),
      h: Math.max(1, maxY - minY + 1),
      coverage: count / Math.max(1, canvasW * canvasH),
      fallback: "design-region",
      mask: mask,
      maskWidth: canvasW,
      maskHeight: canvasH,
      _canvasWidth: canvasW,
      _canvasHeight: canvasH
    };
  }

  function analysisFromMaskCanvas(maskCanvas, fallback, fallbackName) {
    var canvasW = maskCanvas && maskCanvas.width || 1;
    var canvasH = maskCanvas && maskCanvas.height || 1;
    var mctx = maskCanvas.getContext("2d", { willReadFrequently: true });
    var img = mctx.getImageData(0, 0, canvasW, canvasH);
    var data = img.data;
    var mask = new Uint8Array(canvasW * canvasH);
    var minX = canvasW;
    var minY = canvasH;
    var maxX = 0;
    var maxY = 0;
    var count = 0;
    for (var y = 0; y < canvasH; y++) {
      for (var x = 0; x < canvasW; x++) {
        var i = y * canvasW + x;
        if (data[i * 4 + 3] < 16) continue;
        mask[i] = 1;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
        count++;
      }
    }
    if (!count) {
      return {
        x: fallback && fallback.x || 0,
        y: fallback && fallback.y || 0,
        w: fallback && fallback.w || 1,
        h: fallback && fallback.h || 1,
        coverage: 0,
        fallback: fallbackName || "empty-visible-region",
        mask: mask,
        maskWidth: canvasW,
        maskHeight: canvasH,
        _canvasWidth: canvasW,
        _canvasHeight: canvasH
      };
    }
    return {
      x: minX,
      y: minY,
      w: Math.max(1, maxX - minX + 1),
      h: Math.max(1, maxY - minY + 1),
      coverage: count / Math.max(1, canvasW * canvasH),
      fallback: fallbackName || "visible-region",
      mask: mask,
      maskWidth: canvasW,
      maskHeight: canvasH,
      _canvasWidth: canvasW,
      _canvasHeight: canvasH
    };
  }

  function designRegionPaintItems(design) {
    var regions = design && design.regions || [];
    return regions.map(function (region, index) {
      return {
        region: region,
        index: index,
        z: finiteNumber(region && region.zIndex, index)
      };
    }).sort(function (a, b) {
      if (a.z !== b.z) return a.z - b.z;
      return a.index - b.index;
    });
  }

  function cutoutVisibleRegionAnalysis(baseAnalysis, paintItems, paintIndex, body, canvasW, canvasH, design, clipAnalysis) {
    if (!baseAnalysis || !baseAnalysis.mask) return baseAnalysis;
    var maskCanvas = createCanvas(canvasW, canvasH);
    var mctx = maskCanvas.getContext("2d", { willReadFrequently: true });
    var img = mctx.createImageData(canvasW, canvasH);
    var data = img.data;
    for (var i = 0; i < baseAnalysis.mask.length; i++) {
      if (baseAnalysis.mask[i]) data[i * 4 + 3] = 255;
    }
    mctx.putImageData(img, 0, 0);

    var minSide = Math.min(finiteNumber(body.w, 0), finiteNumber(body.h, 0));
    var coverStroke = Math.max(2.5, Math.min(11, minSide * 0.016));
    mctx.globalCompositeOperation = "destination-out";
    mctx.fillStyle = "#000";
    mctx.strokeStyle = "#000";
    mctx.lineJoin = "round";
    mctx.lineCap = "round";
    mctx.lineWidth = coverStroke;
    for (var j = paintIndex + 1; j < paintItems.length; j++) {
      mctx.beginPath();
      traceDesignRegionPath(mctx, paintItems[j].region, body, design);
      mctx.fill();
      mctx.stroke();
    }
    mctx.globalCompositeOperation = "source-over";
    if (clipAnalysis && clipAnalysis.mask) {
      drawAnalysisMask(mctx, clipAnalysis, "destination-in");
    }
    return analysisFromMaskCanvas(maskCanvas, baseAnalysis, "cutout-visible-region");
  }

  function drawDesignSeamsMasked(ctx, design, body, clipAnalysis) {
    if (design && design.stitchesVisible === false) return;
    if (design && design.cutoutOutsideRegions) {
      drawCutoutVisibleSeams(ctx, design, body, clipAnalysis);
      return;
    }
    if (!clipAnalysis || !clipAnalysis.mask) {
      drawDesignSeams(ctx, design, body);
      return;
    }
    var layer = createCanvas(clipAnalysis.maskWidth, clipAnalysis.maskHeight);
    var lctx = layer.getContext("2d", { willReadFrequently: true });
    drawDesignSeams(lctx, design, body);
    drawAnalysisMask(lctx, clipAnalysis, "destination-in");
    ctx.drawImage(layer, 0, 0);
  }

  function drawCutoutVisibleSeams(ctx, design, body, clipAnalysis) {
    if (!ctx || !ctx.canvas || !design || design.stitchesVisible === false) return;
    var paintItems = designRegionPaintItems(design);
    if (!paintItems.length) return;
    var canvasW = ctx.canvas.width;
    var canvasH = ctx.canvas.height;
    var minSide = Math.min(finiteNumber(body.w, 0), finiteNumber(body.h, 0));
    var coverStroke = Math.max(2.5, Math.min(10, minSide * 0.014));

    function buildVisibleMask(paintIndex) {
      var mask = createCanvas(canvasW, canvasH);
      var mctx = mask.getContext("2d", { willReadFrequently: true });
      mctx.fillStyle = "#000";
      mctx.strokeStyle = "#000";
      mctx.lineJoin = "round";
      mctx.lineCap = "round";
      mctx.beginPath();
      traceDesignRegionPath(mctx, paintItems[paintIndex].region, body, design);
      mctx.fill();

      mctx.globalCompositeOperation = "destination-out";
      mctx.lineWidth = coverStroke;
      for (var j = paintIndex + 1; j < paintItems.length; j++) {
        mctx.beginPath();
        traceDesignRegionPath(mctx, paintItems[j].region, body, design);
        mctx.fill();
        mctx.stroke();
      }
      mctx.globalCompositeOperation = "source-over";
      if (clipAnalysis && clipAnalysis.mask) {
        drawAnalysisMask(mctx, clipAnalysis, "destination-in");
      }
      return mask;
    }

    function drawPass(strokeStyle, lineWidth, dash, alpha) {
      paintItems.forEach(function (item, idx) {
        var layer = createCanvas(canvasW, canvasH);
        var lctx = layer.getContext("2d", { willReadFrequently: true });
        lctx.save();
        lctx.lineWidth = lineWidth;
        lctx.lineJoin = "round";
        lctx.lineCap = "round";
        lctx.strokeStyle = strokeStyle;
        if (dash && dash.length) lctx.setLineDash(dash);
        lctx.beginPath();
        traceDesignRegionPath(lctx, item.region, body, design);
        lctx.stroke();
        lctx.restore();
        lctx.globalCompositeOperation = "destination-in";
        lctx.drawImage(buildVisibleMask(idx), 0, 0);
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.drawImage(layer, 0, 0);
        ctx.restore();
      });
    }

    var darkWidth = Math.max(1, Math.round(minSide * 0.002));
    drawPass("rgba(42,35,29,0.30)", darkWidth, null, 1);
    drawPass("rgba(255,255,255,0.38)", Math.max(0.85, darkWidth * 0.85), [5, 8], 0.58);
  }

  function drawDesignSeams(ctx, design, body) {
    if (topologyEnabled(design)) {
      drawTopologySeams(ctx, design, body);
      return;
    }
    ctx.save();
    ctx.lineWidth = Math.max(1, Math.round(Math.min(body.w, body.h) * 0.002));
    ctx.strokeStyle = "rgba(42,35,29,0.34)";
    (design.regions || []).forEach(function (region) {
      traceDesignRegionPath(ctx, region, body, design);
      ctx.stroke();
    });
    ctx.globalAlpha = 0.62;
    ctx.setLineDash([5, 8]);
    ctx.strokeStyle = "rgba(255,255,255,0.42)";
    (design.regions || []).forEach(function (region) {
      traceDesignRegionPath(ctx, region, body, design);
      ctx.stroke();
    });
    ctx.restore();
  }

  function sharedRange(aStart, aEnd, bStart, bEnd) {
    return {
      start: Math.max(aStart, bStart),
      end: Math.min(aEnd, bEnd)
    };
  }

  function drawTopologySeams(ctx, design, body) {
    var productionSeams = design && design.designTopology && Array.isArray(design.designTopology.seams)
      ? design.designTopology.seams
      : [];
    if (productionSeams.length) {
      ctx.save();
      ctx.lineWidth = Math.max(1, Math.round(Math.min(body.w, body.h) * 0.0018));
      ctx.strokeStyle = "rgba(42,35,29,0.38)";
      ctx.beginPath();
      productionSeams.forEach(function (seam) {
        var path = seam && seam.path;
        if (path && path.type === "commands") traceCommandPath(ctx, path.commands, body);
      });
      ctx.stroke();
      ctx.globalAlpha = 0.5;
      ctx.setLineDash([5, 8]);
      ctx.strokeStyle = "rgba(255,255,255,0.36)";
      ctx.beginPath();
      productionSeams.forEach(function (seam) {
        var path = seam && seam.path;
        if (path && path.type === "commands") traceCommandPath(ctx, path.commands, body);
      });
      ctx.stroke();
      ctx.restore();
      return;
    }
    var regions = design.regions || [];
    var rects = regions.map(function (region) {
      return { region: region, rect: designRegionRect(region, body, design) };
    });
    var seen = {};
    ctx.save();
    ctx.lineWidth = Math.max(1, Math.round(Math.min(body.w, body.h) * 0.0018));
    ctx.strokeStyle = "rgba(42,35,29,0.36)";
    ctx.beginPath();
    rects.forEach(function (a, ai) {
      rects.forEach(function (b, bi) {
        if (bi <= ai) return;
        var ar = a.rect;
        var br = b.rect;
        var aright = ar.x + ar.w;
        var bright = br.x + br.w;
        var abottom = ar.y + ar.h;
        var bbottom = br.y + br.h;
        var eps = 1.5;
        if (Math.abs(aright - br.x) <= eps || Math.abs(bright - ar.x) <= eps) {
          var x = Math.abs(aright - br.x) <= eps ? (aright + br.x) / 2 : (bright + ar.x) / 2;
          var yr = sharedRange(ar.y, abottom, br.y, bbottom);
          if (yr.end - yr.start > eps) {
            var vkey = "v:" + Math.round(x) + ":" + Math.round(yr.start) + ":" + Math.round(yr.end);
            if (!seen[vkey]) {
              seen[vkey] = true;
              ctx.moveTo(x, yr.start);
              ctx.lineTo(x, yr.end);
            }
          }
        }
        if (Math.abs(abottom - br.y) <= eps || Math.abs(bbottom - ar.y) <= eps) {
          var y = Math.abs(abottom - br.y) <= eps ? (abottom + br.y) / 2 : (bbottom + ar.y) / 2;
          var xr = sharedRange(ar.x, aright, br.x, bright);
          if (xr.end - xr.start > eps) {
            var hkey = "h:" + Math.round(y) + ":" + Math.round(xr.start) + ":" + Math.round(xr.end);
            if (!seen[hkey]) {
              seen[hkey] = true;
              ctx.moveTo(xr.start, y);
              ctx.lineTo(xr.end, y);
            }
          }
        }
      });
    });
    ctx.stroke();
    ctx.globalAlpha = 0.55;
    ctx.setLineDash([5, 8]);
    ctx.strokeStyle = "rgba(255,255,255,0.38)";
    ctx.stroke();
    ctx.restore();
  }

  function designCoverageStats(analyses, body, canvasW, canvasH, coverageAnalysis, design) {
    var topologyName = design && design.premium ? "production-template-v1" : (design && design.topology ? design.topology : "partition-no-hole-v1");
    var union = new Uint8Array(canvasW * canvasH);
    var counts = new Uint8Array(canvasW * canvasH);
    analyses.forEach(function (analysis) {
      var mask = analysis && analysis.mask;
      if (!mask) return;
      for (var i = 0; i < mask.length; i++) {
        if (!mask[i]) continue;
        union[i] = 1;
        if (counts[i] < 255) counts[i]++;
      }
    });
    if (coverageAnalysis && coverageAnalysis.mask) {
      var shapeTotal = 0;
      var shapeCovered = 0;
      var shapeOverlap = 0;
      for (var si = 0; si < coverageAnalysis.mask.length; si++) {
        if (!coverageAnalysis.mask[si]) continue;
        shapeTotal++;
        if (union[si]) shapeCovered++;
        if (counts[si] > 1) shapeOverlap++;
      }
      return {
        bodyPixels: shapeTotal,
        coveredPixels: shapeCovered,
        gapPixels: Math.max(0, shapeTotal - shapeCovered),
        overlapPixels: shapeOverlap,
        outerCornerGapPixels: 0,
        rawGapPixels: Math.max(0, shapeTotal - shapeCovered),
        coverageRatio: shapeTotal ? shapeCovered / shapeTotal : 1,
        topology: topologyName,
        clippedToShape: true
      };
    }
    var x0 = clamp(Math.floor(body.x) + 2, 0, canvasW - 1);
    var y0 = clamp(Math.floor(body.y) + 2, 0, canvasH - 1);
    var x1 = clamp(Math.ceil(body.x + body.w) - 2, 0, canvasW);
    var y1 = clamp(Math.ceil(body.y + body.h) - 2, 0, canvasH);
    var total = 0;
    var covered = 0;
    var overlap = 0;
    var internalGaps = 0;
    var outerCornerGaps = 0;
    var cornerZone = Math.max(24, Math.min(body.w, body.h) * 0.18);
    for (var y = y0; y < y1; y++) {
      for (var x = x0; x < x1; x++) {
        total++;
        if (union[y * canvasW + x]) {
          covered++;
          if (counts[y * canvasW + x] > 1) overlap++;
        } else {
          var nearHorizontalOuter = x < body.x + cornerZone || x > body.x + body.w - cornerZone;
          var nearVerticalOuter = y < body.y + cornerZone || y > body.y + body.h - cornerZone;
          if (nearHorizontalOuter && nearVerticalOuter) outerCornerGaps++;
          else internalGaps++;
        }
      }
    }
    return {
      bodyPixels: total,
      coveredPixels: covered,
      gapPixels: internalGaps,
      overlapPixels: overlap,
      outerCornerGapPixels: outerCornerGaps,
      rawGapPixels: Math.max(0, total - covered),
      coverageRatio: total ? (total - internalGaps) / total : 1,
      topology: topologyName
    };
  }

  function designGapAnalysis(analyses, coverageAnalysis, canvasW, canvasH) {
    if (!coverageAnalysis || !coverageAnalysis.mask) return null;
    var union = new Uint8Array(canvasW * canvasH);
    analyses.forEach(function (analysis) {
      var mask = analysis && analysis.mask;
      if (!mask) return;
      for (var i = 0; i < mask.length; i++) {
        if (mask[i]) union[i] = 1;
      }
    });

    var gapMask = new Uint8Array(canvasW * canvasH);
    var minX = canvasW;
    var minY = canvasH;
    var maxX = 0;
    var maxY = 0;
    var count = 0;
    for (var y = 0; y < canvasH; y++) {
      for (var x = 0; x < canvasW; x++) {
        var idx = y * canvasW + x;
        if (!coverageAnalysis.mask[idx] || union[idx]) continue;
        gapMask[idx] = 1;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
        count++;
      }
    }
    if (!count) return null;
    return {
      x: minX,
      y: minY,
      w: Math.max(1, maxX - minX + 1),
      h: Math.max(1, maxY - minY + 1),
      coverage: count / Math.max(1, canvasW * canvasH),
      fallback: "design-gap-guard",
      mask: gapMask,
      maskWidth: canvasW,
      maskHeight: canvasH,
      _canvasWidth: canvasW,
      _canvasHeight: canvasH,
      _gapPixels: count
    };
  }

  function drawCutoutPieceEdges(ctx, design, body, pieceEdge, clipAnalysis) {
    if (!ctx || !design || !design.cutoutOutsideRegions) return;
    var regions = design.regions || [];
    if (!regions.length) return;
    var minSide = Math.min(finiteNumber(body.w, 0), finiteNumber(body.h, 0));
    if (minSide < 8) return;
    pieceEdge = pieceEdge || {};
    var edgeMode = String(pieceEdge.edgeType || design.pieceEdgeMode || "").toLowerCase();
    var edgeColor = pieceEdge.fabric ? swatchColor(pieceEdge.fabric) : "";
    var edgeOptions = pieceEdge.options || {};
    var widthCm = Math.max(1, finiteNumber(design.widthCm, body.w || 160));
    var lengthCm = Math.max(1, finiteNumber(design.lengthCm, body.h || 230));
    var pxPerCm = ((finiteNumber(body.w, 0) / widthCm) + (finiteNumber(body.h, 0) / lengthCm)) / 2;
    var normalBiyeDepth = pxPerCm > 0
      ? Math.max(5, Math.round(pxPerCm * EDGE_CM_DEFAULTS.biye))
      : Math.max(5, Math.round(biyeVisualDepth(body, edgeOptions)));
    var outerLine = edgeMode === "overlok"
      ? Math.max(1.6, Math.min(4.5, minSide * 0.006))
      : edgeMode === "biye"
        ? Math.max(5, Math.min(Math.max(5, minSide * 0.08), normalBiyeDepth))
        : Math.max(4.5, Math.min(10, minSide * 0.015));
    if (edgeMode === "biye") {
      try {
        window.__MASSIMO_DESIGN_BIYE_DEPTH_FIX__ = { build: BUILD, pxPerCm: pxPerCm, normalBiyeDepth: normalBiyeDepth, outerLine: outerLine };
      } catch (e) {}
    }
    var canvasW = ctx.canvas && ctx.canvas.width || Math.ceil(body.x + body.w);
    var canvasH = ctx.canvas && ctx.canvas.height || Math.ceil(body.y + body.h);
    var unionMask = createCanvas(canvasW, canvasH);
    var uctx = unionMask.getContext("2d", { willReadFrequently: true });
    uctx.fillStyle = "#000";
    uctx.strokeStyle = "#000";
    uctx.lineJoin = "round";
    uctx.lineCap = "round";
    regions.forEach(function (region) {
      uctx.beginPath();
      traceDesignRegionPath(uctx, region, body, design);
      uctx.fill();
    });
    uctx.lineWidth = Math.max(1.2, Math.min(outerLine * 0.26, minSide * 0.0045));
    regions.forEach(function (region) {
      uctx.beginPath();
      traceDesignRegionPath(uctx, region, body, design);
      uctx.stroke();
    });
    if (clipAnalysis && clipAnalysis.mask) {
      drawAnalysisMask(uctx, clipAnalysis, "destination-in");
    }
    var ring = createCanvas(canvasW, canvasH);
    var rctx = ring.getContext("2d", { willReadFrequently: true });
    rctx.strokeStyle = "#000";
    rctx.lineJoin = "round";
    rctx.lineCap = "round";
    rctx.lineWidth = Math.max(1.5, outerLine);
    regions.forEach(function (region) {
      rctx.beginPath();
      traceDesignRegionPath(rctx, region, body, design);
      rctx.stroke();
    });
    if (clipAnalysis && clipAnalysis.mask) {
      drawAnalysisMask(rctx, clipAnalysis, "destination-in");
    }
    rctx.globalCompositeOperation = "destination-in";
    rctx.drawImage(unionMask, 0, 0);
    rctx.globalCompositeOperation = "source-over";
    var paint = createCanvas(canvasW, canvasH);
    var pctx = paint.getContext("2d", { willReadFrequently: true });
    pctx.imageSmoothingEnabled = true;
    pctx.imageSmoothingQuality = "high";
    var texturedEdge = false;
    if (edgeMode !== "overlok" && pieceEdge.fabricImg) {
      texturedEdge = drawBiyeFabricTextureFill(pctx, pieceEdge.fabricImg, body.x, body.y, body.w, body.h, outerLine);
      if (texturedEdge) applyAmorfEdgeFabricFinish(pctx, body.x, body.y, body.w, body.h, outerLine, 1.02);
    }
    if (!texturedEdge) {
      pctx.fillStyle = edgeMode === "overlok" ? "rgba(43,35,28,0.62)" : (edgeColor || "rgba(132,103,74,0.90)");
      pctx.fillRect(0, 0, canvasW, canvasH);
    }
    pctx.globalCompositeOperation = "destination-in";
    pctx.drawImage(ring, 0, 0);
    ctx.save();
    ctx.globalAlpha = edgeMode === "overlok" ? 0.78 : 1;
    ctx.drawImage(paint, 0, 0);
    if (edgeMode !== "overlok" && !texturedEdge) {
      var soft = createCanvas(canvasW, canvasH);
      var sctx = soft.getContext("2d", { willReadFrequently: true });
      sctx.fillStyle = "rgba(255,250,238,0.24)";
      sctx.fillRect(0, 0, canvasW, canvasH);
      sctx.globalCompositeOperation = "destination-in";
      sctx.drawImage(ring, 0, 0);
      ctx.globalAlpha = 0.5;
      ctx.drawImage(soft, 0, 0);
    }
    ctx.restore();
    if (edgeMode === "biye") {
      var accent = createCanvas(canvasW, canvasH);
      var actx = accent.getContext("2d", { willReadFrequently: true });
      actx.strokeStyle = edgeColor || "rgba(72,58,47,0.78)";
      actx.lineJoin = "round";
      actx.lineCap = "round";
      actx.lineWidth = Math.max(1.4, Math.min(6.5, outerLine * 0.14));
      regions.forEach(function (region) {
        actx.beginPath();
        traceDesignRegionPath(actx, region, body, design);
        actx.stroke();
      });
      if (clipAnalysis && clipAnalysis.mask) {
        drawAnalysisMask(actx, clipAnalysis, "destination-in");
      }
      actx.globalCompositeOperation = "destination-in";
      actx.drawImage(unionMask, 0, 0);
      actx.globalCompositeOperation = "source-over";
      ctx.save();
      ctx.globalAlpha = 0.72;
      ctx.drawImage(accent, 0, 0);
      ctx.restore();
    }
    ctx.save();
    ctx.globalCompositeOperation = "destination-in";
    ctx.drawImage(unionMask, 0, 0);
    ctx.restore();
  }

  function cleanDesignRegionTextureOptions(options) {
    var sourceInset = Math.max(0.3, finiteNumber(options && options.textureSourceInsetPct, 0));
    return Object.assign({}, options || {}, {
      __massimoCleanDesignRegionTexture: true,
      __massimoArCleanTexture: true,
      renderPipeline: "",
      bodyTextureSurfaceMode: "repeat",
      textureSourceInsetPct: sourceInset,
      baseToneStrength: 0,
      bodyDetailStrength: 0,
      textureShadingStrength: 0
    });
  }

  function cleanDesignRegionTransferOptions(options) {
    var sourceInset = Math.max(0.3, finiteNumber(options && options.textureSourceInsetPct, 0));
    return Object.assign({}, options || {}, {
      __massimoSmoothDesignRegionTexture: true,
      __massimoArCleanTexture: true,
      renderPipeline: "",
      bodyTextureSurfaceMode: "repeat",
      textureSourceInsetPct: sourceInset,
      bodyTextureRepeatX: 18,
      bodyTextureRepeatY: 26,
      bodyTextureMinTileSize: 42,
      baseToneStrength: 0,
      bodyDetailStrength: 0,
      textureDetailStrength: 0.035,
      textureChromaDetailStrength: 0.025,
      textureContrastRatio: 0.16,
      maxTextureContrastRatio: 0.24,
      textureShadingStrength: 0,
      smoothRegionMicroStrength: 0.08,
      smoothRegionSoftStrength: 0.018,
      smoothRegionChromaStrength: 0.04
    });
  }

  function drawCleanDesignRegionSurface(ctx, bounds, patternImg, options) {
    if (!ctx || !bounds || !patternImg) return false;
    var canvasW = ctx.canvas.width;
    var canvasH = ctx.canvas.height;
    var x = finiteNumber(bounds.x, 0);
    var y = finiteNumber(bounds.y, 0);
    var w = finiteNumber(bounds.w, 0);
    var h = finiteNumber(bounds.h, 0);
    if (w < 1 || h < 1) return false;
    var sourceInset = clamp(Math.max(0.3, finiteNumber(options && options.textureSourceInsetPct, 0)), 0, TEXTURE_SOURCE_MAX_INSET);
    var repeatX = clamp(finiteNumber(Object.prototype.hasOwnProperty.call(options || {}, "bodyTextureRepeatX") ? options.bodyTextureRepeatX : options && options.textureRepeatX, DEFAULTS.textureRepeatX), 1.2, 96);
    var repeatY = clamp(finiteNumber(Object.prototype.hasOwnProperty.call(options || {}, "bodyTextureRepeatY") ? options.bodyTextureRepeatY : options && options.textureRepeatY, DEFAULTS.textureRepeatY), 1.2, 140);
    var minTileSize = clamp(finiteNumber(Object.prototype.hasOwnProperty.call(options || {}, "bodyTextureMinTileSize") ? options.bodyTextureMinTileSize : options && options.textureMinTileSize, 36), 12, 160);
    var layer = createCanvas(canvasW, canvasH);
    var lctx = layer.getContext("2d", { willReadFrequently: true });
    lctx.imageSmoothingEnabled = true;
    lctx.imageSmoothingQuality = "high";
    var painted = false;
    try {
      painted = drawMirroredTextureSurfaceFromSourceInset(lctx, patternImg, x, y, w, h, sourceInset, repeatX, repeatY, { minTileSize: minTileSize })
        || drawImageCoverFromSourceInset(lctx, patternImg, sourceInset, x, y, w, h);
    } catch (err) {
      painted = false;
    }
    if (!painted) return false;
    if (bounds.mask) drawMaskToLayer(lctx, bounds, 0);
    ctx.save();
    ctx.drawImage(layer, 0, 0);
    ctx.restore();
    ctx.canvas.__massimoTextureDebug = {
      mode: "clean-design-region-surface",
      build: BUILD,
      sourceInsetPct: sourceInset,
      repeatX: repeatX,
      repeatY: repeatY,
      minTileSize: minTileSize,
      cleanDesignRegionTexture: true,
      arCleanTexture: true,
      bodyDetailStrength: 0,
      sourceEdgeGuard: 0
    };
    return true;
  }

  function drawSmoothDesignRegionSurface(ctx, bounds, patternImg, options) {
    if (!ctx || !bounds || !patternImg) return false;
    var canvasW = ctx.canvas.width;
    var canvasH = ctx.canvas.height;
    var x = finiteNumber(bounds.x, 0);
    var y = finiteNumber(bounds.y, 0);
    var w = finiteNumber(bounds.w, 0);
    var h = finiteNumber(bounds.h, 0);
    if (w < 1 || h < 1) return false;
    bounds._canvasWidth = canvasW;
    bounds._canvasHeight = canvasH;

    var sourceInset = clamp(Math.max(0.3, finiteNumber(options && options.textureSourceInsetPct, 0)), 0, TEXTURE_SOURCE_MAX_INSET);
    var repeatX = clamp(finiteNumber(options && options.bodyTextureRepeatX, 18), 4, 96);
    var repeatY = clamp(finiteNumber(options && options.bodyTextureRepeatY, 26), 4, 140);
    var minTileSize = clamp(finiteNumber(options && options.bodyTextureMinTileSize, 42), 24, 120);
    var layer = createCanvas(canvasW, canvasH);
    var lctx = layer.getContext("2d", { willReadFrequently: true });
    lctx.imageSmoothingEnabled = true;
    lctx.imageSmoothingQuality = "high";
    var painted = false;
    try {
      painted = drawMirroredTextureSurfaceFromSourceInset(lctx, patternImg, x, y, w, h, sourceInset, repeatX, repeatY, { minTileSize: minTileSize })
        || drawImageCoverFromSourceInset(lctx, patternImg, sourceInset, x, y, w, h);
    } catch (err) {
      painted = false;
    }
    if (!painted) return false;

    var image;
    var lowPass;
    try {
      image = lctx.getImageData(0, 0, canvasW, canvasH);
      lowPass = textureLowPassData(layer, canvasW, canvasH, { textureLowPassRatio: 0.025 }) || image.data;
    } catch (err) {
      return false;
    }

    var data = image.data;
    var minX = Math.max(0, Math.floor(x));
    var minY = Math.max(0, Math.floor(y));
    var maxX = Math.min(canvasW, Math.ceil(x + w));
    var maxY = Math.min(canvasH, Math.ceil(y + h));
    var rSum = 0;
    var gSum = 0;
    var bSum = 0;
    var ySum = 0;
    var y2Sum = 0;
    var cbSum = 0;
    var crSum = 0;
    var count = 0;

    for (var py = minY; py < maxY; py++) {
      for (var px = minX; px < maxX; px++) {
        if (!isBodyPixel(bounds, px, py, 0)) continue;
        var i = (py * canvasW + px) * 4;
        if (data[i + 3] < 8) continue;
        var luma = pixelLuma(data, i);
        var chroma = pixelChroma(data, i, luma);
        rSum += data[i];
        gSum += data[i + 1];
        bSum += data[i + 2];
        ySum += luma;
        y2Sum += luma * luma;
        cbSum += chroma.cb;
        crSum += chroma.cr;
        count++;
      }
    }
    if (!count) return false;

    var avgY = ySum / count;
    var avgCb = cbSum / count;
    var avgCr = crSum / count;
    var sourceStd = Math.sqrt(Math.max(0, y2Sum / count - avgY * avgY));
    var microStrength = clamp(finiteNumber(options && options.smoothRegionMicroStrength, 0.08), 0, 0.18);
    var softStrength = clamp(finiteNumber(options && options.smoothRegionSoftStrength, 0.018), 0, 0.08);
    var chromaStrength = clamp(finiteNumber(options && options.smoothRegionChromaStrength, 0.04), 0, 0.12);
    var outYSum = 0;
    var outY2Sum = 0;

    for (var ty = minY; ty < maxY; ty++) {
      for (var tx = minX; tx < maxX; tx++) {
        var p = (ty * canvasW + tx) * 4;
        if (!isBodyPixel(bounds, tx, ty, 0) || data[p + 3] < 8) {
          data[p + 3] = 0;
          continue;
        }
        var rawY = pixelLuma(data, p);
        var lowY = pixelLuma(lowPass, p);
        var rawChroma = pixelChroma(data, p, rawY);
        var lowChroma = pixelChroma(lowPass, p, lowY);
        var outY = clamp(avgY + (rawY - lowY) * microStrength + (lowY - avgY) * softStrength, 0, 255);
        var outCb = avgCb + (rawChroma.cb - lowChroma.cb) * chromaStrength;
        var outCr = avgCr + (rawChroma.cr - lowChroma.cr) * chromaStrength;
        var rgb = chromaToRgb(outY, outCb, outCr);
        data[p] = rgb.r;
        data[p + 1] = rgb.g;
        data[p + 2] = rgb.b;
        data[p + 3] = 255;
        outYSum += outY;
        outY2Sum += outY * outY;
      }
    }

    try {
      lctx.putImageData(image, 0, 0);
    } catch (err) {
      return false;
    }
    if (bounds.mask) drawMaskToLayer(lctx, bounds, 0);
    ctx.save();
    ctx.drawImage(layer, 0, 0);
    ctx.restore();
    var outAvg = outYSum / count;
    var outStd = Math.sqrt(Math.max(0, outY2Sum / count - outAvg * outAvg));
    ctx.canvas.__massimoTextureDebug = {
      mode: "smooth-design-region-surface",
      build: BUILD,
      sourceInsetPct: sourceInset,
      repeatX: repeatX,
      repeatY: repeatY,
      minTileSize: minTileSize,
      smoothDesignRegionTexture: true,
      cleanDesignRegionTexture: false,
      arCleanTexture: true,
      microStrength: microStrength,
      softStrength: softStrength,
      chromaStrength: chromaStrength,
      sourceAvg: { r: rSum / count, g: gSum / count, b: bSum / count, y: avgY, yStd: sourceStd },
      outputAvg: { y: outAvg, yStd: outStd },
      contrastRatio: outStd / Math.max(1, sourceStd),
      bodyPixels: count,
      sourceEdgeGuard: 0
    };
    return true;
  }

  function drawDesignBody(ctx, design, bounds, edgeDepth, patternImg, designImgs, options, canvasW, canvasH, clipAnalysis, pieceEdge) {
    var body = designBounds(bounds, edgeDepth);
    var fallbackImg = patternImg;
    var analyses = [];
    var paintItems = designRegionPaintItems(design);
    paintItems.forEach(function (item, paintIndex) {
      var region = item.region;
      var assignment = design.assignments && design.assignments[region.id];
      var img = assignment && assignment.image ? designImgs[assignment.image] : null;
      var analysis = designRegionAnalysis(region, body, canvasW, canvasH, design, clipAnalysis);
      if (design.cutoutOutsideRegions) {
        analysis = cutoutVisibleRegionAnalysis(analysis, paintItems, paintIndex, body, canvasW, canvasH, design, clipAnalysis);
      }
      var textureAnalysis = design.cutoutOutsideRegions
        ? Object.assign({}, analysis, { x: body.x, y: body.y, w: body.w, h: body.h, _massimoDesignTextureBodyBounds: true })
        : analysis;
      analyses.push(analysis);
      /* v216: Atama varsa renderTarget override'larını sil — pattern'in gerçek rengi kullanılsın. */
      var regionOptions = options;
      if (img && options && (options.renderTargetR != null || options.renderTargetG != null || options.renderTargetB != null)) {
        regionOptions = Object.assign({}, options);
        regionOptions.renderTargetR = undefined;
        regionOptions.renderTargetG = undefined;
        regionOptions.renderTargetB = undefined;
      }
      if (design.cleanRegionBaseLuma) {
        regionOptions = cleanDesignRegionTransferOptions(regionOptions);
      }
      if (design.cleanRegionTexture) {
        regionOptions = cleanDesignRegionTextureOptions(regionOptions);
      }
      if (design.cleanRegionBaseLuma && drawSmoothDesignRegionSurface(ctx, textureAnalysis, img || fallbackImg, regionOptions)) {
        return;
      }
      if (design.cleanRegionTexture && drawCleanDesignRegionSurface(ctx, textureAnalysis, img || fallbackImg, regionOptions)) {
        return;
      }
      if (!drawTextureTransfer(ctx, textureAnalysis, 0, "overlok", img || fallbackImg, regionOptions)) {
        drawBodyPattern(ctx, textureAnalysis, 0, "overlok", img || fallbackImg, regionOptions);
      }
    });
    var coverage = designCoverageStats(analyses, body, canvasW, canvasH, clipAnalysis, design);
    if (!design.cutoutOutsideRegions && clipAnalysis && coverage && coverage.gapPixels > 0) {
      var gapAnalysis = designGapAnalysis(analyses, clipAnalysis, canvasW, canvasH);
      if (gapAnalysis) {
        if (!drawTextureTransfer(ctx, gapAnalysis, 0, "overlok", fallbackImg, options)) {
          drawBodyPattern(ctx, gapAnalysis, 0, "overlok", fallbackImg, options);
        }
        coverage = Object.assign({}, coverage, {
          gapPixels: 0,
          coveredPixels: coverage.bodyPixels,
          coverageRatio: 1,
          healedGapPixels: gapAnalysis._gapPixels
        });
      }
    }
    if (ctx && ctx.canvas) ctx.canvas.__massimoDesignCoverage = coverage;
    drawCutoutPieceEdges(ctx, design, body, pieceEdge, clipAnalysis);
    drawDesignSeamsMasked(ctx, design, body, clipAnalysis);
    return body;
  }

  function designRegionPixels(design, bounds, edgeDepth) {
    var body = designBounds(bounds, edgeDepth);
    return (design.regions || []).map(function (region) {
      var rect = designRegionRect(region, body, design);
      return {
        id: region.id,
        label: region.label,
        x: Math.round(rect.x),
        y: Math.round(rect.y),
        w: Math.round(rect.w),
        h: Math.round(rect.h),
        xCm: region.xCm,
        yCm: region.yCm,
        widthCm: region.widthCm,
        heightCm: region.heightCm,
        cornerModes: region.cornerModes || {},
        edgeModes: region.edgeModes || {}
      };
    });
  }

  async function loadDesignImages(instance, design) {
    if (!design || !design.active) return;
    instance.designImgs = instance.designImgs || {};
    var assignments = design.assignments || {};
    var images = [];
    Object.keys(assignments).forEach(function (key) {
      var src = assignments[key] && assignments[key].image;
      if (src && images.indexOf(src) < 0 && !instance.designImgs[src]) images.push(src);
    });
    for (var i = 0; i < images.length; i++) {
      try {
        instance.designImgs[images[i]] = await loadImage(images[i]);
      } catch (err) {
        instance.designImgs[images[i]] = null;
      }
    }
  }

  function bordurVisualDepth(depth) {
    var safeDepth = Math.max(0, Math.round(finiteNumber(depth, 0)));
    return safeDepth + 3;
  }

  function colorLuma(color) {
    return color.r * 0.299 + color.g * 0.587 + color.b * 0.114;
  }

  function colorDistance(a, b) {
    var dr = a.r - b.r;
    var dg = a.g - b.g;
    var db = a.b - b.b;
    return Math.sqrt(dr * dr + dg * dg + db * db);
  }

  function averageSourceLine(imageData, bounds, side, offset) {
    if (!imageData || !bounds) return null;
    var data = imageData.data;
    var width = imageData.width;
    var height = imageData.height;
    var x;
    var y;
    var start;
    var end;
    var step;
    var r = 0;
    var g = 0;
    var b = 0;
    var n = 0;

    if (side === "left" || side === "right") {
      x = side === "left" ? bounds.x + offset : bounds.x + bounds.w - 1 - offset;
      x = clamp(Math.round(x), 0, width - 1);
      start = clamp(Math.round(bounds.y + bounds.h * 0.12), 0, height - 1);
      end = clamp(Math.round(bounds.y + bounds.h * 0.88), 0, height - 1);
      step = Math.max(1, Math.round((end - start) / 80));
      for (y = start; y <= end; y += step) {
        var vi = (y * width + x) * 4;
        r += data[vi];
        g += data[vi + 1];
        b += data[vi + 2];
        n++;
      }
    } else {
      y = side === "top" ? bounds.y + offset : bounds.y + bounds.h - 1 - offset;
      y = clamp(Math.round(y), 0, height - 1);
      start = clamp(Math.round(bounds.x + bounds.w * 0.12), 0, width - 1);
      end = clamp(Math.round(bounds.x + bounds.w * 0.88), 0, width - 1);
      step = Math.max(1, Math.round((end - start) / 80));
      for (x = start; x <= end; x += step) {
        var hi = (y * width + x) * 4;
        r += data[hi];
        g += data[hi + 1];
        b += data[hi + 2];
        n++;
      }
    }

    if (!n) return null;
    return { r: r / n, g: g / n, b: b / n };
  }

  function detectSideBorderDepth(imageData, bounds, side, maxProbe) {
    var edge = averageSourceLine(imageData, bounds, side, 2);
    var inner = averageSourceLine(imageData, bounds, side, Math.max(8, Math.round(maxProbe * 0.9)));
    if (!edge || !inner) return 0;

    var edgeY = colorLuma(edge);
    var innerY = colorLuma(inner);
    var lumaDelta = innerY - edgeY;
    var threshold = edgeY + lumaDelta * 0.52;
    var stable = 0;
    var lastCandidate = 0;

    for (var d = 4; d <= maxProbe; d++) {
      var sample = averageSourceLine(imageData, bounds, side, d);
      if (!sample) continue;
      var sampleY = colorLuma(sample);
      var lumaPass = Math.abs(lumaDelta) > 18
        ? (lumaDelta > 0 ? sampleY >= threshold : sampleY <= threshold)
        : false;
      var colorPass = colorDistance(sample, inner) < colorDistance(sample, edge) * 0.72
        && colorDistance(sample, inner) < 48;
      if (lumaPass || colorPass) {
        stable++;
        if (!lastCandidate) lastCandidate = d;
        if (stable >= 3) return lastCandidate;
      } else {
        stable = 0;
        lastCandidate = 0;
      }
    }
    return 0;
  }

  function detectSourceBorderDepth(imageData, bounds) {
    if (!imageData || !bounds) return null;
    var minSide = Math.min(finiteNumber(bounds.w, 0), finiteNumber(bounds.h, 0));
    if (minSide < 40) return null;
    var maxProbe = Math.max(24, Math.min(150, Math.round(minSide * 0.12)));
    var sides = {
      left: detectSideBorderDepth(imageData, bounds, "left", maxProbe),
      right: detectSideBorderDepth(imageData, bounds, "right", maxProbe),
      top: detectSideBorderDepth(imageData, bounds, "top", maxProbe),
      bottom: detectSideBorderDepth(imageData, bounds, "bottom", maxProbe)
    };
    var values = Object.keys(sides).map(function (key) { return sides[key]; }).filter(function (value) {
      return value >= 8 && value <= maxProbe;
    });
    if (!values.length) return null;
    values.sort(function (a, b) { return a - b; });
    var maxValue = values[values.length - 1];
    var cap = Math.max(28, Math.round(minSide * 0.09));
    return {
      depth: Math.min(maxValue, cap),
      sides: sides,
      maxProbe: maxProbe
    };
  }

  function bordurCoverMetrics(bounds, depth, options, sourceImageData) {
    var configDepth = bordurVisualDepth(depth);
    var detected = detectSourceBorderDepth(sourceImageData, bounds);
    var safety = clamp(Math.round(finiteNumber(options && options.bordurCoverSafetyPx, 3)), 0, 8);
    var detectedDepth = detected && detected.depth ? detected.depth + safety : 0;
    return {
      depth: Math.max(configDepth, detectedDepth),
      configDepth: configDepth,
      detectedDepth: detected ? detected.depth : 0,
      safety: safety,
      sides: detected ? detected.sides : null
    };
  }

  function isDoubleBordur(edgeType) {
    return edgeType === "cift_bordur";
  }

  function doubleBordurDepths(depth) {
    var outer = bordurVisualDepth(depth);
    var inner = Math.max(5, Math.round(outer * 0.5));
    return {
      outer: outer,
      inner: inner,
      total: outer + inner
    };
  }

  function insetBounds(bounds, inset) {
    var safeInset = Math.max(0, Math.round(finiteNumber(inset, 0)));
    return {
      x: bounds.x + safeInset,
      y: bounds.y + safeInset,
      w: Math.max(1, bounds.w - safeInset * 2),
      h: Math.max(1, bounds.h - safeInset * 2),
      coverage: bounds.coverage,
      fallback: bounds.fallback,
      mask: bounds.mask,
      maskWidth: bounds.maskWidth,
      maskHeight: bounds.maskHeight,
      _canvasWidth: bounds._canvasWidth,
      _canvasHeight: bounds._canvasHeight
    };
  }

  function biyeVisualDepth(bounds, options) {
    if (isAmorfGeometry(bounds)) {
      var pxPerCm = amorfPxPerCm(bounds);
      if (pxPerCm > 0) {
        return Math.max(5, Math.round(pxPerCm * EDGE_CM_DEFAULTS.biye));
      }
    }
    var minSide = geometryMinSideForDepth(bounds);
    var bordurPct = finiteNumber(options && options.bordurDepthPct, DEFAULTS.bordurDepthPct);
    var bordurDepth = Math.max(10, Math.round(minSide * bordurPct)) + 3;
    var ratio = finiteNumber(options && options.biyeToBordurRatio, 3 / 6);
    return Math.max(5, Math.round(bordurDepth * ratio));
  }

  function geometryMinSideForDepth(bounds) {
    if (isAmorfGeometry(bounds)) {
      var fit = amorfFitBox(bounds, 0);
      return Math.min(finiteNumber(fit && fit.w, bounds.w), finiteNumber(fit && fit.h, bounds.h));
    }
    return Math.min(finiteNumber(bounds && bounds.w, 0), finiteNumber(bounds && bounds.h, 0));
  }

  function amorfPxPerCm(geometry) {
    if (!isAmorfGeometry(geometry)) return 0;
    var fit = amorfFitBox(geometry, 0);
    var widthCm = Math.max(1, finiteNumber(geometry.widthCm, 160));
    var lengthCm = Math.max(1, finiteNumber(geometry.lengthCm, 230));
    var pxW = finiteNumber(fit && fit.w, 0) / widthCm;
    var pxH = finiteNumber(fit && fit.h, 0) / lengthCm;
    if (pxW > 0 && pxH > 0) return (pxW + pxH) / 2;
    return Math.max(pxW, pxH, 0);
  }

  function shapeEdgeDepthSeed(geometry, fallbackDepth, edgeType, options) {
    if (!isAmorfGeometry(geometry)) return fallbackDepth;
    if (edgeType === "overlok") return 0;
    var pxPerCm = amorfPxPerCm(geometry);
    if (pxPerCm > 0) {
      if (edgeType === "bordur") {
        return Math.max(10, Math.round(pxPerCm * EDGE_CM_DEFAULTS.bordur - 3));
      }
      if (isDoubleBordur(edgeType)) {
        var totalCm = EDGE_CM_DEFAULTS.ciftOuter + EDGE_CM_DEFAULTS.ciftInner;
        return Math.max(10, Math.round((pxPerCm * totalCm) / 1.5 - 3));
      }
    }
    var minSide = geometryMinSideForDepth(geometry);
    var bordurPct = finiteNumber(options && options.bordurDepthPct, DEFAULTS.bordurDepthPct);
    return Math.max(10, Math.round(minSide * bordurPct));
  }

  function edgeVisualDepth(bounds, depth, edgeType, options) {
    var safeDepth = Math.max(0, Math.round(finiteNumber(depth, 0)));
    if (edgeType === "overlok") return 0;
    if (edgeType === "bordur" && options && options.__bordurCoverDepth) return options.__bordurCoverDepth;
    if (edgeType === "bordur") return bordurVisualDepth(safeDepth);
    if (isDoubleBordur(edgeType)) return doubleBordurDepths(safeDepth).total;
    if (edgeType !== "biye") return safeDepth + 2;
    return biyeVisualDepth(bounds, options);
  }

  function bordurReferenceDepth(bounds, options) {
    var minSide = geometryMinSideForDepth(bounds);
    var bordurPct = finiteNumber(options && options.bordurDepthPct, DEFAULTS.bordurDepthPct);
    return bordurVisualDepth(Math.max(10, Math.round(minSide * bordurPct)));
  }

  function makeBodyPattern(ctx, patternImg, innerW, innerH) {
    if (!patternImg) return null;
    var tile = document.createElement("canvas");
    var sourceW = finiteNumber(patternImg.naturalWidth || patternImg.width, 0);
    var sourceH = finiteNumber(patternImg.naturalHeight || patternImg.height, 0);
    var safeW = finiteNumber(innerW, 0);
    var safeH = finiteNumber(innerH, 0);
    if (sourceW < 1 || sourceH < 1 || safeW < 2 || safeH < 2) return null;
    var size = Math.max(90, Math.min(260, Math.round(Math.min(safeW, safeH) * 0.34)));
    if (!Number.isFinite(size) || size < 1) return null;
    var scale = Math.max(size / sourceW, size / sourceH);
    var drawW = Math.round(sourceW * scale);
    var drawH = Math.round(sourceH * scale);
    if (!Number.isFinite(drawW) || !Number.isFinite(drawH) || drawW < 1 || drawH < 1) return null;
    var drawX = Math.round((size - drawW) / 2);
    var drawY = Math.round((size - drawH) / 2);
    tile.width = size;
    tile.height = size;
    var tctx = tile.getContext("2d", { willReadFrequently: true });
    tctx.fillStyle = "#d8d3ca";
    tctx.fillRect(0, 0, size, size);
    try {
      tctx.drawImage(patternImg, drawX, drawY, drawW, drawH);
      return ctx.createPattern(tile, "repeat");
    } catch (err) {
      return null;
    }
  }

  function drawImageCover(ctx, img, x, y, w, h) {
    var sourceW = finiteNumber(img.naturalWidth || img.width, 0);
    var sourceH = finiteNumber(img.naturalHeight || img.height, 0);
    if (sourceW < 1 || sourceH < 1 || w < 1 || h < 1) return false;
    var scale = Math.max(w / sourceW, h / sourceH);
    var drawW = sourceW * scale;
    var drawH = sourceH * scale;
    var drawX = x + (w - drawW) / 2;
    var drawY = y + (h - drawH) / 2;
    ctx.drawImage(img, drawX, drawY, drawW, drawH);
    return true;
  }

  function drawImageCoverFromSourceRect(ctx, img, sx, sy, sw, sh, x, y, w, h) {
    if (sw < 1 || sh < 1 || w < 1 || h < 1) return false;
    var scale = Math.max(w / sw, h / sh);
    var drawW = sw * scale;
    var drawH = sh * scale;
    var drawX = x + (w - drawW) / 2;
    var drawY = y + (h - drawH) / 2;
    ctx.drawImage(img, sx, sy, sw, sh, drawX, drawY, drawW, drawH);
    return true;
  }

  function drawImageCoverFromSourceInset(ctx, img, sourceInsetPct, x, y, w, h) {
    var sourceW = finiteNumber(img && (img.naturalWidth || img.width), 0);
    var sourceH = finiteNumber(img && (img.naturalHeight || img.height), 0);
    if (sourceW < 1 || sourceH < 1 || w < 1 || h < 1) return false;
    var insetPct = clamp(finiteNumber(sourceInsetPct, 0), 0, TEXTURE_SOURCE_MAX_INSET);
    var sx = Math.round(sourceW * insetPct);
    var sy = Math.round(sourceH * insetPct);
    var sw = Math.max(1, Math.round(sourceW - sx * 2));
    var sh = Math.max(1, Math.round(sourceH - sy * 2));
    return drawImageCoverFromSourceRect(ctx, img, sx, sy, sw, sh, x, y, w, h);
  }

  function isOverlokCleanBody(options) {
    return !!(options && options.__massimoOverlokCleanBody);
  }

  function usesBodyRepeatSurface(options) {
    return String(options && options.bodyTextureSurfaceMode || "").toLowerCase() === "repeat";
  }

  function overlokCleanBodyOptions(options) {
    var repeatBody = usesBodyRepeatSurface(options);
    return Object.assign({}, options || {}, {
      __massimoOverlokCleanBody: true,
      textureSourceInsetPct: clamp(finiteNumber(options && options.textureSourceInsetPct, 0), 0, TEXTURE_SOURCE_MAX_INSET),
      textureEdgeOverscanPx: repeatBody
        ? Math.max(40, finiteNumber(options && options.textureEdgeOverscanPx, 0))
        : finiteNumber(options && options.textureEdgeOverscanPx, 0)
    });
  }

  function drawOverlokCleanBodySurface(ctx, bounds, inset, x, y, w, h, patternImg, options) {
    var layer = createCanvas(ctx.canvas.width, ctx.canvas.height);
    var lctx = layer.getContext("2d", { willReadFrequently: true });
    var useBodyRepeat = usesBodyRepeatSurface(options);
    var sourceInset = clamp(finiteNumber(options && options.textureSourceInsetPct, 0), 0, TEXTURE_SOURCE_MAX_INSET);
    var repeatXValue = useBodyRepeat && Object.prototype.hasOwnProperty.call(options || {}, "bodyTextureRepeatX")
      ? options.bodyTextureRepeatX
      : options && options.textureRepeatX;
    var repeatYValue = useBodyRepeat && Object.prototype.hasOwnProperty.call(options || {}, "bodyTextureRepeatY")
      ? options.bodyTextureRepeatY
      : options && options.textureRepeatY;
    var repeatX = clamp(finiteNumber(repeatXValue, DEFAULTS.textureRepeatX), 1.2, useBodyRepeat ? 96 : 6);
    var repeatY = clamp(finiteNumber(repeatYValue, DEFAULTS.textureRepeatY), 1.2, useBodyRepeat ? 140 : 8);
    var minTileSize = useBodyRepeat
      ? clamp(finiteNumber(Object.prototype.hasOwnProperty.call(options || {}, "bodyTextureMinTileSize") ? options.bodyTextureMinTileSize : options && options.textureMinTileSize, 36), 12, 160)
      : 0;
    var painted = false;
    lctx.imageSmoothingEnabled = true;
    lctx.imageSmoothingQuality = "high";
    try {
      painted = useBodyRepeat
        ? (sourceInset
          ? drawMirroredTextureSurfaceFromSourceInset(lctx, patternImg, x, y, w, h, sourceInset, repeatX, repeatY, { minTileSize: minTileSize })
          : drawMirroredTextureSurface(lctx, patternImg, x, y, w, h, repeatX, repeatY, { minTileSize: minTileSize }))
        : drawImageCoverFromSourceInset(lctx, patternImg, sourceInset, x, y, w, h);
      if (!painted) painted = drawImageCoverFromSourceInset(lctx, patternImg, sourceInset, x, y, w, h);
    } catch (err) {
      painted = false;
    }
    if (!painted) return false;
    if (bounds.mask) drawMaskToLayer(lctx, bounds, inset);
    ctx.save();
    ctx.globalAlpha = 1;
    ctx.drawImage(layer, 0, 0);
    ctx.restore();
    ctx.canvas.__massimoTextureDebug = {
      mode: useBodyRepeat ? "overlok-clean-body-repeat-source" : "overlok-clean-body-single-source",
      build: BUILD,
      sourceInsetPct: sourceInset,
      repeatX: useBodyRepeat ? repeatX : null,
      repeatY: useBodyRepeat ? repeatY : null,
      tileWidth: useBodyRepeat ? Math.max(minTileSize, Math.round(w / Math.max(0.35, repeatX))) : null,
      tileHeight: useBodyRepeat ? Math.max(minTileSize, Math.round(h / Math.max(0.35, repeatY))) : null,
      sourceEdgeGuard: 0
    };
    return true;
  }

  function drawScaledTextureSurface(ctx, img, x, y, w, h, options) {
    var sourceW = finiteNumber(img.naturalWidth || img.width, 0);
    var sourceH = finiteNumber(img.naturalHeight || img.height, 0);
    if (sourceW < 1 || sourceH < 1 || w < 1 || h < 1) return false;

    var rx = Math.round(x);
    var ry = Math.round(y);
    var rw = Math.round(w);
    var rh = Math.round(h);
    var overscan = Math.max(0, Math.round(finiteNumber(options && options.textureEdgeOverscanPx, 0)));
    var dx = rx - overscan;
    var dy = ry - overscan;
    var dw = rw + overscan * 2;
    var dh = rh + overscan * 2;
    ctx.save();
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    var sourceInsetPct = clamp(finiteNumber(options && options.textureSourceInsetPct, 0), 0, TEXTURE_SOURCE_MAX_INSET);
    var sx = Math.round(sourceW * sourceInsetPct);
    var sy = Math.round(sourceH * sourceInsetPct);
    var sw = Math.max(1, Math.round(sourceW - sx * 2));
    var sh = Math.max(1, Math.round(sourceH - sy * 2));
    var useBodyRepeatSurface = !!(options && options.__massimoBodyRepeatSurface);
    var repeatX = 0;
    var repeatY = 0;
    var minTileSize = 0;
    var painted = false;
    if (useBodyRepeatSurface) {
      repeatX = clamp(finiteNumber(options && options.textureRepeatX, DEFAULTS.textureRepeatX), 1.2, 96);
      repeatY = clamp(finiteNumber(options && options.textureRepeatY, DEFAULTS.textureRepeatY), 1.2, 140);
      minTileSize = clamp(finiteNumber(options && options.textureMinTileSize, 36), 12, 160);
      painted = sourceInsetPct
        ? drawMirroredTextureSurfaceFromSourceInset(ctx, img, dx, dy, dw, dh, sourceInsetPct, repeatX, repeatY, { minTileSize: minTileSize })
        : drawMirroredTextureSurface(ctx, img, dx, dy, dw, dh, repeatX, repeatY, { minTileSize: minTileSize });
    }
    if (!painted) {
      painted = drawImageCoverFromSourceRect(ctx, img, sx, sy, sw, sh, dx, dy, dw, dh);
    }
    if (!painted) {
      ctx.restore();
      return false;
    }
    ctx.restore();
    ctx.canvas.__massimoTextureDebug = {
      mode: useBodyRepeatSurface ? "body-repeat-surface-tone-transfer" : "single-surface-tone-transfer",
      scale: clamp(finiteNumber(options && options.textureScale, DEFAULTS.textureScale), 0.25, 1.1),
      cellWidth: rw,
      cellHeight: rh,
      tileWidth: useBodyRepeatSurface ? Math.max(minTileSize, Math.round(dw / Math.max(0.35, repeatX))) : rw,
      tileHeight: useBodyRepeatSurface ? Math.max(minTileSize, Math.round(dh / Math.max(0.35, repeatY))) : rh,
      repeatX: repeatX || null,
      repeatY: repeatY || null,
      sourceInsetPct: sourceInsetPct,
      textureEdgeOverscanPx: overscan
    };
    return true;
  }

  function textureLowPassData(sourceCanvas, width, height, options) {
    var ratio = clamp(finiteNumber(options && options.textureLowPassRatio, DEFAULTS.textureLowPassRatio), 0.025, 0.12);
    var lowW = Math.max(24, Math.round(width * ratio));
    var lowH = Math.max(32, Math.round(height * ratio));
    var small = createCanvas(lowW, lowH);
    var sctx = small.getContext("2d", { willReadFrequently: true });
    sctx.imageSmoothingEnabled = true;
    sctx.imageSmoothingQuality = "high";
    sctx.drawImage(sourceCanvas, 0, 0, lowW, lowH);

    var low = createCanvas(width, height);
    var lctx = low.getContext("2d", { willReadFrequently: true });
    lctx.imageSmoothingEnabled = true;
    lctx.imageSmoothingQuality = "high";
    lctx.drawImage(small, 0, 0, width, height);
    try {
      return lctx.getImageData(0, 0, width, height).data;
    } catch (err) {
      return null;
    }
  }

  function drawSoftPatternReference(ctx, img, x, y, w, h) {
    var sourceW = finiteNumber(img.naturalWidth || img.width, 0);
    var sourceH = finiteNumber(img.naturalHeight || img.height, 0);
    if (sourceW < 1 || sourceH < 1 || w < 1 || h < 1) return false;

    var softW = Math.max(36, Math.round(w * 0.09));
    var softH = Math.max(48, Math.round(h * 0.09));
    var small = createCanvas(softW, softH);
    var sctx = small.getContext("2d", { willReadFrequently: true });
    sctx.imageSmoothingEnabled = true;
    sctx.imageSmoothingQuality = "high";
    if (!drawImageCover(sctx, img, 0, 0, softW, softH)) return false;

    var mid = createCanvas(Math.max(1, Math.round(w * 0.30)), Math.max(1, Math.round(h * 0.30)));
    var mctx = mid.getContext("2d", { willReadFrequently: true });
    mctx.imageSmoothingEnabled = true;
    mctx.imageSmoothingQuality = "high";
    mctx.drawImage(small, 0, 0, mid.width, mid.height);

    ctx.save();
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(mid, x, y, w, h);
    ctx.restore();
    return true;
  }

  function pixelLuma(data, i) {
    return data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
  }

  function reflectCoordinate(v, min, max) {
    return clamp(v, min, max);
  }

  function sampleLuma(data, width, height, x, y) {
    x = clamp(x, 0, width - 1);
    y = clamp(y, 0, height - 1);
    var x0 = Math.floor(x);
    var y0 = Math.floor(y);
    var x1 = Math.min(width - 1, x0 + 1);
    var y1 = Math.min(height - 1, y0 + 1);
    var tx = x - x0;
    var ty = y - y0;
    var i00 = (y0 * width + x0) * 4;
    var i10 = (y0 * width + x1) * 4;
    var i01 = (y1 * width + x0) * 4;
    var i11 = (y1 * width + x1) * 4;
    var a = pixelLuma(data, i00) * (1 - tx) + pixelLuma(data, i10) * tx;
    var b = pixelLuma(data, i01) * (1 - tx) + pixelLuma(data, i11) * tx;
    return a * (1 - ty) + b * ty;
  }

  function scaledBodyLuma(data, width, height, x, y, bounds, inset, scale, edgeGuard) {
    var safeScale = clamp(finiteNumber(scale, 1), 0.45, 1.2);
    var innerX = bounds.x + inset;
    var innerY = bounds.y + inset;
    var innerRight = bounds.x + bounds.w - inset;
    var innerBottom = bounds.y + bounds.h - inset;
    var safeGuard = Math.max(0, Math.round(finiteNumber(edgeGuard, 0)));
    var maxGuard = Math.max(0, Math.floor(Math.min(innerRight - innerX, innerBottom - innerY) / 2) - 1);
    safeGuard = Math.min(safeGuard, maxGuard);
    if (Math.abs(safeScale - 1) < 0.001) {
      var gx = clamp(x, innerX + safeGuard, innerRight - 1 - safeGuard);
      var gy = clamp(y, innerY + safeGuard, innerBottom - 1 - safeGuard);
      return sampleLuma(data, width, height, gx, gy);
    }
    var cx = (innerX + innerRight) / 2;
    var cy = (innerY + innerBottom) / 2;
    var sx = reflectCoordinate(cx + (x - cx) / safeScale, innerX, innerRight - 1);
    var sy = reflectCoordinate(cy + (y - cy) / safeScale, innerY, innerBottom - 1);
    sx = clamp(sx, innerX + safeGuard, innerRight - 1 - safeGuard);
    sy = clamp(sy, innerY + safeGuard, innerBottom - 1 - safeGuard);
    return sampleLuma(data, width, height, sx, sy);
  }

  function pixelChroma(data, i, y) {
    return {
      cb: (data[i + 2] - y) * 0.565,
      cr: (data[i] - y) * 0.713
    };
  }

  function chromaToRgb(y, cb, cr) {
    return {
      r: clamp(y + 1.403 * cr, 0, 255),
      g: clamp(y - 0.344 * cb - 0.714 * cr, 0, 255),
      b: clamp(y + 1.770 * cb, 0, 255)
    };
  }

  function isBodyPixel(bounds, px, py, inset) {
    var safeInset = Math.max(0, Math.round(finiteNumber(inset, 0)));
    var innerX = bounds.x + safeInset;
    var innerY = bounds.y + safeInset;
    var innerRight = bounds.x + bounds.w - safeInset;
    var innerBottom = bounds.y + bounds.h - safeInset;
    if (px < innerX || px >= innerRight || py < innerY || py >= innerBottom) return false;
    if (!bounds.mask || !bounds.maskWidth || !bounds.maskHeight) return true;
    var mx = Math.round(px * bounds.maskWidth / Math.max(1, bounds._canvasWidth || bounds.maskWidth));
    var my = Math.round(py * bounds.maskHeight / Math.max(1, bounds._canvasHeight || bounds.maskHeight));
    mx = clamp(mx, 0, bounds.maskWidth - 1);
    my = clamp(my, 0, bounds.maskHeight - 1);
    return !!bounds.mask[my * bounds.maskWidth + mx];
  }

  function edgeDetailFade(bounds, px, py, inset, guard) {
    if (!bounds || !guard) return 1;
    var innerX = bounds.x + inset;
    var innerY = bounds.y + inset;
    var innerRight = bounds.x + bounds.w - inset;
    var innerBottom = bounds.y + bounds.h - inset;
    var dist = Math.min(px - innerX, innerRight - px, py - innerY, innerBottom - py);
    var t = clamp(dist / guard, 0, 1);
    return t * t * (3 - 2 * t);
  }

  function drawMirroredTextureSurface(ctx, img, x, y, w, h, repeatX, repeatY, options) {
    var sourceW = finiteNumber(img.naturalWidth || img.width, 0);
    var sourceH = finiteNumber(img.naturalHeight || img.height, 0);
    if (sourceW < 1 || sourceH < 1 || w < 1 || h < 1) return false;
    var minTile = clamp(finiteNumber(options && options.minTileSize, 80), 12, 160);
    var tileW = Math.max(minTile, Math.round(w / Math.max(0.35, repeatX)));
    var tileH = Math.max(minTile, Math.round(h / Math.max(0.35, repeatY)));
    var tile = createCanvas(tileW, tileH);
    var tctx = tile.getContext("2d", { willReadFrequently: true });
    tctx.imageSmoothingEnabled = true;
    tctx.imageSmoothingQuality = "high";
    if (!drawImageCover(tctx, img, 0, 0, tileW, tileH)) return false;

    var rx = Math.round(x);
    var ry = Math.round(y);
    var rw = Math.round(w);
    var rh = Math.round(h);
    ctx.save();
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.beginPath();
    ctx.rect(rx, ry, rw, rh);
    ctx.clip();
    for (var row = 0, yy = 0; yy < rh + tileH; yy += tileH, row++) {
      for (var col = 0, xx = 0; xx < rw + tileW; xx += tileW, col++) {
        var flipX = col % 2 === 1;
        var flipY = row % 2 === 1;
        ctx.save();
        ctx.translate(rx + xx + (flipX ? tileW : 0), ry + yy + (flipY ? tileH : 0));
        ctx.scale(flipX ? -1 : 1, flipY ? -1 : 1);
        ctx.drawImage(tile, 0, 0, tileW, tileH);
        ctx.restore();
      }
    }
    ctx.restore();
    return true;
  }

  function drawMirroredTextureSurfaceFromSourceInset(ctx, img, x, y, w, h, sourceInsetPct, repeatX, repeatY, options) {
    var sourceW = finiteNumber(img && (img.naturalWidth || img.width), 0);
    var sourceH = finiteNumber(img && (img.naturalHeight || img.height), 0);
    if (sourceW < 1 || sourceH < 1 || w < 1 || h < 1) return false;
    var minTile = clamp(finiteNumber(options && options.minTileSize, 120), 48, 240);
    var tileW = Math.max(minTile, Math.round(w / Math.max(0.35, repeatX)));
    var tileH = Math.max(minTile, Math.round(h / Math.max(0.35, repeatY)));
    var tile = createCanvas(tileW, tileH);
    var tctx = tile.getContext("2d", { willReadFrequently: true });
    tctx.imageSmoothingEnabled = true;
    tctx.imageSmoothingQuality = "high";
    if (!drawImageCoverFromSourceInset(tctx, img, sourceInsetPct, 0, 0, tileW, tileH)) return false;

    var rx = Math.round(x);
    var ry = Math.round(y);
    var rw = Math.round(w);
    var rh = Math.round(h);
    ctx.save();
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.beginPath();
    ctx.rect(rx, ry, rw, rh);
    ctx.clip();
    for (var row = 0, yy = 0; yy < rh + tileH; yy += tileH, row++) {
      for (var col = 0, xx = 0; xx < rw + tileW; xx += tileW, col++) {
        var flipX = col % 2 === 1;
        var flipY = row % 2 === 1;
        ctx.save();
        ctx.translate(rx + xx + (flipX ? tileW : 0), ry + yy + (flipY ? tileH : 0));
        ctx.scale(flipX ? -1 : 1, flipY ? -1 : 1);
        ctx.drawImage(tile, 0, 0, tileW, tileH);
        ctx.restore();
      }
    }
    ctx.restore();
    return true;
  }

  function isBarcelonaSurfacePipeline(options) {
    return String(options && options.renderPipeline || "").toLowerCase() === "barcelona160x230";
  }

  function isBarcelonaSingleSurfacePipeline(options) {
    var pipeline = String(options && options.renderPipeline || "").toLowerCase();
    return pipeline === "barcelonasinglesurface" || pipeline === "barcelona-single-surface";
  }

  function drawBarcelonaSingleSurface(ctx, bounds, inset, x, y, w, h, patternImg, options) {
    if (!patternImg || !bounds) return false;
    var canvasW = ctx.canvas.width;
    var canvasH = ctx.canvas.height;
    var layer = createCanvas(canvasW, canvasH);
    var lctx = layer.getContext("2d", { willReadFrequently: true });
    lctx.imageSmoothingEnabled = true;
    lctx.imageSmoothingQuality = "high";
    var sourceInset = options && options.__massimoArCleanTexture
      ? clamp(Math.max(AR_CLEAN_TEXTURE_MIN_INSET, finiteNumber(options && options.textureSourceInsetPct, 0)), 0, AR_CLEAN_TEXTURE_MAX_INSET)
      : 0;
    // Tile factor: "2m uzaktan bakis" gibi pile pattern'in body icinde daha kucuk
    // gozukmesi icin source 2x2/3x3 tile edilir. Source texture-synthesis ile
    // seamless uretildi (--tiling generate) → dikis gozukmuyor.
    var tileFactor = Math.max(1, Math.round(finiteNumber(options && options.bodyTileFactor, 1)));
    if (sourceInset) tileFactor = 1; // AR clean texture path tile etmez
    var painted = false;
    try {
      if (tileFactor > 1) {
        // Tile mode: source-asp ≈ body-asp oldugu icin her tile'a SOURCE'U STRETCH
        // ederek ciziyoruz (drawImageCover overflow seam yarattigi icin). Source
        // texture-synthesis ile --tiling generate uretildi: yatay seamless tam,
        // dikey ~%20 fark var ama gozle pek belirgin degil. Mirror yaklasimi
        // gorunur simetri yaratiyor (worse), bu yuzden duz stretch kullaniyoruz.
        var tileW = w / tileFactor;
        var tileH = h / tileFactor;
        var sourceW = patternImg.naturalWidth || patternImg.width || 0;
        var sourceH = patternImg.naturalHeight || patternImg.height || 0;
        if (sourceW > 0 && sourceH > 0) {
          for (var ty = 0; ty < tileFactor; ty += 1) {
            for (var tx = 0; tx < tileFactor; tx += 1) {
              lctx.drawImage(patternImg, 0, 0, sourceW, sourceH, x + tx * tileW, y + ty * tileH, tileW, tileH);
            }
          }
          painted = true;
        }
      } else {
        painted = sourceInset
          ? drawImageCoverFromSourceInset(lctx, patternImg, sourceInset, x, y, w, h)
          : drawImageCover(lctx, patternImg, x, y, w, h);
      }
    } catch (err) {
      painted = false;
    }
    if (!painted) return false;

    // Natural lighting overlay: subtle radial gradient (lighter top-center,
    // slightly darker edges) — mimics polytuft-style room lighting on carpet.
    // Range ±%6 luminance; preserves texture, adds 3D "lit floor" feel.
    var lightStrength = clamp(finiteNumber(options && options.bodyLightingStrength, 0), 0, 0.2);
    if (lightStrength > 0) {
      var cx = x + w / 2;
      var cy = y + h * 0.35; // light center slightly above middle (window light)
      var radius = Math.sqrt(w * w + h * h) * 0.65;
      var grad = lctx.createRadialGradient(cx, cy, radius * 0.1, cx, cy, radius);
      // Multiplicative-style: light center, dark edge (compress to small alpha)
      grad.addColorStop(0, "rgba(255,255,255," + (lightStrength * 0.9) + ")");
      grad.addColorStop(0.55, "rgba(255,255,255,0)");
      grad.addColorStop(1, "rgba(0,0,0," + (lightStrength * 0.6) + ")");
      lctx.globalCompositeOperation = "overlay";
      lctx.fillStyle = grad;
      lctx.fillRect(x, y, w, h);
      lctx.globalCompositeOperation = "source-over";
    }

    if (bounds.mask) drawMaskToLayer(lctx, bounds, inset);
    ctx.drawImage(layer, 0, 0);

    var stats = bodyTextureStats(layer, bounds, inset, x, y, w, h);
    ctx.canvas.__massimoTextureDebug = {
      mode: "barcelona-single-surface",
      build: BUILD,
      sourceNaturalWidth: patternImg.naturalWidth || patternImg.width || 0,
      sourceNaturalHeight: patternImg.naturalHeight || patternImg.height || 0,
      sourceInsetPct: sourceInset || 0,
      lightStrength: lightStrength,
      tileFactor: tileFactor,
      selectedWidthCm: finiteNumber(options && options.__massimoPhysicalWidthCm, 160),
      selectedLengthCm: finiteNumber(options && options.__massimoPhysicalLengthCm, 230),
      surfacePxPerCm: (patternImg.naturalWidth || patternImg.width || 0) * tileFactor / Math.max(1, finiteNumber(options && options.__massimoPhysicalWidthCm, 160)),
      bodyPixels: stats.count,
      outputAvg: { y: stats.yAvg, yStd: stats.yStd },
      rowStd: stats.rowStd
    };
    return true;
  }

  function bodyTextureStats(layer, bounds, inset, x, y, w, h) {
    var result = { count: 0, yAvg: 0, yStd: 0, rowStd: 0 };
    try {
      var canvasW = layer.width;
      var canvasH = layer.height;
      var data = layer.getContext("2d", { willReadFrequently: true }).getImageData(0, 0, canvasW, canvasH).data;
      var minX = Math.max(0, Math.floor(x));
      var minY = Math.max(0, Math.floor(y));
      var maxX = Math.min(canvasW, Math.ceil(x + w));
      var maxY = Math.min(canvasH, Math.ceil(y + h));
      var ySum = 0;
      var y2Sum = 0;
      var rowMeans = [];
      for (var py = minY; py < maxY; py++) {
        var rowSum = 0;
        var rowCount = 0;
        for (var px = minX; px < maxX; px++) {
          if (!isBodyPixel(bounds, px, py, inset)) continue;
          var i = (py * canvasW + px) * 4;
          if (data[i + 3] < 8) continue;
          var yValue = pixelLuma(data, i);
          ySum += yValue;
          y2Sum += yValue * yValue;
          rowSum += yValue;
          rowCount += 1;
          result.count += 1;
        }
        if (rowCount) rowMeans.push(rowSum / rowCount);
      }
      if (result.count) {
        result.yAvg = ySum / result.count;
        result.yStd = Math.sqrt(Math.max(0, y2Sum / result.count - result.yAvg * result.yAvg));
      }
      if (rowMeans.length) {
        var rowAvg = rowMeans.reduce(function (sum, value) { return sum + value; }, 0) / rowMeans.length;
        result.rowStd = Math.sqrt(rowMeans.reduce(function (sum, value) { return sum + (value - rowAvg) * (value - rowAvg); }, 0) / rowMeans.length);
      }
    } catch (err) {}
    return result;
  }

  function withPhysicalTextureScaleOptions(options, shapeConfig, designConfig) {
    var widthCm = finiteNumber(shapeConfig && shapeConfig.widthCm, finiteNumber(designConfig && designConfig.widthCm, 160));
    var lengthCm = finiteNumber(shapeConfig && shapeConfig.lengthCm, finiteNumber(designConfig && designConfig.lengthCm, 230));
    return Object.assign({}, options || {}, {
      __massimoPhysicalWidthCm: Math.max(1, widthCm),
      __massimoPhysicalLengthCm: Math.max(1, lengthCm)
    });
  }

  function physicalRepeatForReference(configuredRepeat, selectedCm, referenceCm, fallbackRepeat, minRepeat, maxRepeat) {
    var baseRepeat = finiteNumber(configuredRepeat, fallbackRepeat);
    var safeSelected = Math.max(1, finiteNumber(selectedCm, referenceCm));
    var safeReference = Math.max(1, finiteNumber(referenceCm, 1));
    return clamp(baseRepeat * safeSelected / safeReference, minRepeat, maxRepeat);
  }

  function drawBarcelona160x230Surface(ctx, bounds, inset, x, y, w, h, patternImg, options) {
    if (!patternImg || !bounds) return false;
    var canvasW = ctx.canvas.width;
    var canvasH = ctx.canvas.height;
    var baseImage = ctx.canvas.__massimoSourceImageData;
    var baseData = baseImage && baseImage.width === canvasW && baseImage.height === canvasH ? baseImage.data : null;
    if (!baseData) return false;

    var layer = createCanvas(canvasW, canvasH);
    var lctx = layer.getContext("2d", { willReadFrequently: true });
    // v213: high-quality image smoothing — pattern + detail texture daha keskin sample edilir.
    // Görüntü değişmez, sadece pixel interpolation kalitesi artar (low→high).
    lctx.imageSmoothingEnabled = true;
    lctx.imageSmoothingQuality = "high";
    var referenceWidthCm = 160;
    var referenceLengthCm = 230;
    var selectedWidthCm = Math.max(1, finiteNumber(options && options.__massimoPhysicalWidthCm, referenceWidthCm));
    var selectedLengthCm = Math.max(1, finiteNumber(options && options.__massimoPhysicalLengthCm, referenceLengthCm));
    var repeatX = physicalRepeatForReference(options && options.textureRepeatX, selectedWidthCm, referenceWidthCm, 16, 1.2, 34);
    var repeatY = physicalRepeatForReference(options && options.textureRepeatY, selectedLengthCm, referenceLengthCm, 24, 1.2, 48);
    var minTileSize = clamp(finiteNumber(options && options.textureMinTileSize, 34), 12, 90);
    var arCleanTexture = !!(options && options.__massimoArCleanTexture);
    var sourceInset = arCleanTexture
      ? clamp(Math.max(AR_CLEAN_TEXTURE_MIN_INSET, finiteNumber(options && options.textureSourceInsetPct, 0)), 0, AR_CLEAN_TEXTURE_MAX_INSET)
      : 0;
    var painted = false;
    try {
      painted = sourceInset
        ? drawMirroredTextureSurfaceFromSourceInset(lctx, patternImg, x, y, w, h, sourceInset, repeatX, repeatY, { minTileSize: minTileSize })
        : drawMirroredTextureSurface(lctx, patternImg, x, y, w, h, repeatX, repeatY, { minTileSize: minTileSize });
      if (!painted) {
        painted = sourceInset
          ? drawImageCoverFromSourceInset(lctx, patternImg, sourceInset, x, y, w, h)
          : drawImageCover(lctx, patternImg, x, y, w, h);
      }
    } catch (err) {
      painted = false;
    }
    if (!painted) return false;
    if (bounds.mask) drawMaskToLayer(lctx, bounds, inset);

    var textureImage;
    var outputImage;
    var lowData;
    var microLowData = null;
    var baseLowData;
    var lowPassRatio = clamp(finiteNumber(options && options.textureLowPassRatio, 0.035), 0.025, 0.12);
    var microDetailStrength = clamp(finiteNumber(options && options.textureMicroDetailStrength, DEFAULTS.textureMicroDetailStrength), 0, 0.9);
    var microLowPassRatio = clamp(finiteNumber(options && options.textureMicroDetailLowPassRatio, DEFAULTS.textureMicroDetailLowPassRatio), 0.035, 0.16);
    var baseLowPassRatio = clamp(finiteNumber(options && options.baseLowPassRatio, 0.075), 0.035, 0.14);
    try {
      textureImage = lctx.getImageData(0, 0, canvasW, canvasH);
      outputImage = ctx.getImageData(0, 0, canvasW, canvasH);
      /* v219: pattern low-pass cache */
      var patternCacheBucket = ctx.canvas.__massimoPatternLowCache;
      if (!patternCacheBucket || patternCacheBucket.patternImg !== patternImg || patternCacheBucket.w !== canvasW || patternCacheBucket.h !== canvasH) {
        patternCacheBucket = { patternImg: patternImg, w: canvasW, h: canvasH, byRatio: {} };
        ctx.canvas.__massimoPatternLowCache = patternCacheBucket;
      }
      var lowKey = lowPassRatio.toFixed(4);
      if (patternCacheBucket.byRatio[lowKey]) {
        lowData = patternCacheBucket.byRatio[lowKey];
      } else {
        lowData = textureLowPassData(layer, canvasW, canvasH, { textureLowPassRatio: lowPassRatio }) || textureImage.data;
        patternCacheBucket.byRatio[lowKey] = lowData;
      }
      if (microDetailStrength > 0) {
        var microKey = microLowPassRatio.toFixed(4);
        if (patternCacheBucket.byRatio[microKey]) {
          microLowData = patternCacheBucket.byRatio[microKey];
        } else {
          microLowData = textureLowPassData(layer, canvasW, canvasH, { textureLowPassRatio: microLowPassRatio }) || lowData;
          patternCacheBucket.byRatio[microKey] = microLowData;
        }
      }
      /* v218: baseLowData CACHE — source image değişmediği sürece tekrar hesaplanmaz */
      var baseCache = ctx.canvas.__massimoBaseLowCache;
      if (baseCache && baseCache.baseImage === baseImage && baseCache.ratio === baseLowPassRatio && baseCache.w === canvasW && baseCache.h === canvasH) {
        baseLowData = baseCache.data;
      } else {
        var baseLayer = createCanvas(canvasW, canvasH);
        baseLayer.getContext("2d", { willReadFrequently: true }).putImageData(baseImage, 0, 0);
        baseLowData = textureLowPassData(baseLayer, canvasW, canvasH, { textureLowPassRatio: baseLowPassRatio }) || baseData;
        ctx.canvas.__massimoBaseLowCache = {
          baseImage: baseImage,
          ratio: baseLowPassRatio,
          w: canvasW,
          h: canvasH,
          data: baseLowData
        };
      }
    } catch (err2) {
      return false;
    }

    // 2026-05-19: barcelona-detail-pattern — inject high-frequency structure
    // from a secondary detail pattern (e.g. weave-rich Lasis) while keeping
    // primary pattern colors. Opt-in: only active when options._detailPatternImg
    // is provided; safe NO-OP otherwise (zero regression for other qualities).
    var detailPatternImg = options && options._detailPatternImg;
    var detailData = null;
    var detailLowData = null;
    var detailPatternStrength = 0;
    if (detailPatternImg && detailPatternImg.naturalWidth) {
      try {
        var detailLayer = createCanvas(canvasW, canvasH);
        var dctx = detailLayer.getContext("2d", { willReadFrequently: true });
        dctx.imageSmoothingEnabled = true;
        dctx.imageSmoothingQuality = "high";
        var detailPainted = sourceInset
          ? drawMirroredTextureSurfaceFromSourceInset(dctx, detailPatternImg, x, y, w, h, sourceInset, repeatX, repeatY, { minTileSize: minTileSize })
          : drawMirroredTextureSurface(dctx, detailPatternImg, x, y, w, h, repeatX, repeatY, { minTileSize: minTileSize });
        if (!detailPainted) {
          detailPainted = sourceInset
            ? drawImageCoverFromSourceInset(dctx, detailPatternImg, sourceInset, x, y, w, h)
            : drawImageCover(dctx, detailPatternImg, x, y, w, h);
        }
        if (detailPainted) {
          if (bounds.mask) drawMaskToLayer(dctx, bounds, inset);
          var detailImage = dctx.getImageData(0, 0, canvasW, canvasH);
          detailData = detailImage.data;
          detailLowData = textureLowPassData(detailLayer, canvasW, canvasH, { textureLowPassRatio: lowPassRatio }) || detailData;
          detailPatternStrength = clamp(finiteNumber(options && options.detailPatternStrength, 1.0), 0, 2.5);
        }
      } catch (errDetail) {
        detailData = null;
        detailLowData = null;
        detailPatternStrength = 0;
      }
    }

    var texData = textureImage.data;
    var outData = outputImage.data;
    var minX = Math.max(0, Math.floor(x));
    var minY = Math.max(0, Math.floor(y));
    var maxX = Math.min(canvasW, Math.ceil(x + w));
    var maxY = Math.min(canvasH, Math.ceil(y + h));
    var baseYSum = 0;
    var baseY2Sum = 0;
    var texYSum = 0;
    var texY2Sum = 0;
    var texRSum = 0;
    var texGSum = 0;
    var texBSum = 0;
    var texCbSum = 0;
    var texCrSum = 0;
    var count = 0;
    var sourceEdgeGuard = arCleanTexture
      ? Math.max(180, Math.round(Math.min(w, h) * 0.18), Math.round(inset * 6.2))
      : 0;

    for (var sy = minY; sy < maxY; sy++) {
      for (var sx = minX; sx < maxX; sx++) {
        if (!isBodyPixel(bounds, sx, sy, inset)) continue;
        var si = (sy * canvasW + sx) * 4;
        if (texData[si + 3] < 8) continue;
        var baseY = arCleanTexture
          ? scaledBodyLuma(baseData, canvasW, canvasH, sx, sy, bounds, inset, 1, sourceEdgeGuard)
          : pixelLuma(baseData, si);
        var texY = pixelLuma(texData, si);
        var texChroma = pixelChroma(texData, si, texY);
        baseYSum += baseY;
        baseY2Sum += baseY * baseY;
        texYSum += texY;
        texY2Sum += texY * texY;
        texRSum += texData[si];
        texGSum += texData[si + 1];
        texBSum += texData[si + 2];
        texCbSum += texChroma.cb;
        texCrSum += texChroma.cr;
        count++;
      }
    }
    if (count < 10) return false;

    var inv = 1 / count;
    var baseAvgY = baseYSum * inv;
    var texAvgY = texYSum * inv;
    var texStdY = Math.sqrt(Math.max(0, texY2Sum * inv - texAvgY * texAvgY));
    var baseStdY = Math.sqrt(Math.max(0, baseY2Sum * inv - baseAvgY * baseAvgY));
    var texAvgR = texRSum * inv;
    var texAvgG = texGSum * inv;
    var texAvgB = texBSum * inv;
    var texAvgCb = texCbSum * inv;
    var texAvgCr = texCrSum * inv;
    var bodyDetailStrength = arCleanTexture
      ? clamp(finiteNumber(options && options.bodyDetailStrength, 0.14), 0.08, 0.18)
      : clamp(finiteNumber(options && options.bodyDetailStrength, 0.92), 0.45, 1.45);
    var baseToneStrength = arCleanTexture
      ? 0
      : clamp(finiteNumber(options && options.baseToneStrength, 0.1), 0, 0.4);
    var textureDetailStrength = clamp(finiteNumber(options && options.textureDetailStrength, 0.075), 0, 0.28);
    var textureChromaDetailStrength = clamp(finiteNumber(options && options.textureChromaDetailStrength, 0.035), 0, 0.2);
    var toneFieldStrength = clamp(finiteNumber(options && options.textureDetailMix, 0.1), 0, 0.66);
    var shadeStrength = arCleanTexture
      ? 0
      : clamp(finiteNumber(options && options.textureShadingStrength, 0.07), 0, 0.35);
    var sharpen = clamp(finiteNumber(options && options.textureSharpen, 0.5), 0, 0.62);
    var colorLock = clamp(finiteNumber(options && options.textureColorLock, 1), 0, 1);
    var maxColorCorrection = clamp(finiteNumber(options && options.maxColorCorrection, DEFAULTS.maxColorCorrection), 4, 26);
    var hasRenderTarget = options && (
      Number.isFinite(Number(options.renderTargetR)) ||
      Number.isFinite(Number(options.renderTargetG)) ||
      Number.isFinite(Number(options.renderTargetB))
    );
    var targetAvgR = hasRenderTarget ? finiteNumber(options.renderTargetR, texAvgR) : texAvgR;
    var targetAvgG = hasRenderTarget ? finiteNumber(options.renderTargetG, texAvgG) : texAvgG;
    var targetAvgB = hasRenderTarget ? finiteNumber(options.renderTargetB, texAvgB) : texAvgB;
    var renderColorCorrectionLimit = hasRenderTarget
      ? clamp(finiteNumber(options && options.renderColorCorrectionLimit, 72), maxColorCorrection, 90)
      : maxColorCorrection;
    var renderLightnessLift = clamp(finiteNumber(options && options.renderLightnessLift, DEFAULTS.renderLightnessLift), -10, 18);
    var renderRgbShiftR = clamp(finiteNumber(options && options.renderRgbShiftR, 0), -12, 12);
    var renderRgbShiftG = clamp(finiteNumber(options && options.renderRgbShiftG, 0), -12, 12);
    var renderRgbShiftB = clamp(finiteNumber(options && options.renderRgbShiftB, 0), -12, 12);
    var detailGain = textureDetailStrength * (0.72 + sharpen * 0.28);
    var outRSum = 0;
    var outGSum = 0;
    var outBSum = 0;
    var outYSum = 0;
    var outY2Sum = 0;

    for (var py = minY; py < maxY; py++) {
      for (var px = minX; px < maxX; px++) {
        if (!isBodyPixel(bounds, px, py, inset)) continue;
        var p = (py * canvasW + px) * 4;
        if (texData[p + 3] < 8) continue;
        var bodyY = arCleanTexture
          ? scaledBodyLuma(baseData, canvasW, canvasH, px, py, bounds, inset, 1, sourceEdgeGuard)
          : pixelLuma(baseData, p);
        var texY2 = pixelLuma(texData, p);
        var lowY = pixelLuma(lowData, p);
        var texChroma2 = pixelChroma(texData, p, texY2);
        var lowChroma = pixelChroma(lowData, p, lowY);
        var baseLowY = arCleanTexture
          ? scaledBodyLuma(baseLowData, canvasW, canvasH, px, py, bounds, inset, 1, sourceEdgeGuard)
          : pixelLuma(baseLowData, p);
        var bodyDetail = bodyY - baseLowY;
        var baseToneField = baseLowY - baseAvgY;
        var textureLowField = lowY - texAvgY;
        var textureDetail = texY2 - lowY;
        var microDetail = 0;
        if (microLowData) {
          microDetail = clamp(texY2 - pixelLuma(microLowData, p), -52, 52) * microDetailStrength;
        }
        var shade = bodyDetail * shadeStrength;
        // 2026-05-19: barcelona-detail-pattern — secondary high-frequency injection.
        // Bypasses the multi-component smoothing of primary pattern by adding raw
        // (det - detLow) high-pass directly to yValue. Strength=0 when no detail
        // pattern provided → identical to legacy behavior for non-Barcelona qualities.
        var extraDetailY = 0;
        if (detailData && detailLowData) {
          var detY = pixelLuma(detailData, p);
          var detLowY = pixelLuma(detailLowData, p);
          extraDetailY = (detY - detLowY) * detailPatternStrength;
        }
        var yValue = clamp(
          texAvgY +
          bodyDetail * bodyDetailStrength +
          baseToneField * baseToneStrength +
          textureLowField * toneFieldStrength +
          textureDetail * detailGain +
          microDetail +
          shade +
          renderLightnessLift +
          extraDetailY,
          0,
          255
        );
        var cbValue = texAvgCb + (lowChroma.cb - texAvgCb) * toneFieldStrength + (texChroma2.cb - lowChroma.cb) * textureChromaDetailStrength;
        var crValue = texAvgCr + (lowChroma.cr - texAvgCr) * toneFieldStrength + (texChroma2.cr - lowChroma.cr) * textureChromaDetailStrength;
        var rgb = chromaToRgb(yValue, cbValue, crValue);
        var baseR = baseData[p] + (texAvgR - texAvgY);
        var baseG = baseData[p + 1] + (texAvgG - texAvgY);
        var baseB = baseData[p + 2] + (texAvgB - texAvgY);
        var r = clamp(rgb.r * colorLock + baseR * (1 - colorLock), 0, 255);
        var g = clamp(rgb.g * colorLock + baseG * (1 - colorLock), 0, 255);
        var b = clamp(rgb.b * colorLock + baseB * (1 - colorLock), 0, 255);
        outData[p] = r;
        outData[p + 1] = g;
        outData[p + 2] = b;
        outData[p + 3] = 255;
        outRSum += r;
        outGSum += g;
        outBSum += b;
        outYSum += yValue;
        outY2Sum += yValue * yValue;
      }
    }

    var outRAvg = outRSum * inv;
    var outGAvg = outGSum * inv;
    var outBAvg = outBSum * inv;
    var rShift = clamp(targetAvgR - outRAvg, -renderColorCorrectionLimit, renderColorCorrectionLimit);
    var gShift = clamp(targetAvgG - outGAvg, -renderColorCorrectionLimit, renderColorCorrectionLimit);
    var bShift = clamp(targetAvgB - outBAvg, -renderColorCorrectionLimit, renderColorCorrectionLimit);
    var finalRSum = 0;
    var finalGSum = 0;
    var finalBSum = 0;
    var finalYSum = 0;
    var finalY2Sum = 0;

    for (var cy = minY; cy < maxY; cy++) {
      for (var cx = minX; cx < maxX; cx++) {
        if (!isBodyPixel(bounds, cx, cy, inset)) continue;
        var cp = (cy * canvasW + cx) * 4;
        if (texData[cp + 3] < 8) continue;
        var cr = clamp(outData[cp] + rShift + renderRgbShiftR, 0, 255);
        var cg = clamp(outData[cp + 1] + gShift + renderRgbShiftG, 0, 255);
        var cb = clamp(outData[cp + 2] + bShift + renderRgbShiftB, 0, 255);
        var finalY = cr * 0.299 + cg * 0.587 + cb * 0.114;
        outData[cp] = cr;
        outData[cp + 1] = cg;
        outData[cp + 2] = cb;
        outData[cp + 3] = 255;
        finalRSum += cr;
        finalGSum += cg;
        finalBSum += cb;
        finalYSum += finalY;
        finalY2Sum += finalY * finalY;
      }
    }

    ctx.putImageData(outputImage, 0, 0);
    var finalYAvg = finalYSum * inv;
    var finalYStd = Math.sqrt(Math.max(0, finalY2Sum * inv - finalYAvg * finalYAvg));
    ctx.canvas.__massimoTextureDebug = {
      mode: "barcelona-160x230-surface",
      build: BUILD,
      repeatX: repeatX,
      repeatY: repeatY,
      selectedWidthCm: selectedWidthCm,
      selectedLengthCm: selectedLengthCm,
      referenceWidthCm: referenceWidthCm,
      referenceLengthCm: referenceLengthCm,
      physicalTileCmX: selectedWidthCm / Math.max(1, repeatX),
      physicalTileCmY: selectedLengthCm / Math.max(1, repeatY),
      minTileSize: minTileSize,
      sourceInsetPct: sourceInset,
      sourceEdgeGuard: sourceEdgeGuard,
      arCleanTexture: arCleanTexture,
      cleanDesignRegionTexture: !!(options && options.__massimoCleanDesignRegionTexture),
      lowPassRatio: lowPassRatio,
      baseLowPassRatio: baseLowPassRatio,
      microDetailStrength: microDetailStrength,
      microLowPassRatio: microLowPassRatio,
      bodyDetailStrength: bodyDetailStrength,
      baseToneStrength: baseToneStrength,
      textureDetailStrength: textureDetailStrength,
      textureChromaDetailStrength: textureChromaDetailStrength,
      toneFieldStrength: toneFieldStrength,
      shading: shadeStrength,
      sharpen: sharpen,
      colorLock: colorLock,
      renderTargetAvg: hasRenderTarget ? { r: targetAvgR, g: targetAvgG, b: targetAvgB } : null,
      renderColorCorrectionLimit: renderColorCorrectionLimit,
      sourceAvg: { r: texAvgR, g: texAvgG, b: texAvgB, y: texAvgY, yStd: texStdY },
      baseAvg: { y: baseAvgY, yStd: baseStdY },
      outputAvg: {
        r: finalRSum * inv,
        g: finalGSum * inv,
        b: finalBSum * inv,
        y: finalYAvg,
        yStd: finalYStd
      },
      delta: {
        r: finalRSum * inv - texAvgR,
        g: finalGSum * inv - texAvgG,
        b: finalBSum * inv - texAvgB,
        y: finalYAvg - texAvgY,
        yStd: finalYStd - texStdY
      },
      colorShift: { r: rShift, g: gShift, b: bShift },
      renderRgbShift: { r: renderRgbShiftR, g: renderRgbShiftG, b: renderRgbShiftB },
      bodyPixels: count
    };
    return true;
  }

  function drawPhotoSurfaceHD(ctx, bounds, inset, x, y, w, h, patternImg, options) {
    if (!patternImg || !bounds) return false;
    if (isBarcelonaSingleSurfacePipeline(options)) {
      return drawBarcelonaSingleSurface(ctx, bounds, inset, x, y, w, h, patternImg, options);
    }
    if (isBarcelonaSurfacePipeline(options)) {
      return drawBarcelona160x230Surface(ctx, bounds, inset, x, y, w, h, patternImg, options);
    }
    var canvasW = ctx.canvas.width;
    var canvasH = ctx.canvas.height;
    var layer = createCanvas(canvasW, canvasH);
    var lctx = layer.getContext("2d", { willReadFrequently: true });
    var repeatX = clamp(finiteNumber(options && options.textureRepeatX, DEFAULTS.textureRepeatX), 0.8, 6);
    var repeatY = clamp(finiteNumber(options && options.textureRepeatY, DEFAULTS.textureRepeatY), 0.8, 8);
    var detailMix = clamp(finiteNumber(options && options.textureDetailMix, DEFAULTS.textureDetailMix), 0, 1.25);
    var shadeStrength = clamp(finiteNumber(options && options.textureShadingStrength, DEFAULTS.textureShadingStrength), 0, 0.45);
    var sharpen = clamp(finiteNumber(options && options.textureSharpen, DEFAULTS.textureSharpen), 0, 0.9);
    var colorLock = clamp(finiteNumber(options && options.textureColorLock, DEFAULTS.textureColorLock), 0, 1);
    var painted = false;
    try {
      var sourceInset = options && options.__massimoArCleanTexture
        ? clamp(Math.max(AR_CLEAN_TEXTURE_MIN_INSET, finiteNumber(options && options.textureSourceInsetPct, 0)), 0, AR_CLEAN_TEXTURE_MAX_INSET)
        : 0;
      painted = sourceInset
        ? drawImageCoverFromSourceInset(lctx, patternImg, sourceInset, x, y, w, h)
        : drawImageCover(lctx, patternImg, x, y, w, h);
    } catch (err) {
      painted = false;
    }
    if (!painted) return false;
    if (bounds.mask) drawMaskToLayer(lctx, bounds, inset);

    var textureImage;
    var outputImage;
    var lowData;
    try {
      textureImage = lctx.getImageData(0, 0, canvasW, canvasH);
      outputImage = ctx.getImageData(0, 0, canvasW, canvasH);
      lowData = textureLowPassData(layer, canvasW, canvasH, { textureLowPassRatio: 0.045 });
    } catch (err3) {
      return false;
    }

    var texData = textureImage.data;
    var outData = outputImage.data;
    var baseImage = ctx.canvas.__massimoSourceImageData;
    var baseData = baseImage && baseImage.width === canvasW && baseImage.height === canvasH ? baseImage.data : null;
    var startX = Math.max(0, Math.floor(x));
    var startY = Math.max(0, Math.floor(y));
    var endX = Math.min(canvasW, Math.ceil(x + w));
    var endY = Math.min(canvasH, Math.ceil(y + h));
    var count = 0;
    var texR = 0;
    var texG = 0;
    var texB = 0;
    var texL = 0;
    var baseL = 0;
    for (var py = startY; py < endY; py++) {
      for (var px = startX; px < endX; px++) {
        if (!isBodyPixel(bounds, px, py, inset)) continue;
        var i = (py * canvasW + px) * 4;
        if (texData[i + 3] < 8) continue;
        var luma = pixelLuma(texData, i);
        texR += texData[i];
        texG += texData[i + 1];
        texB += texData[i + 2];
        texL += luma;
        baseL += baseData ? pixelLuma(baseData, i) : luma;
        count++;
      }
    }
    if (count < 10) return false;
    var inv = 1 / count;
    var texAvgR = texR * inv;
    var texAvgG = texG * inv;
    var texAvgB = texB * inv;
    var texAvgL = texL * inv;
    var baseAvgL = baseL * inv;
    var edgeGuard = Math.max(10, Math.round(Math.min(w, h) * 0.035));
    var detailGain = 0.9 + detailMix * 0.16 + sharpen * 0.18;
    var lowTone = clamp(0.1 + (1 - detailMix) * 0.12, 0.06, 0.24);
    for (var py2 = startY; py2 < endY; py2++) {
      for (var px2 = startX; px2 < endX; px2++) {
        if (!isBodyPixel(bounds, px2, py2, inset)) continue;
        var j = (py2 * canvasW + px2) * 4;
        if (texData[j + 3] < 8) continue;
        var fade = edgeDetailFade(bounds, px2, py2, inset, edgeGuard);
        var shade = 0;
        if (baseData) {
          shade = clamp(pixelLuma(baseData, j) - baseAvgL, -70, 70) * shadeStrength;
        }
        var activeGain = detailGain * (0.72 + fade * 0.28);
        var surfaceR = texAvgR + (lowData[j] - texAvgR) * lowTone + (texData[j] - lowData[j]) * activeGain + shade;
        var surfaceG = texAvgG + (lowData[j + 1] - texAvgG) * lowTone + (texData[j + 1] - lowData[j + 1]) * activeGain + shade;
        var surfaceB = texAvgB + (lowData[j + 2] - texAvgB) * lowTone + (texData[j + 2] - lowData[j + 2]) * activeGain + shade;
        var baseR = baseData ? baseData[j] + (texAvgR - texAvgL) : texData[j];
        var baseG = baseData ? baseData[j + 1] + (texAvgG - texAvgL) : texData[j + 1];
        var baseB = baseData ? baseData[j + 2] + (texAvgB - texAvgL) : texData[j + 2];
        outData[j] = clamp(surfaceR * colorLock + baseR * (1 - colorLock), 0, 255);
        outData[j + 1] = clamp(surfaceG * colorLock + baseG * (1 - colorLock), 0, 255);
        outData[j + 2] = clamp(surfaceB * colorLock + baseB * (1 - colorLock), 0, 255);
        outData[j + 3] = Math.max(outData[j + 3], texData[j + 3]);
      }
    }
    ctx.putImageData(outputImage, 0, 0);
    ctx.canvas.__massimoTextureDebug = {
      mode: "photoSurfaceHD-single-source",
      build: BUILD,
      repeatX: repeatX,
      repeatY: repeatY,
      detailMix: detailMix,
      shading: shadeStrength,
      sharpen: sharpen,
      colorLock: colorLock,
      sourceInsetPct: sourceInset || 0,
      arCleanTexture: !!(options && options.__massimoArCleanTexture),
      cleanDesignRegionTexture: !!(options && options.__massimoCleanDesignRegionTexture),
      lowTone: lowTone,
      detailGain: detailGain,
      bodyPixels: count
    };
    return true;
  }

  function drawTextureTransfer(ctx, bounds, depth, edgeType, patternImg, options) {
    if (!patternImg || !bounds) return false;
    var safeDepth = finiteNumber(depth, 0);
    var inset = edgeType === "overlok" ? 0 : safeDepth;
    var x = finiteNumber(bounds.x, 0) + inset;
    var y = finiteNumber(bounds.y, 0) + inset;
    var bw = finiteNumber(bounds.w, 0);
    var bh = finiteNumber(bounds.h, 0);
    if (bw < 2 || bh < 2) return false;
    var w = Math.max(1, bw - inset * 2);
    var h = Math.max(1, bh - inset * 2);
    var cleanOverlok = edgeType === "overlok" && isOverlokCleanBody(options);
    var cleanOverlokRepeat = cleanOverlok && usesBodyRepeatSurface(options);
    if (cleanOverlokRepeat) {
      return drawOverlokCleanBodySurface(ctx, bounds, inset, x, y, w, h, patternImg, options);
    }
    if (!cleanOverlok && options && (options.renderPipeline === "photoSurfaceHD" || isBarcelonaSurfacePipeline(options) || isBarcelonaSingleSurfacePipeline(options))) {
      return drawPhotoSurfaceHD(ctx, bounds, inset, x, y, w, h, patternImg, options);
    }

    var layer = createCanvas(ctx.canvas.width, ctx.canvas.height);
    var lctx = layer.getContext("2d", { willReadFrequently: true });
    var painted = false;
    lctx.imageSmoothingEnabled = true;
    lctx.imageSmoothingQuality = "high";
    try {
      var textureOptions = options;
      if (edgeType === "biye" || cleanOverlokRepeat) {
        var bodyOverscan = Math.max(28, Math.round(inset * 3.2));
        if (cleanOverlokRepeat) bodyOverscan = Math.max(40, Math.round(Math.min(w, h) * 0.055));
        var bodyRepeatSurface = usesBodyRepeatSurface(options);
        textureOptions = Object.assign({}, options || {}, {
          textureSourceInsetPct: cleanOverlokRepeat
            ? Math.max(0.24, finiteNumber(options && options.textureSourceInsetPct, 0))
            : finiteNumber(options && options.textureSourceInsetPct, bodyRepeatSurface ? 0.24 : 0),
          textureEdgeOverscanPx: finiteNumber(options && options.textureEdgeOverscanPx, (cleanOverlokRepeat || bodyRepeatSurface) ? bodyOverscan : 0)
        });
      }
      if (String(options && options.bodyTextureSurfaceMode || "").toLowerCase() === "repeat") {
        textureOptions = Object.assign({}, textureOptions || {}, {
          __massimoBodyRepeatSurface: true,
          textureRepeatX: Object.prototype.hasOwnProperty.call(options || {}, "bodyTextureRepeatX") ? options.bodyTextureRepeatX : options.textureRepeatX,
          textureRepeatY: Object.prototype.hasOwnProperty.call(options || {}, "bodyTextureRepeatY") ? options.bodyTextureRepeatY : options.textureRepeatY,
          textureMinTileSize: Object.prototype.hasOwnProperty.call(options || {}, "bodyTextureMinTileSize") ? options.bodyTextureMinTileSize : options.textureMinTileSize
        });
      }
      painted = drawScaledTextureSurface(lctx, patternImg, x, y, w, h, textureOptions);
    } catch (err) {
      painted = false;
    }
    if (painted && lctx.canvas.__massimoTextureDebug) {
      ctx.canvas.__massimoTextureDebug = lctx.canvas.__massimoTextureDebug;
    }
    if (!painted) {
      var pattern = makeBodyPattern(lctx, patternImg, w, h);
      if (!pattern) return false;
      lctx.fillStyle = pattern;
      lctx.fillRect(x, y, w, h);
    }

    var canvasW = ctx.canvas.width;
    var canvasH = ctx.canvas.height;
    bounds._canvasWidth = canvasW;
    bounds._canvasHeight = canvasH;

    var base;
    var output;
    var texture;
    try {
      var sourceBase = ctx.canvas.__massimoSourceImageData;
      if (sourceBase && sourceBase.width === canvasW && sourceBase.height === canvasH) {
        base = sourceBase;
      } else {
        base = ctx.getImageData(0, 0, canvasW, canvasH);
      }
      output = ctx.getImageData(0, 0, canvasW, canvasH);
      texture = lctx.getImageData(0, 0, canvasW, canvasH);
    } catch (err) {
      return false;
    }

    var baseData = base.data;
    var outputData = output.data;
    var textureData = texture.data;
    var lowPassData = textureLowPassData(layer, canvasW, canvasH, options) || textureData;
    var minX = Math.max(0, Math.floor(x));
    var minY = Math.max(0, Math.floor(y));
    var maxX = Math.min(canvasW, Math.ceil(x + w));
    var maxY = Math.min(canvasH, Math.ceil(y + h));
    var surfaceScale = 1;
    var sourceEdgeGuard = edgeType === "biye" ? Math.max(24, Math.round(inset * 2.6)) : 0;
    if (edgeType === "overlok") {
      sourceEdgeGuard = Math.max(96, Math.round(Math.min(w, h) * 0.09));
    }
    if (cleanOverlok) {
      sourceEdgeGuard = Math.max(36, Math.round(Math.min(w, h) * 0.06));
    }
    var cleanDesignRegionTexture = !!(options && options.__massimoCleanDesignRegionTexture);
    if (cleanDesignRegionTexture) {
      sourceEdgeGuard = Math.max(sourceEdgeGuard, 120, Math.round(Math.min(w, h) * 0.18));
    }
    var baseSum = 0;
    var baseY2Sum = 0;
    var textureSum = 0;
    var baseCbSum = 0;
    var baseCrSum = 0;
    var baseRSum = 0;
    var baseGSum = 0;
    var baseBSum = 0;
    var textureCbSum = 0;
    var textureCrSum = 0;
    var textureRSum = 0;
    var textureGSum = 0;
    var textureBSum = 0;
    var textureY2Sum = 0;
    var count = 0;

    for (var py = minY; py < maxY; py++) {
      for (var px = minX; px < maxX; px++) {
        if (!isBodyPixel(bounds, px, py, inset)) continue;
        var idx = (py * canvasW + px) * 4;
        if (textureData[idx + 3] < 8) continue;
        var baseLuma = scaledBodyLuma(baseData, canvasW, canvasH, px, py, bounds, inset, surfaceScale, sourceEdgeGuard);
        var textureLuma = pixelLuma(textureData, idx);
        var baseChrom = pixelChroma(baseData, idx, baseLuma);
        var textureChrom = pixelChroma(textureData, idx, textureLuma);
        baseSum += baseLuma;
        baseY2Sum += baseLuma * baseLuma;
        baseRSum += baseData[idx];
        baseGSum += baseData[idx + 1];
        baseBSum += baseData[idx + 2];
        textureSum += textureLuma;
        textureY2Sum += textureLuma * textureLuma;
        textureRSum += textureData[idx];
        textureGSum += textureData[idx + 1];
        textureBSum += textureData[idx + 2];
        baseCbSum += baseChrom.cb;
        baseCrSum += baseChrom.cr;
        textureCbSum += textureChrom.cb;
        textureCrSum += textureChrom.cr;
        count++;
      }
    }
    if (!count) return false;

    var baseAvg = Math.max(1, baseSum / count);
    var textureAvg = Math.max(1, textureSum / count);
    var baseRAvg = baseRSum / count;
    var baseGAvg = baseGSum / count;
    var baseBAvg = baseBSum / count;
    var textureRAvg = textureRSum / count;
    var textureGAvg = textureGSum / count;
    var textureBAvg = textureBSum / count;
    var baseStd = Math.sqrt(Math.max(0, baseY2Sum / count - baseAvg * baseAvg));
    var textureStd = Math.sqrt(Math.max(0, textureY2Sum / count - textureAvg * textureAvg));
    var textureCbAvg = textureCbSum / count;
    var textureCrAvg = textureCrSum / count;
    var baseCbAvg = baseCbSum / count;
    var baseCrAvg = baseCrSum / count;
    var globalTone = clamp(textureAvg / baseAvg, 0.72, 1.18);
    var chromaDelta = Math.sqrt(
      Math.pow(textureCbSum / count - baseCbSum / count, 2) +
      Math.pow(textureCrSum / count - baseCrSum / count, 2)
    );
    var bodyDetailStrength = cleanDesignRegionTexture ? 0 : clamp(finiteNumber(options && options.bodyDetailStrength, DEFAULTS.bodyDetailStrength), 0.5, 1.5);
    var textureDetailStrength = clamp(finiteNumber(options && options.textureDetailStrength, DEFAULTS.textureDetailStrength), 0, 0.6);
    var textureChromaDetailStrength = clamp(finiteNumber(options && options.textureChromaDetailStrength, DEFAULTS.textureChromaDetailStrength), 0, 0.55);
    var textureToneStrength = clamp(finiteNumber(options && options.textureToneStrength, DEFAULTS.textureToneStrength), 0, 1);
    var textureColorStrength = clamp(finiteNumber(options && options.textureColorStrength, DEFAULTS.textureColorStrength), 0, 1);
    var targetContrastRatio = clamp(finiteNumber(options && options.textureContrastRatio, DEFAULTS.textureContrastRatio), 0, 0.86);
    var maxTextureContrastRatio = clamp(finiteNumber(options && options.maxTextureContrastRatio, DEFAULTS.maxTextureContrastRatio), 0, 1.18);
    var maxContrastBoost = clamp(finiteNumber(options && options.maxTextureContrastBoost, DEFAULTS.maxTextureContrastBoost), 1, 2);
    var maxColorCorrection = clamp(finiteNumber(options && options.maxColorCorrection, DEFAULTS.maxColorCorrection), 4, 28);
    var renderLightnessLift = clamp(finiteNumber(options && options.renderLightnessLift, DEFAULTS.renderLightnessLift), 0, 18);
    var outYSum = 0;
    var outY2Sum = 0;
    var outRSum = 0;
    var outGSum = 0;
    var outBSum = 0;
    var targetLumaAvg = cleanDesignRegionTexture ? textureAvg : baseAvg + (textureAvg - baseAvg) * textureToneStrength;
    var targetCbAvg = cleanDesignRegionTexture ? textureCbAvg : baseCbAvg + (textureCbAvg - baseCbAvg) * textureColorStrength;
    var targetCrAvg = cleanDesignRegionTexture ? textureCrAvg : baseCrAvg + (textureCrAvg - baseCrAvg) * textureColorStrength;
    var targetRAvg = cleanDesignRegionTexture ? textureRAvg : baseRAvg + (textureRAvg - baseRAvg) * textureColorStrength;
    var targetGAvg = cleanDesignRegionTexture ? textureGAvg : baseGAvg + (textureGAvg - baseGAvg) * textureColorStrength;
    var targetBAvg = cleanDesignRegionTexture ? textureBAvg : baseBAvg + (textureBAvg - baseBAvg) * textureColorStrength;
    // Different patterns must fully replace the body color while preserving the real 160x230 surface scale.
    for (var ty = minY; ty < maxY; ty++) {
      for (var tx = minX; tx < maxX; tx++) {
        if (!isBodyPixel(bounds, tx, ty, inset)) continue;
        var p = (ty * canvasW + tx) * 4;
        if (textureData[p + 3] < 8) continue;
        var bodyLuma = scaledBodyLuma(baseData, canvasW, canvasH, tx, ty, bounds, inset, surfaceScale, sourceEdgeGuard);
        var texLuma = pixelLuma(textureData, p);
        var lowLuma = pixelLuma(lowPassData, p);
        var texChroma = pixelChroma(textureData, p, texLuma);
        var lowChroma = pixelChroma(lowPassData, p, lowLuma);
        var bodyDetail = bodyLuma - baseAvg;
        var textureDetail = texLuma - lowLuma;
        var edgeFade = cleanOverlok ? 1 : edgeDetailFade(bounds, tx, ty, inset, sourceEdgeGuard);
        var transferredY = clamp(targetLumaAvg + bodyDetail * bodyDetailStrength * edgeFade + textureDetail * textureDetailStrength * edgeFade, 0, 255);
        var transferredCb = targetCbAvg + (texChroma.cb - lowChroma.cb) * textureChromaDetailStrength * edgeFade;
        var transferredCr = targetCrAvg + (texChroma.cr - lowChroma.cr) * textureChromaDetailStrength * edgeFade;
        var transferred = chromaToRgb(transferredY, transferredCb, transferredCr);
        outputData[p] = transferred.r;
        outputData[p + 1] = transferred.g;
        outputData[p + 2] = transferred.b;
        outputData[p + 3] = 255;
        outYSum += transferredY;
        outY2Sum += transferredY * transferredY;
        outRSum += transferred.r;
        outGSum += transferred.g;
        outBSum += transferred.b;
      }
    }

    var outAvg = outYSum / count;
    var outStd = Math.sqrt(Math.max(0, outY2Sum / count - outAvg * outAvg));
    var contrastSourceStd = cleanDesignRegionTexture || textureDetailStrength > 0 ? textureStd : baseStd;
    var targetStd = contrastSourceStd * targetContrastRatio;
    var maxOutputStd = contrastSourceStd * maxTextureContrastRatio;
    var desiredStd = outStd < targetStd ? targetStd : outStd;
    desiredStd = Math.min(desiredStd, maxOutputStd);
    var contrastGain = outStd > 0.1 ? clamp(desiredStd / outStd, 0.45, maxContrastBoost) : 1;
    var finalRSum = 0;
    var finalGSum = 0;
    var finalBSum = 0;
    var finalYSum = 0;
    var finalY2Sum = 0;

    for (var ay = minY; ay < maxY; ay++) {
      for (var ax = minX; ax < maxX; ax++) {
        if (!isBodyPixel(bounds, ax, ay, inset)) continue;
        var ap = (ay * canvasW + ax) * 4;
        if (textureData[ap + 3] < 8) continue;
        var currentY = pixelLuma(outputData, ap);
        var currentChroma = pixelChroma(outputData, ap, currentY);
        var adjustedY = clamp(targetLumaAvg + (currentY - outAvg) * contrastGain, 0, 255);
        var adjusted = chromaToRgb(adjustedY, currentChroma.cb, currentChroma.cr);
        outputData[ap] = adjusted.r;
        outputData[ap + 1] = adjusted.g;
        outputData[ap + 2] = adjusted.b;
        outputData[ap + 3] = 255;
        finalRSum += adjusted.r;
        finalGSum += adjusted.g;
        finalBSum += adjusted.b;
        finalYSum += adjustedY;
        finalY2Sum += adjustedY * adjustedY;
      }
    }

    var finalRAvg = finalRSum / count;
    var finalGAvg = finalGSum / count;
    var finalBAvg = finalBSum / count;
    var rShift = clamp(targetRAvg - finalRAvg, -maxColorCorrection, maxColorCorrection);
    var gShift = clamp(targetGAvg - finalGAvg, -maxColorCorrection, maxColorCorrection);
    var bShift = clamp(targetBAvg - finalBAvg, -maxColorCorrection, maxColorCorrection);
    var correctedRSum = 0;
    var correctedGSum = 0;
    var correctedBSum = 0;
    var correctedYSum = 0;
    var correctedY2Sum = 0;

    for (var cy = minY; cy < maxY; cy++) {
      for (var cx = minX; cx < maxX; cx++) {
        if (!isBodyPixel(bounds, cx, cy, inset)) continue;
        var cp = (cy * canvasW + cx) * 4;
        if (textureData[cp + 3] < 8) continue;
        var cr = clamp(outputData[cp] + rShift + renderLightnessLift, 0, 255);
        var cg = clamp(outputData[cp + 1] + gShift + renderLightnessLift, 0, 255);
        var cb = clamp(outputData[cp + 2] + bShift + renderLightnessLift, 0, 255);
        var correctedY = cr * 0.299 + cg * 0.587 + cb * 0.114;
        outputData[cp] = cr;
        outputData[cp + 1] = cg;
        outputData[cp + 2] = cb;
        outputData[cp + 3] = 255;
        correctedRSum += cr;
        correctedGSum += cg;
        correctedBSum += cb;
        correctedYSum += correctedY;
        correctedY2Sum += correctedY * correctedY;
      }
    }

    ctx.putImageData(output, 0, 0);
    var correctedYAvg = correctedYSum / count;
    var correctedYStd = Math.sqrt(Math.max(0, correctedY2Sum / count - correctedYAvg * correctedYAvg));
    ctx.canvas.__massimoTextureDebug = Object.assign({}, ctx.canvas.__massimoTextureDebug || {}, {
      mode: ctx.canvas.__massimoTextureDebug && ctx.canvas.__massimoTextureDebug.mode || "single-surface-tone-transfer",
      build: BUILD,
      chromaDelta: chromaDelta,
      globalTone: globalTone,
      sourceAvg: { r: textureRAvg, g: textureGAvg, b: textureBAvg, y: textureAvg, yStd: textureStd },
      baseAvg: { r: baseRAvg, g: baseGAvg, b: baseBAvg, y: baseAvg, yStd: baseStd },
      targetAvg: { r: targetRAvg, g: targetGAvg, b: targetBAvg, y: targetLumaAvg },
      firstPassAvg: {
        r: outRSum / count,
        g: outGSum / count,
        b: outBSum / count,
        y: outAvg,
        yStd: outStd
      },
      outputAvg: {
        r: correctedRSum / count,
        g: correctedGSum / count,
        b: correctedBSum / count,
        y: correctedYAvg,
        yStd: correctedYStd
      },
      delta: {
        r: correctedRSum / count - textureRAvg,
        g: correctedGSum / count - textureGAvg,
        b: correctedBSum / count - textureBAvg,
        y: correctedYAvg - textureAvg,
        yStd: correctedYStd - textureStd
      },
      contrastRatio: correctedYStd / Math.max(1, textureStd),
      contrastGain: contrastGain,
      targetContrastRatio: targetContrastRatio,
      maxTextureContrastRatio: maxTextureContrastRatio,
      textureLowPassRatio: clamp(finiteNumber(options && options.textureLowPassRatio, DEFAULTS.textureLowPassRatio), 0.025, 0.12),
      scale: clamp(finiteNumber(options && options.textureScale, DEFAULTS.textureScale), 0.45, 1.2),
      colorShift: { r: rShift, g: gShift, b: bShift },
      bodyDetailStrength: bodyDetailStrength,
      cleanDesignRegionTexture: cleanDesignRegionTexture,
      arCleanTexture: !!(options && options.__massimoArCleanTexture),
      textureDetailStrength: textureDetailStrength,
      textureChromaDetailStrength: textureChromaDetailStrength,
      textureToneStrength: textureToneStrength,
      textureColorStrength: textureColorStrength,
      renderLightnessLift: renderLightnessLift,
      sourceEdgeGuard: sourceEdgeGuard
    });
    return true;
  }

  function swatchColor(fabric) {
    if (!fabric) return "#9a8d7d";
    if (fabric.color) return fabric.color;
    var key = String(fabric.id || fabric.label || "").toLowerCase();
    var colors = {
      "canvas-02": "#b9aa96",
      "canvas-06": "#9f927e",
      "canvas-14": "#c6b8a2",
      "canvas-24": "#8d8171",
      "canvas-26": "#756b5f",
      "canvas-33": "#d1c2aa",
      "canvas-47": "#6f665e",
      "canvas-48": "#a99b87",
      "canvas-49": "#8f8272",
      "canvas-64": "#cfc3b4",
      "canvas-66": "#b2a391",
      "canvas-84": "#5f5a52",
      "alcantara-02": "#c3b6a5",
      "alcantara-05": "#9b8c7b",
      "alcantara-09": "#776f67",
      "alcantara-11": "#b0a596",
      "alcantara-16": "#d2c5b4",
      "alcantara-17": "#8a7969",
      "alcantara-19": "#665c54",
      "alcantara-20": "#a18f7d"
    };
    return colors[key] || "#9a8d7d";
  }

  function averageFabricImageColor(fabricImg, fallback) {
    if (!fabricImg) return fallback || "#9a8d7d";
    try {
      var sample = createCanvas(18, 18);
      var sctx = sample.getContext("2d", { willReadFrequently: true });
      sctx.drawImage(fabricImg, 0, 0, 18, 18);
      var data = sctx.getImageData(0, 0, 18, 18).data;
      var r = 0;
      var g = 0;
      var b = 0;
      var count = 0;
      for (var i = 0; i < data.length; i += 4) {
        var a = data[i + 3] / 255;
        if (a < 0.35) continue;
        r += data[i] * a;
        g += data[i + 1] * a;
        b += data[i + 2] * a;
        count += a;
      }
      if (!count) return fallback || "#9a8d7d";
      r = Math.round(r / count);
      g = Math.round(g / count);
      b = Math.round(b / count);
      return "rgb(" + r + "," + g + "," + b + ")";
    } catch (_) {
      return fallback || "#9a8d7d";
    }
  }

  function normalizeDoubleBordurFabrics(fabric) {
    if (!fabric) return { outer: null, inner: null };
    if (fabric.outer || fabric.inner) {
      return {
        outer: fabric.outer || null,
        inner: fabric.inner || null
      };
    }
    return { outer: fabric, inner: null };
  }

  function drawRoundedRect(ctx, x, y, w, h, r) {
    r = Math.max(0, Math.min(r, w / 2, h / 2));
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  function drawSegment(ctx, a, b) {
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
  }

  function drawBordurCornerSeams(ctx, bounds, depth, options) {
    if (!bounds || depth < 8) return;
    var x = bounds.x;
    var y = bounds.y;
    var w = bounds.w;
    var h = bounds.h;
    var seams = [
      [{ x: x, y: y }, { x: x + depth, y: y + depth }],
      [{ x: x + w, y: y }, { x: x + w - depth, y: y + depth }],
      [{ x: x + w, y: y + h }, { x: x + w - depth, y: y + h - depth }],
      [{ x: x, y: y + h }, { x: x + depth, y: y + h - depth }]
    ];
    var soft = !!(options && options.softBordurSeams);
    var seamWidth = soft
      ? Math.max(0.45, Math.min(1.3, depth * 0.045))
      : Math.max(0.65, Math.min(1.8, depth * 0.055));
    var stitchLen = Math.max(3, Math.min(8, depth * 0.18));
    var darkAlpha = clamp(finiteNumber(options && options.bordurCornerSeamAlpha, soft ? 0.04 : 0.07), 0, 0.18);
    var lightAlpha = clamp(finiteNumber(options && options.bordurCornerSeamLightAlpha, soft ? 0.03 : 0.05), 0, 0.16);
    var stitchAlpha = clamp(finiteNumber(options && options.bordurCornerStitchAlpha, soft ? 0 : 0.025), 0, 0.08);

    ctx.save();
    ctx.lineCap = "round";
    seams.forEach(function (pair) {
      var a = pair[0];
      var b = pair[1];
      var dx = b.x - a.x;
      var dy = b.y - a.y;
      var len = Math.sqrt(dx * dx + dy * dy) || 1;
      var nx = -dy / len;
      var ny = dx / len;

      ctx.strokeStyle = "rgba(35, 26, 20, " + darkAlpha + ")";
      ctx.lineWidth = seamWidth;
      drawSegment(ctx, a, b);

      ctx.strokeStyle = "rgba(255, 245, 225, " + lightAlpha + ")";
      ctx.lineWidth = 1;
      drawSegment(ctx, { x: a.x + nx * 1.6, y: a.y + ny * 1.6 }, { x: b.x + nx * 1.6, y: b.y + ny * 1.6 });

      ctx.strokeStyle = "rgba(40, 30, 24, " + stitchAlpha + ")";
      ctx.lineWidth = 1;
      var count = Math.max(3, Math.floor(len / 9));
      for (var i = 1; i < count; i++) {
        var t = i / count;
        var px = a.x + dx * t;
        var py = a.y + dy * t;
        ctx.beginPath();
        ctx.moveTo(px - nx * stitchLen * 0.45, py - ny * stitchLen * 0.45);
        ctx.lineTo(px + nx * stitchLen * 0.45, py + ny * stitchLen * 0.45);
        ctx.stroke();
      }
    });
    ctx.restore();
  }

  function drawFabricRectBand(ctx, x, y, w, h, depth, radius, fabricColor, fabricImg, fillAlpha, textureAlpha) {
    if (depth < 1 || w < 2 || h < 2) return;
    var layer = createCanvas(ctx.canvas.width, ctx.canvas.height);
    var lctx = layer.getContext("2d", { willReadFrequently: true });
    var patternInfo = null;
    lctx.save();
    clipRectEdgeBand(lctx, x, y, w, h, depth, radius || 0);
    lctx.globalAlpha = fillAlpha;
    lctx.fillStyle = averageFabricImageColor(fabricImg, fabricColor || "#897b6d");
    lctx.fillRect(x, y, w, h);
    if (fabricImg) {
      lctx.globalAlpha = textureAlpha;
      patternInfo = drawFabricTextureFill(lctx, fabricImg, x, y, w, h, depth, "edge-hd");
    }
    lctx.restore();
    if (patternInfo) {
      applyEdgeFabricFinish(lctx, x, y, w, h, depth, 1.06);
      recordEdgeTextureDebug(
        ctx,
        fabricImg,
        "rect-double-bordur-fabric-texture",
        patternInfo,
        canvasRectLumaStats(layer, x, y, w, h)
      );
    }
    ctx.drawImage(layer, 0, 0);
  }

  function drawInsetStitches(ctx, bounds, inset, style, lineWidth) {
    if (!bounds || inset < 4) return;
    var x1 = bounds.x + inset;
    var y1 = bounds.y + inset;
    var x2 = bounds.x + bounds.w - inset;
    var y2 = bounds.y + bounds.h - inset;
    var spacing = Math.max(6, Math.min(13, inset * 0.24));
    var stitch = Math.max(2, Math.min(5, inset * 0.1));

    ctx.save();
    ctx.strokeStyle = style;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = "round";
    for (var x = x1 + spacing; x < x2 - spacing; x += spacing) {
      ctx.beginPath();
      ctx.moveTo(x - stitch, y1);
      ctx.lineTo(x + stitch, y1);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x - stitch, y2);
      ctx.lineTo(x + stitch, y2);
      ctx.stroke();
    }
    for (var y = y1 + spacing; y < y2 - spacing; y += spacing) {
      ctx.beginPath();
      ctx.moveTo(x1, y - stitch);
      ctx.lineTo(x1, y + stitch);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x2, y - stitch);
      ctx.lineTo(x2, y + stitch);
      ctx.stroke();
    }
    ctx.restore();
  }

  function drawDoubleBordurSeams(ctx, bounds, depths) {
    if (!bounds || !depths) return;
    var total = depths.total;
    var innerBounds = insetBounds(bounds, depths.outer);
    strokeInnerEdge(ctx, bounds.x, bounds.y, bounds.w, bounds.h, depths.outer, 0, "rgba(18,14,11,0.12)", 0.8);
    strokeInnerEdge(ctx, bounds.x, bounds.y, bounds.w, bounds.h, total, 0, "rgba(54,42,34,0.16)", 0.9);
    strokeInnerEdge(ctx, bounds.x, bounds.y, bounds.w, bounds.h, Math.max(1, total - 2), 0, "rgba(255,245,230,0.06)", 0.7);
    drawBordurCornerSeams(ctx, bounds, depths.outer);
    drawBordurCornerSeams(ctx, innerBounds, depths.inner);
    drawInsetStitches(ctx, bounds, depths.outer, "rgba(33,25,20,0.10)", 0.65);
    drawInsetStitches(ctx, bounds, total, "rgba(33,25,20,0.12)", 0.65);
  }

  function drawDoubleBordurRectEdgeBand(ctx, bounds, depth, fabrics, fabricImgs) {
    var depths = doubleBordurDepths(depth);
    var outerFabric = fabrics && fabrics.outer;
    var innerFabric = fabrics && fabrics.inner;
    var outerImg = fabricImgs && fabricImgs.outer;
    var innerImg = fabricImgs && fabricImgs.inner;
    var innerBounds = insetBounds(bounds, depths.outer);

    if (outerFabric) {
      drawFabricRectBand(ctx, bounds.x, bounds.y, bounds.w, bounds.h, depths.outer, 0, swatchColor(outerFabric), outerImg, outerImg ? 0.74 : 0.84, 0.92);
    }
    if (innerFabric) {
      var innerColor = averageFabricImageColor(innerImg, swatchColor(innerFabric));
      drawFabricRectBand(ctx, innerBounds.x, innerBounds.y, innerBounds.w, innerBounds.h, depths.inner, 0, innerColor, innerImg, 1, 0.9);
    }
    if (outerFabric || innerFabric) drawDoubleBordurSeams(ctx, bounds, depths);
  }

  function drawDoubleBordurMaskEdgeBand(ctx, bounds, depth, fabrics, fabricImgs) {
    if (!bounds || !bounds.mask) {
      drawDoubleBordurRectEdgeBand(ctx, bounds, depth, fabrics, fabricImgs);
      return;
    }

    var depths = doubleBordurDepths(depth);
    var outerFabric = fabrics && fabrics.outer;
    var innerFabric = fabrics && fabrics.inner;
    var outerImg = fabricImgs && fabricImgs.outer;
    var innerImg = fabricImgs && fabricImgs.inner;

    if (outerFabric) {
      var outerLayer = createCanvas(ctx.canvas.width, ctx.canvas.height);
      var octx = outerLayer.getContext("2d", { willReadFrequently: true });
      octx.fillStyle = averageFabricImageColor(outerImg, swatchColor(outerFabric));
      octx.fillRect(bounds.x, bounds.y, bounds.w, bounds.h);
      var outerPatternInfo = outerImg ? drawFabricTextureFill(octx, outerImg, bounds.x, bounds.y, bounds.w, bounds.h, depths.outer, "edge-hd") : null;
      drawMaskToLayer(octx, bounds, 0);
      clearInnerRect(octx, bounds, depths.outer);
      if (outerPatternInfo) {
        applyEdgeFabricFinish(octx, bounds.x, bounds.y, bounds.w, bounds.h, depths.outer, 1.06);
        recordEdgeTextureDebug(
          ctx,
          outerImg,
          "mask-double-bordur-outer-fabric-texture",
          outerPatternInfo,
          canvasRectLumaStats(outerLayer, bounds.x, bounds.y, bounds.w, bounds.h)
        );
      }
      ctx.save();
      ctx.globalAlpha = outerImg ? 0.92 : 0.84;
      ctx.drawImage(outerLayer, 0, 0);
      ctx.restore();
    }

    if (innerFabric) {
      var innerLayer = createCanvas(ctx.canvas.width, ctx.canvas.height);
      var ictx = innerLayer.getContext("2d", { willReadFrequently: true });
      ictx.fillStyle = averageFabricImageColor(innerImg, swatchColor(innerFabric));
      ictx.fillRect(bounds.x, bounds.y, bounds.w, bounds.h);
      var innerPatternInfo = innerImg ? drawFabricTextureFill(ictx, innerImg, bounds.x, bounds.y, bounds.w, bounds.h, depths.inner, "edge-hd") : null;
      drawMaskToLayer(ictx, bounds, depths.outer);
      clearInnerRect(ictx, bounds, depths.total);
      if (innerPatternInfo) {
        applyEdgeFabricFinish(ictx, bounds.x, bounds.y, bounds.w, bounds.h, depths.inner, 1.06);
        recordEdgeTextureDebug(
          ctx,
          innerImg,
          "mask-double-bordur-inner-fabric-texture",
          innerPatternInfo,
          canvasRectLumaStats(innerLayer, bounds.x, bounds.y, bounds.w, bounds.h)
        );
      }
      ctx.save();
      ctx.globalAlpha = 0.96;
      ctx.drawImage(innerLayer, 0, 0);
      ctx.restore();
    }

    if (outerFabric || innerFabric) drawDoubleBordurSeams(ctx, bounds, depths);
  }

  function strokeInnerEdge(ctx, x, y, w, h, depth, radius, style, lineWidth) {
    ctx.save();
    ctx.strokeStyle = style;
    ctx.lineWidth = lineWidth;
    drawRoundedRect(ctx, x + depth, y + depth, Math.max(1, w - depth * 2), Math.max(1, h - depth * 2), radius);
    ctx.stroke();
    ctx.restore();
  }

  function drawBordurRectEdgeBand(ctx, bounds, depth, fabricColor, fabricImg, options) {
    var x = bounds.x;
    var y = bounds.y;
    var w = bounds.w;
    var h = bounds.h;
    var edgeDepth = bordurVisualDepth(depth);
    var layer = createCanvas(ctx.canvas.width, ctx.canvas.height);
    var lctx = layer.getContext("2d", { willReadFrequently: true });
    var patternInfo = null;

    lctx.save();
    // v200: edgeDepth+1 — bordür iç kenarı 1px body üzerine taşar, anti-aliasing gap'i kapanır.
    // (Body bordürün arkasında zaten innerX'ten itibaren çiziliyor; 1px overlap = bordür on top of body.)
    clipRectEdgeBand(lctx, x, y, w, h, edgeDepth + 1, 0);
    lctx.globalAlpha = 1;
    lctx.fillStyle = averageFabricImageColor(fabricImg, fabricColor || "#897b6d");
    lctx.fillRect(x, y, w, h);
    if (fabricImg) {
      lctx.globalAlpha = 1;
      patternInfo = drawFabricTextureFill(lctx, fabricImg, x, y, w, h, edgeDepth, "edge-hd");
    }
    lctx.restore();
    if (patternInfo) {
      applyEdgeFabricFinish(lctx, x, y, w, h, edgeDepth, 1.06);
      // v198: feather KALDIRILDI — destination-out, body dikdörtgeninin ulaşmadığı
      // bordür-inner pikseli silip beyaz canvas zemini gösteriyordu (boşluk artifact'i).
      // Bordürle body bitişik olmalı; v196 texture iyileştirmeleri zaten makro hissini azaltıyor.
      recordEdgeTextureDebug(
        ctx,
        fabricImg,
        "rect-bordur-fabric-texture",
        patternInfo,
        canvasRectLumaStats(layer, x, y, w, h)
      );
    }
    ctx.drawImage(layer, 0, 0);

    var softInner = !!(options && options.softBordurInnerEdge);
    var innerAlpha = clamp(finiteNumber(options && options.bordurInnerEdgeAlpha, softInner ? 0.08 : 0.12), 0, 0.2);
    var innerWidth = clamp(finiteNumber(options && options.bordurInnerEdgeWidth, softInner ? 0.55 : 0.75), 0.35, 1.1);
    strokeInnerEdge(ctx, x, y, w, h, edgeDepth, 0, "rgba(70,58,48," + innerAlpha + ")", innerWidth);
    drawBordurCornerSeams(ctx, bounds, edgeDepth, options);
    // v210: gerçek deri bordür dikiş izi — ÇOK İNCE sürekli çizgi (tek iplikçik), bordür içine 8% çekik.
    var stitchEnabled = !(options && options.disableBordurInsetStitches);
    var stitchAlpha = clamp(finiteNumber(options && options.bordurInsetStitchAlpha, 0.26), 0, 0.35);
    if (stitchEnabled && stitchAlpha > 0 && edgeDepth >= 10) {
      var stitchInsetPx = Math.max(1, Math.round(edgeDepth * 0.08));
      var sx = x + edgeDepth - stitchInsetPx;
      var sy = y + edgeDepth - stitchInsetPx;
      var sw = Math.max(1, w - (edgeDepth - stitchInsetPx) * 2);
      var sh = Math.max(1, h - (edgeDepth - stitchInsetPx) * 2);
      ctx.save();
      ctx.strokeStyle = "rgba(32, 26, 20, " + stitchAlpha + ")";
      ctx.lineWidth = Math.max(0.6, Math.min(1.1, edgeDepth * 0.022));
      ctx.lineCap = "butt";
      ctx.strokeRect(sx, sy, sw, sh);
      ctx.restore();
    }
  }

  function drawBiyeRectEdgeBand(ctx, bounds, options, fabricColor, fabricImg) {
    var x = bounds.x;
    var y = bounds.y;
    var w = bounds.w;
    var h = bounds.h;
    var edgeDepth = biyeVisualDepth(bounds, options);
    var rOuter = Math.round(edgeDepth * 1.72);

    var layer = createCanvas(ctx.canvas.width, ctx.canvas.height);
    var lctx = layer.getContext("2d", { willReadFrequently: true });
    var mask = createCanvas(ctx.canvas.width, ctx.canvas.height);
    var mctx = mask.getContext("2d", { willReadFrequently: true });
    var patternInfo = null;

    lctx.save();
    lctx.fillStyle = averageFabricImageColor(fabricImg, fabricColor || "#897b6d");
    lctx.fillRect(x, y, w, h);
    if (fabricImg) {
      patternInfo = drawBiyeFabricTextureFill(lctx, fabricImg, x, y, w, h, edgeDepth);
    }
    drawBiyeRectCordShading(lctx, x, y, w, h, rOuter, edgeDepth);

    mctx.save();
    mctx.strokeStyle = "#000";
    mctx.lineWidth = Math.max(2, edgeDepth * 2.08);
    mctx.lineJoin = "round";
    mctx.lineCap = "round";
    drawRoundedRect(mctx, x, y, w, h, rOuter);
    mctx.stroke();
    mctx.globalCompositeOperation = "destination-in";
    drawRoundedRect(mctx, x, y, w, h, rOuter);
    mctx.fillStyle = "#000";
    mctx.fill();
    mctx.restore();

    lctx.globalCompositeOperation = "destination-in";
    lctx.drawImage(mask, 0, 0);
    lctx.restore();
    if (patternInfo) {
      applyEdgeFabricFinish(lctx, x, y, w, h, edgeDepth, 1.05);
      recordEdgeTextureDebug(
        ctx,
        fabricImg,
        "rect-biye-fabric-texture",
        patternInfo,
        canvasRectLumaStats(layer, x, y, w, h)
      );
    }

    ctx.drawImage(layer, 0, 0);

    return edgeDepth;
  }

  function drawRectEdgeBand(ctx, bounds, depth, edgeType, fabricColor, fabricImg, options) {
    if (edgeType === "bordur") {
      drawBordurRectEdgeBand(ctx, bounds, depth, fabricColor, fabricImg, options);
      return;
    }
    if (edgeType === "biye") {
      drawBiyeRectEdgeBand(ctx, bounds, options, fabricColor, fabricImg);
      return;
    }

    var x = bounds.x;
    var y = bounds.y;
    var w = bounds.w;
    var h = bounds.h;
    var edgeDepth = edgeVisualDepth(bounds, depth, edgeType, options);

    ctx.save();
    ctx.beginPath();
    drawRoundedRect(ctx, x, y, w, h, 0);
    ctx.rect(x + edgeDepth, y + edgeDepth, Math.max(1, w - edgeDepth * 2), Math.max(1, h - edgeDepth * 2));
    drawFabricFill(ctx, fabricColor);
    ctx.restore();
  }

  function drawMaskEdgeBand(ctx, bounds, depth, edgeType, fabricColor, fabricImg, options) {
    if (!bounds || !bounds.mask) {
      drawRectEdgeBand(ctx, bounds, depth, edgeType, fabricColor, fabricImg, options);
      return;
    }
    if (edgeType === "biye") {
      drawBiyeRectEdgeBand(ctx, bounds, options, fabricColor, fabricImg);
      return;
    }

    var layer = createCanvas(ctx.canvas.width, ctx.canvas.height);
    var lctx = layer.getContext("2d", { willReadFrequently: true });
    var edgeDepth = edgeVisualDepth(bounds, depth, edgeType, options);
    var patternInfo = null;
    lctx.fillStyle = averageFabricImageColor(fabricImg, fabricColor || "#897b6d");
    lctx.fillRect(bounds.x, bounds.y, bounds.w, bounds.h);
    if (fabricImg) {
      patternInfo = drawFabricTextureFill(lctx, fabricImg, bounds.x, bounds.y, bounds.w, bounds.h, edgeDepth, "edge-hd");
    }
    drawMaskToLayer(lctx, bounds, 0);
    clearInnerRect(lctx, bounds, edgeDepth);
    if (patternInfo) {
      applyEdgeFabricFinish(lctx, bounds.x, bounds.y, bounds.w, bounds.h, edgeDepth, edgeType === "bordur" ? 1.06 : 1.05);
      recordEdgeTextureDebug(
        ctx,
        fabricImg,
        edgeType === "bordur" ? "mask-bordur-fabric-texture" : "mask-edge-fabric-texture",
        patternInfo,
        canvasRectLumaStats(layer, bounds.x, bounds.y, bounds.w, bounds.h)
      );
    }
    ctx.save();
    if (edgeType === "bordur") ctx.globalAlpha = 1;
    ctx.drawImage(layer, 0, 0);
    ctx.restore();

    if (edgeType === "bordur") {
      strokeInnerEdge(ctx, bounds.x, bounds.y, bounds.w, bounds.h, edgeDepth, 0, "rgba(70,58,48,0.12)", 0.75);
      drawBordurCornerSeams(ctx, bounds, edgeDepth);
    } else if (edgeType === "biye") {
      strokeInnerEdge(ctx, bounds.x, bounds.y, bounds.w, bounds.h, edgeDepth, Math.round(edgeDepth * 0.45), "rgba(48,38,31,0.16)", 0.8);
    }
  }

  function drawBodyPattern(ctx, bounds, depth, edgeType, patternImg, options) {
    if (!patternImg || !bounds) return;
    if (drawTextureTransfer(ctx, bounds, depth, edgeType, patternImg, options)) return;
    var safeDepth = finiteNumber(depth, 0);
    var inset = edgeType === "overlok" ? 0 : safeDepth;
    var x = finiteNumber(bounds.x, 0) + inset;
    var y = finiteNumber(bounds.y, 0) + inset;
    var bw = finiteNumber(bounds.w, 0);
    var bh = finiteNumber(bounds.h, 0);
    if (bw < 2 || bh < 2) return;
    var w = Math.max(1, bw - inset * 2);
    var h = Math.max(1, bh - inset * 2);
    var layer = createCanvas(ctx.canvas.width, ctx.canvas.height);
    var lctx = layer.getContext("2d", { willReadFrequently: true });
    var painted = false;
    try {
      painted = isOverlokCleanBody(options)
        ? drawImageCoverFromSourceInset(lctx, patternImg, Math.max(0.24, finiteNumber(options && options.textureSourceInsetPct, 0)), x, y, w, h)
        : drawImageCover(lctx, patternImg, x, y, w, h);
    } catch (err) {
      painted = false;
    }
    if (!painted) {
      var pattern = makeBodyPattern(lctx, patternImg, w, h);
      if (pattern) {
        lctx.fillStyle = pattern;
        lctx.fillRect(x, y, w, h);
      }
    }

    if (bounds.mask) {
      drawMaskToLayer(lctx, bounds, inset);
    }

    ctx.save();
    ctx.globalAlpha = 1;
    ctx.drawImage(layer, 0, 0);
    ctx.restore();

  }

  function drawOverlok(ctx, bounds) {
    /* v220: Overlok kenar dikişi — dikdörtgen halı için. */
    if (!bounds || !ctx) return;
    var x = finiteNumber(bounds.x, 0);
    var y = finiteNumber(bounds.y, 0);
    var w = finiteNumber(bounds.w, 0);
    var h = finiteNumber(bounds.h, 0);
    if (w < 8 || h < 8) return;
    var minSide = Math.min(w, h);
    var lineWidth = Math.max(1.5, Math.round(minSide * 0.006));
    var half = lineWidth * 0.5;
    ctx.save();
    ctx.lineWidth = lineWidth;
    ctx.strokeStyle = "rgba(46,38,30,0.46)";
    ctx.lineJoin = "miter";
    ctx.lineCap = "butt";
    ctx.strokeRect(x + half, y + half, Math.max(1, w - lineWidth), Math.max(1, h - lineWidth));
    ctx.lineWidth = Math.max(0.8, lineWidth * 0.55);
    ctx.strokeStyle = "rgba(232,222,206,0.42)";
    var off = lineWidth * 1.4;
    ctx.strokeRect(x + off, y + off, Math.max(1, w - off * 2), Math.max(1, h - off * 2));
    ctx.restore();
  }

  function MassimoPreview(options) {
    this.options = Object.assign({}, DEFAULTS, options || {});
    this.canvas = this.options.canvas;
    this.edgeType = this.options.edgeType || "biye";
    this.sourceImage = this.options.sourceImage || "";
    this.patternImage = this.options.patternImage || "";
    this.detailPatternImage = this.options.detailPatternImage || "";
    this.shape = normalizeShapeConfig(this.options.shape || { type: "dikdortgen" });
    this.design = normalizeDesignConfig(this.options.design || {});
    this.designImgs = {};
    this.fabric = null;
    this.fabricImg = null;
    this.edgeFabrics = { outer: null, inner: null };
    this.edgeFabricImgs = { outer: null, inner: null };
    this.sourceImg = null;
    this.patternImg = null;
    this.detailPatternImg = null;
    this.lastAnalysis = null;
    this._renderSerial = 0;
    this.owner = this.options.owner || null;
  }

  MassimoPreview.prototype.isCurrent = function () {
    return !this.owner || this.owner.__massimoPreview === this;
  };

  MassimoPreview.prototype.markRendered = function (serial, shapeConfig) {
    if (!this.canvas || serial !== this._renderSerial || !this.isCurrent()) return this.canvas;
    this.canvas.__massimoRenderedAt = Date.now();
    this.canvas.__massimoRenderedSerial = serial;
    this.canvas.__massimoRenderedEdgeType = this.edgeType || "";
    this.canvas.__massimoRenderedShapeType = shapeConfig && shapeConfig.type || "";
    this.canvas.__massimoRenderedWidth = this.canvas.width || 0;
    this.canvas.__massimoRenderedHeight = this.canvas.height || 0;
    return this.canvas;
  };

  MassimoPreview.prototype.setEdgeType = function (edgeType) {
    this.edgeType = edgeType || "biye";
    return this.render();
  };

  MassimoPreview.prototype.setSourceImage = function (src) {
    this.sourceImage = src || "";
    this.sourceImg = null;
    return this.render();
  };

  MassimoPreview.prototype.setPatternImage = function (src) {
    this.patternImage = src || "";
    this.patternImg = null;
    return this.render();
  };

  MassimoPreview.prototype.setShape = function (shape) {
    this.shape = normalizeShapeConfig(shape || { type: "dikdortgen" });
    return this.render();
  };

  MassimoPreview.prototype.setDesign = function (design) {
    this.design = normalizeDesignConfig(design || {});
    this.designImgs = {};
    return this.render();
  };

  MassimoPreview.prototype.setFabric = function (fabric) {
    this.fabric = fabric || null;
    this.fabricImg = null;
    this.edgeFabrics = { outer: null, inner: null };
    this.edgeFabricImgs = { outer: null, inner: null };
    return this.render();
  };

  MassimoPreview.prototype.setEdgeAndFabric = function (edgeType, fabric) {
    this.edgeType = edgeType || "biye";
    if (isDoubleBordur(this.edgeType)) {
      this.fabric = null;
      this.fabricImg = null;
      this.edgeFabrics = normalizeDoubleBordurFabrics(fabric);
      this.edgeFabricImgs = { outer: null, inner: null };
    } else {
      this.fabric = fabric || null;
      this.fabricImg = null;
      this.edgeFabrics = { outer: null, inner: null };
      this.edgeFabricImgs = { outer: null, inner: null };
    }
    return this.render();
  };

  MassimoPreview.prototype.renderArSurface = async function (options) {
    options = options || {};
    if (!this.canvas || !this.sourceImage) {
      return {
        canvas: null,
        last: clonePlain(window.__MASSIMO_OZEL_OLCU_LAST__ || null),
        fallbackUsed: true,
        sourceMode: "massimo-preview-transparent-ar",
        error: "ar-render-unavailable"
      };
    }

    var previousLast = window.__MASSIMO_OZEL_OLCU_LAST__;
    var tempCanvas = createCanvas(this.canvas.width || 1, this.canvas.height || 1);
    tempCanvas.__massimoArTransparentSurface = true;
    var tempOptions = arCleanTextureOptions(this.options || {}, this.edgeType);
    var requestedSource = options.sourceImage || this.sourceImage;
    var requestedPattern = options.patternImage || this.patternImage;
    var requestedDetailPattern = options.detailPatternImage || this.detailPatternImage;
    var requestedDetailStrength = Object.prototype.hasOwnProperty.call(options, 'detailPatternStrength')
      ? options.detailPatternStrength
      : this.options.detailPatternStrength;
    var tempPreview = new MassimoPreview(Object.assign({}, tempOptions, {
      owner: null,
      canvas: tempCanvas,
      sourceImage: requestedSource,
      patternImage: requestedPattern,
      detailPatternImage: requestedDetailPattern,
      detailPatternStrength: requestedDetailStrength,
      textureRepeatX: Object.prototype.hasOwnProperty.call(options, "textureRepeatX") ? options.textureRepeatX : tempOptions.textureRepeatX,
      textureRepeatY: Object.prototype.hasOwnProperty.call(options, "textureRepeatY") ? options.textureRepeatY : tempOptions.textureRepeatY,
      textureMicroDetailStrength: Object.prototype.hasOwnProperty.call(options, "textureMicroDetailStrength")
        ? options.textureMicroDetailStrength
        : (Object.prototype.hasOwnProperty.call(tempOptions, "zoomTextureMicroDetailStrength")
          ? tempOptions.zoomTextureMicroDetailStrength
          : tempOptions.textureMicroDetailStrength),
      textureMicroDetailLowPassRatio: Object.prototype.hasOwnProperty.call(options, "textureMicroDetailLowPassRatio")
        ? options.textureMicroDetailLowPassRatio
        : tempOptions.textureMicroDetailLowPassRatio,
      edgeType: this.edgeType,
      shape: clonePlain(this.shape),
      design: clonePlain(this.design),
      maxCanvasWidth: options.maxCanvasWidth || this.options.maxCanvasWidth,
      maxCanvasHeight: options.maxCanvasHeight || this.options.maxCanvasHeight,
      allowCanvasUpscale: true
    }));

    tempPreview.sourceImg = requestedSource === this.sourceImage ? this.sourceImg : null;
    tempPreview.patternImg = requestedPattern === this.patternImage ? this.patternImg : null;
    tempPreview.detailPatternImg = requestedDetailPattern === this.detailPatternImage ? this.detailPatternImg : null;
    tempPreview.fabricImg = this.fabricImg;
    tempPreview.edgeFabricImgs = clonePlain(this.edgeFabricImgs || {});

    var fabricPayload = Object.prototype.hasOwnProperty.call(options, "fabric")
      ? options.fabric
      : (isDoubleBordur(this.edgeType) ? clonePlain(this.edgeFabrics) : clonePlain(this.fabric));

    try {
      var canvas = await tempPreview.setEdgeAndFabric(this.edgeType, fabricPayload);
      var arLast = clonePlain(window.__MASSIMO_OZEL_OLCU_LAST__ || null);
      var scaledPreviewLast = scaledPreviewLastForAr(previousLast, this.canvas, canvas);
      var geometry = arGeometryFromLast(tempPreview.shape, scaledPreviewLast, canvas)
        || arGeometryFromLast(tempPreview.shape, arLast, canvas);
      if (geometry) applyArGeometryClip(canvas, geometry);
      var arMask = buildArMaskArtifact(canvas, geometry);
      canvas.__massimoArMask = arMask;
      canvas.__massimoArSourceMode = "massimo-preview-transparent-ar";
      if (arLast) {
        if (geometry) {
          arLast.shape = Object.assign({}, arLast.shape || {}, {
            type: geometry.type,
            presetKey: geometry.presetKey,
            presetLabel: geometry.presetLabel,
            x: geometry.x,
            y: geometry.y,
            w: geometry.w,
            h: geometry.h,
            widthCm: geometry.widthCm,
            lengthCm: geometry.lengthCm,
            fitMargin: geometry.fitMargin || null
          });
        }
        arLast.arSurface = {
          sourceMode: "massimo-preview-transparent-ar",
          alphaCoverage: arMask.coverage,
          alphaCoverage220: arMask.alphaCoverage220,
          alphaBBox: arMask.alphaBBox,
          alphaBBox220: arMask.alphaBBox220,
          cornerAlpha: arMask.cornerAlpha
        };
        arLast.shapeBoundsPx = arMask.shapeBoundsPx;
      }
      return {
        canvas: canvas,
        last: arLast,
        fallbackUsed: false,
        sourceMode: "massimo-preview-transparent-ar",
        renderTargetSide: Math.max(canvas.width || 0, canvas.height || 0),
        requestedImageWidth: Math.max(canvas.width || 0, canvas.height || 0),
        canvasWidth: canvas.width || 0,
        canvasHeight: canvas.height || 0,
        arMask: arMask,
        shapeBoundsPx: arMask.shapeBoundsPx,
        edgeMetrics: arLast && arLast.edgeMetrics || null,
        alphaCoverage: arMask.coverage,
        alphaCoverage220: arMask.alphaCoverage220,
        cornerAlpha: arMask.cornerAlpha
      };
    } finally {
      window.__MASSIMO_OZEL_OLCU_LAST__ = previousLast;
    }
  };

  MassimoPreview.prototype.render = async function () {
    if (!this.canvas || !this.sourceImage) return null;
    var serial = ++this._renderSerial;
    var ctx = this.canvas.getContext("2d", { willReadFrequently: true });
    if (!this.sourceImg) this.sourceImg = await loadImage(this.sourceImage);
    if (!this.isCurrent()) return this.canvas;
    if (this.patternImage && !this.patternImg) {
      try {
        this.patternImg = await loadImage(this.patternImage);
      } catch (err) {
        this.patternImg = null;
      }
    }
    if (this.detailPatternImage && !this.detailPatternImg) {
      try {
        this.detailPatternImg = await loadImage(this.detailPatternImage);
      } catch (err) {
        this.detailPatternImg = null;
      }
    }
    this.design = normalizeDesignConfig(this.design || {});
    await loadDesignImages(this, this.design);
    if (serial !== this._renderSerial || !this.isCurrent()) return this.canvas;
    if (isDoubleBordur(this.edgeType)) {
      var outerFabric = this.edgeFabrics && this.edgeFabrics.outer;
      var innerFabric = this.edgeFabrics && this.edgeFabrics.inner;
      if (outerFabric && outerFabric.image && !this.edgeFabricImgs.outer && !outerFabric._imageFailed) {
        try {
          this.edgeFabricImgs.outer = await loadImage(outerFabric.image);
        } catch (err) {
          outerFabric._imageFailed = true;
          this.edgeFabricImgs.outer = null;
        }
      }
      if (innerFabric && innerFabric.image && !this.edgeFabricImgs.inner && !innerFabric._imageFailed) {
        try {
          this.edgeFabricImgs.inner = await loadImage(innerFabric.image);
        } catch (err) {
          innerFabric._imageFailed = true;
          this.edgeFabricImgs.inner = null;
        }
      }
    } else if (this.fabric && this.fabric.image && !this.fabricImg && !this.fabric._imageFailed) {
      try {
        this.fabricImg = await loadImage(this.fabric.image);
      } catch (err) {
        this.fabric._imageFailed = true;
        this.fabricImg = null;
      }
    }
    if (serial !== this._renderSerial || !this.isCurrent()) return this.canvas;

    var size = fitSize(
      this.sourceImg.naturalWidth || this.sourceImg.width,
      this.sourceImg.naturalHeight || this.sourceImg.height,
      this.options.maxCanvasWidth,
      this.options.maxCanvasHeight
    );
    if (this.options.allowCanvasUpscale) {
      var sourceW = Math.max(1, this.sourceImg.naturalWidth || this.sourceImg.width || size.width);
      var sourceH = Math.max(1, this.sourceImg.naturalHeight || this.sourceImg.height || size.height);
      var maxW = Math.max(1, finiteNumber(this.options.maxCanvasWidth, sourceW));
      var maxH = Math.max(1, finiteNumber(this.options.maxCanvasHeight, sourceH));
      var upScale = Math.min(maxW / sourceW, maxH / sourceH);
      if (upScale > size.scale) {
        size = {
          width: Math.max(1, Math.round(sourceW * upScale)),
          height: Math.max(1, Math.round(sourceH * upScale)),
          scale: upScale
        };
      }
    }
    if (!this.isCurrent()) return this.canvas;
    this.canvas.width = size.width;
    this.canvas.height = size.height;

    ctx.clearRect(0, 0, size.width, size.height);
    ctx.fillStyle = "#f6f2eb";
    ctx.fillRect(0, 0, size.width, size.height);
    ctx.drawImage(this.sourceImg, 0, 0, size.width, size.height);
    // 2026-05-21: BASE TEXTURE PAINTING (Lasis: galeri-2 halı yüzeyine yedirilir)
    var baseTexturePaintEnabled = !!(this.options && this.options.baseTexturePaintEnabled);
    if (baseTexturePaintEnabled && this.patternImg && this.patternImg.naturalWidth) {
      var ptScale = clamp(finiteNumber(this.options.baseTexturePaintScale, 0.5), 0.1, 1);
      var ptW = Math.max(64, Math.round((this.patternImg.naturalWidth || 1000) * ptScale));
      var ptH = Math.max(64, Math.round((this.patternImg.naturalHeight || 1000) * ptScale));
      var ptTile = createCanvas(ptW, ptH);
      var ptctx = ptTile.getContext("2d", { willReadFrequently: true });
      ptctx.imageSmoothingEnabled = true;
      ptctx.imageSmoothingQuality = "high";
      ptctx.drawImage(this.patternImg, 0, 0, ptW, ptH);
      var pattern = ctx.createPattern(ptTile, "repeat");
      if (pattern) {
        ctx.save();
        ctx.globalCompositeOperation = "source-atop";
        ctx.fillStyle = pattern;
        ctx.fillRect(0, 0, size.width, size.height);
        ctx.restore();
        var shadingAlpha = clamp(finiteNumber(this.options.baseTexturePaintShadingStrength, 0.5), 0, 1);
        if (shadingAlpha > 0) {
          ctx.save();
          ctx.globalCompositeOperation = "multiply";
          ctx.globalAlpha = shadingAlpha;
          ctx.drawImage(this.sourceImg, 0, 0, size.width, size.height);
          ctx.restore();
        }
      }
    }
    try {
      this.canvas.__massimoSourceImageData = ctx.getImageData(0, 0, size.width, size.height);
    } catch (err) {
      this.canvas.__massimoSourceImageData = null;
    }

    var bounds = analyzeCarpet(this.canvas, ctx, this.options.bgThreshold);
    if (bounds && bounds.fallback) {
      if (this.edgeType === "bordur" || this.edgeType === "biye") {
        bounds = detectRectCarpetBoundsFromImage(
          this.canvas.__massimoSourceImageData,
          size.width,
          size.height,
          this.options.bgThreshold
        ) || bounds;
      }
      if (bounds && bounds.fallback === true) {
        bounds = {
          x: 3,
          y: 3,
          w: Math.max(1, size.width - 6),
          h: Math.max(1, size.height - 6),
          coverage: bounds.coverage,
          fallback: "full-source"
        };
      }
    } else {
      bounds = expandBounds(bounds, size.width, size.height, 0.025);
    }
    var shapeConfig = normalizeShapeConfig(this.shape);
    var designConfig = normalizeDesignConfig(this.design);
    var designActive = !!designConfig.active;
    var designShapeMode = designOuterShapeMode(designConfig);
    if (designActive && designShapeMode === "dikdortgen" && isShapeMode(shapeConfig)) {
      shapeConfig = Object.assign({}, shapeConfig, { type: "dikdortgen", presetKey: "", presetLabel: "", radii: null });
    } else if (designActive && designShapeMode !== "dikdortgen" && !isShapeMode(shapeConfig)) {
      shapeConfig = Object.assign({}, shapeConfig, { type: designShapeMode });
    }
    var shapeDesignActive = designActive && (!designConfig.cutoutOutsideRegions || designConfig.clipCutoutToShape) && designShapeMode !== "dikdortgen" && isShapeMode(shapeConfig);
    var shapeActive = !designActive && isShapeMode(shapeConfig);
    if (!shapeActive && !shapeDesignActive) {
      var physicalWidthCm = designActive ? designConfig.widthCm : shapeConfig.widthCm;
      var physicalLengthCm = designActive ? designConfig.lengthCm : shapeConfig.lengthCm;
      bounds = physicalAspectBounds(bounds, physicalWidthCm, physicalLengthCm, size.width, size.height);
    }
    this.lastAnalysis = bounds;
    window.__MASSIMO_OZEL_OLCU_LAST__ = {
      edgeType: this.edgeType,
      shapeType: shapeConfig.type,
      shapePreset: shapeConfig.presetKey || shapeConfig.presetLabel || "",
      sourceImage: this.sourceImage,
      patternImage: this.patternImage,
      fabric: this.fabric ? (this.fabric.id || this.fabric.label || "") : "",
      fabricImage: this.fabric ? (this.fabric.image || "") : "",
      fabrics: isDoubleBordur(this.edgeType) ? {
        outer: this.edgeFabrics.outer ? (this.edgeFabrics.outer.id || this.edgeFabrics.outer.label || "") : "",
        inner: this.edgeFabrics.inner ? (this.edgeFabrics.inner.id || this.edgeFabrics.inner.label || "") : ""
      } : null,
      fabricImages: isDoubleBordur(this.edgeType) ? {
        outer: this.edgeFabrics.outer ? (this.edgeFabrics.outer.image || "") : "",
        inner: this.edgeFabrics.inner ? (this.edgeFabrics.inner.image || "") : ""
      } : null,
      fabricSize: this.fabricImg ? {
        width: this.fabricImg.naturalWidth || this.fabricImg.width || 0,
        height: this.fabricImg.naturalHeight || this.fabricImg.height || 0
      } : null,
      bounds: debugBounds(bounds),
      design: designActive ? {
        templateId: designConfig.templateId,
        label: designConfig.label,
        topology: designConfig.topology,
        premium: !!designConfig.premium,
        premiumVariant: designConfig.premiumVariant || null,
        stitchesVisible: designConfig.stitchesVisible !== false,
        cutoutOutsideRegions: !!designConfig.cutoutOutsideRegions,
        clipCutoutToShape: !!designConfig.clipCutoutToShape,
        preserveRegionGaps: !!designConfig.preserveRegionGaps,
        cleanRegionTexture: !!designConfig.cleanRegionTexture,
        cleanRegionBaseLuma: !!designConfig.cleanRegionBaseLuma,
        pieceEdgeMode: designConfig.pieceEdgeMode || "",
        seamCount: designConfig.designTopology && designConfig.designTopology.seams ? designConfig.designTopology.seams.length : 0,
        regions: (designConfig.regions || []).length,
        assignments: Object.keys(designConfig.assignments || {}).map(function (key) {
          var item = designConfig.assignments[key] || {};
          return key + ":" + (item.label || item.id || "");
        })
      } : null,
      texture: null,
      edgeTexture: null,
      canvas: { width: this.canvas.width, height: this.canvas.height }
    };

    var isBordurEdge = this.edgeType === "bordur" || isDoubleBordur(this.edgeType);
    var pct = isBordurEdge ? this.options.bordurDepthPct : this.options.biyeDepthPct;
    pct = finiteNumber(pct, isBordurEdge ? DEFAULTS.bordurDepthPct : DEFAULTS.biyeDepthPct);
    var minDepth = this.edgeType === "biye" ? 6 : 10;
    var depth = Math.max(minDepth, Math.round(Math.min(bounds.w, bounds.h) * pct));
    var renderOptions = withPhysicalTextureScaleOptions(this.options, shapeConfig, designConfig);
    var bordurCover = null;
    if (this.edgeType === "bordur" && !shapeActive && !shapeDesignActive) {
      bordurCover = bordurCoverMetrics(bounds, depth, this.options, this.canvas.__massimoSourceImageData);
      renderOptions = withPhysicalTextureScaleOptions(
        Object.assign({}, this.options, { __bordurCoverDepth: bordurCover.depth }),
        shapeConfig,
        designConfig
      );
    }
    // 2026-05-19: barcelona-detail-pattern — bind loaded detail pattern image
    // so drawBarcelona160x230Surface can inject high-frequency structure from a
    // secondary pattern (e.g. Lasis weave) while keeping primary pattern colors.
    if (this.detailPatternImg) renderOptions._detailPatternImg = this.detailPatternImg;
    var visualDepth = edgeVisualDepth(bounds, depth, this.edgeType, renderOptions);
    var referenceDepth = bordurReferenceDepth(bounds, this.options);
    window.__MASSIMO_OZEL_OLCU_LAST__.edgeMetrics = {
      rawDepth: depth,
      visualDepth: visualDepth,
      bordurReferenceDepth: referenceDepth,
      ratioToBordur: referenceDepth ? visualDepth / referenceDepth : null,
      bordurCover: bordurCover
    };
    if (designActive && window.__MASSIMO_OZEL_OLCU_LAST__.design) {
      window.__MASSIMO_OZEL_OLCU_LAST__.design.regionCount = designConfig.regionCount || (designConfig.regions || []).length;
      window.__MASSIMO_OZEL_OLCU_LAST__.design.widthCm = designConfig.widthCm;
      window.__MASSIMO_OZEL_OLCU_LAST__.design.lengthCm = designConfig.lengthCm;
      window.__MASSIMO_OZEL_OLCU_LAST__.design.regionPixels = designRegionPixels(
        designConfig,
        bounds,
        this.edgeType === "overlok" ? 0 : visualDepth
      );
    }

    if (shapeDesignActive) {
      var designCutout = !!designConfig.cutoutOutsideRegions;
      var designGeometry = makeShapeGeometry(shapeConfig, bounds, size.width, size.height);
      var designShapeDepthSeed = shapeEdgeDepthSeed(designGeometry, depth, this.edgeType, this.options);
      var designShapeDepth = edgeVisualDepth(designGeometry, designShapeDepthSeed, this.edgeType, this.options);
      if (this.edgeType === "biye" && !designCutout) {
        designGeometry = insetShapeGeometry(designGeometry, Math.max(3, Math.round(designShapeDepth * 0.35)));
        designShapeDepthSeed = shapeEdgeDepthSeed(designGeometry, depth, this.edgeType, this.options);
        designShapeDepth = edgeVisualDepth(designGeometry, designShapeDepthSeed, this.edgeType, this.options);
      }
      var designBodyDepth = (this.edgeType === "overlok" || designCutout) ? 0 : designShapeDepth;
      var designClip = designCutout && !designConfig.clipCutoutToShape ? null : shapeMaskAnalysis(designGeometry, designBodyDepth);
      var cutoutPieceEdge = designCutout ? {
        edgeType: this.edgeType,
        fabric: this.fabric,
        fabricImg: this.fabricImg,
        options: renderOptions
      } : null;
      window.__MASSIMO_OZEL_OLCU_LAST__.shape = {
        type: designGeometry.type,
        presetKey: designGeometry.presetKey || "",
        presetLabel: designGeometry.presetLabel || "",
        x: designGeometry.x,
        y: designGeometry.y,
        w: designGeometry.w,
        h: designGeometry.h,
        widthCm: designGeometry.widthCm,
        lengthCm: designGeometry.lengthCm,
        outlineCommandCount: hasOutlineCommands(designGeometry) ? designGeometry.outlineCommands.length : 0,
        physicalAspectRatio: designGeometry._physicalAspectRatio || null,
        renderedBounds: isAmorfGeometry(designGeometry) ? amorfFitBox(designGeometry, 0) : null,
        fitMargin: designGeometry.fitMargin || null
      };
      window.__MASSIMO_OZEL_OLCU_LAST__.edgeMetrics.visualDepth = designShapeDepth;
      window.__MASSIMO_OZEL_OLCU_LAST__.edgeMetrics.ratioToBordur = referenceDepth ? designShapeDepth / referenceDepth : null;
      if (window.__MASSIMO_OZEL_OLCU_LAST__.design) {
        window.__MASSIMO_OZEL_OLCU_LAST__.design.shapeType = designGeometry.type;
        window.__MASSIMO_OZEL_OLCU_LAST__.design.regionCount = designConfig.regionCount || (designConfig.regions || []).length;
        window.__MASSIMO_OZEL_OLCU_LAST__.design.widthCm = designConfig.widthCm;
        window.__MASSIMO_OZEL_OLCU_LAST__.design.lengthCm = designConfig.lengthCm;
        window.__MASSIMO_OZEL_OLCU_LAST__.design.regionPixels = designRegionPixels(
          designConfig,
          designGeometry,
          designBodyDepth
        );
      }

      paintPreviewStage(ctx, size.width, size.height);
      if (this.edgeType !== "overlok" && !designCutout) {
        drawShapeShadow(ctx, designGeometry);
      }

      if (this.edgeType === "overlok") {
        drawDesignBody(ctx, designConfig, designGeometry, 0, this.patternImg, this.designImgs, renderOptions, size.width, size.height, designClip, cutoutPieceEdge);
        if (!designConfig.cutoutOutsideRegions) {
          healOverlokShapeBodyEdges(ctx, designGeometry);
          drawShapeOverlok(ctx, designGeometry);
        }
        if (window.__MASSIMO_OZEL_OLCU_LAST__.design) {
          window.__MASSIMO_OZEL_OLCU_LAST__.design.coverage = this.canvas.__massimoDesignCoverage || null;
        }
        window.__MASSIMO_OZEL_OLCU_LAST__.texture = this.canvas.__massimoTextureDebug || null;
        return this.markRendered(serial, shapeConfig);
      }

      drawDesignBody(ctx, designConfig, designGeometry, designBodyDepth, this.patternImg, this.designImgs, renderOptions, size.width, size.height, designClip, cutoutPieceEdge);
      if (!designConfig.cutoutOutsideRegions && this.edgeType === "biye") {
        healBiyeShapeBodyEdges(ctx, designGeometry, designShapeDepth);
      }
      if (window.__MASSIMO_OZEL_OLCU_LAST__.design) {
        window.__MASSIMO_OZEL_OLCU_LAST__.design.coverage = this.canvas.__massimoDesignCoverage || null;
      }
      if (!designConfig.cutoutOutsideRegions) {
        drawShapeEdgeBand(
          ctx,
          designGeometry,
          designShapeDepthSeed,
          this.edgeType,
          this.fabric,
          this.fabricImg,
          this.edgeFabrics,
          this.edgeFabricImgs,
          renderOptions
        );
      }
      if (!designConfig.cutoutOutsideRegions && this.edgeType === "biye") {
        healBiyeShapeBodyEdges(ctx, designGeometry, designShapeDepth);
      }
      window.__MASSIMO_OZEL_OLCU_LAST__.texture = this.canvas.__massimoTextureDebug || null;
      return this.markRendered(serial, shapeConfig);
    }

    if (designActive) {
      var rectCutoutPieceEdge = designConfig.cutoutOutsideRegions ? {
        edgeType: this.edgeType,
        fabric: this.fabric,
        fabricImg: this.fabricImg,
        options: renderOptions
      } : null;
      paintPreviewStage(ctx, size.width, size.height);
      if (!designConfig.cutoutOutsideRegions) drawCarpetShadow(ctx, bounds);

      if (this.edgeType === "overlok") {
        drawDesignBody(ctx, designConfig, bounds, 0, this.patternImg, this.designImgs, renderOptions, size.width, size.height, null, rectCutoutPieceEdge);
        if (!designConfig.cutoutOutsideRegions) {
          healOverlokRectBodyEdges(ctx, bounds);
          drawOverlok(ctx, bounds);
        }
        if (window.__MASSIMO_OZEL_OLCU_LAST__.design) {
          window.__MASSIMO_OZEL_OLCU_LAST__.design.coverage = this.canvas.__massimoDesignCoverage || null;
        }
        window.__MASSIMO_OZEL_OLCU_LAST__.texture = this.canvas.__massimoTextureDebug || null;
        return this.markRendered(serial, shapeConfig);
      }

	      drawDesignBody(ctx, designConfig, bounds, designConfig.cutoutOutsideRegions ? 0 : visualDepth, this.patternImg, this.designImgs, renderOptions, size.width, size.height, null, rectCutoutPieceEdge);
	      if (!designConfig.cutoutOutsideRegions && this.edgeType === "biye") {
	        healBiyeRectBodyEdges(ctx, bounds, visualDepth);
	      }
	      if (window.__MASSIMO_OZEL_OLCU_LAST__.design) {
	        window.__MASSIMO_OZEL_OLCU_LAST__.design.coverage = this.canvas.__massimoDesignCoverage || null;
	      }
      if (!designConfig.cutoutOutsideRegions && isDoubleBordur(this.edgeType)) {
        clearEdgeBandToStage(ctx, bounds, visualDepth);
        drawDoubleBordurMaskEdgeBand(ctx, bounds, depth, this.edgeFabrics, this.edgeFabricImgs);
      } else if (!designConfig.cutoutOutsideRegions && this.fabric) {
        drawMaskEdgeBand(ctx, bounds, depth, this.edgeType, swatchColor(this.fabric), this.fabricImg, renderOptions);
      }
      if (!designConfig.cutoutOutsideRegions && this.edgeType === "biye") {
        healBiyeRectBodyEdges(ctx, bounds, visualDepth);
      }
      window.__MASSIMO_OZEL_OLCU_LAST__.texture = this.canvas.__massimoTextureDebug || null;
      return this.markRendered(serial, shapeConfig);
    }

    if (shapeActive) {
      var geometry = makeShapeGeometry(shapeConfig, bounds, size.width, size.height);
      var shapeDepthSeed = shapeEdgeDepthSeed(geometry, depth, this.edgeType, this.options);
      var shapeDepth = edgeVisualDepth(geometry, shapeDepthSeed, this.edgeType, this.options);
      if (this.edgeType === "biye") {
        geometry = insetShapeGeometry(geometry, Math.max(3, Math.round(shapeDepth * 0.35)));
        shapeDepthSeed = shapeEdgeDepthSeed(geometry, depth, this.edgeType, this.options);
        shapeDepth = edgeVisualDepth(geometry, shapeDepthSeed, this.edgeType, this.options);
      }
      window.__MASSIMO_OZEL_OLCU_LAST__.shape = {
        type: geometry.type,
        presetKey: geometry.presetKey || "",
        presetLabel: geometry.presetLabel || "",
        x: geometry.x,
        y: geometry.y,
        w: geometry.w,
        h: geometry.h,
        widthCm: geometry.widthCm,
        lengthCm: geometry.lengthCm,
        outlineCommandCount: hasOutlineCommands(geometry) ? geometry.outlineCommands.length : 0,
        physicalAspectRatio: geometry._physicalAspectRatio || null,
        renderedBounds: isAmorfGeometry(geometry) ? amorfFitBox(geometry, 0) : null,
        fitMargin: geometry.fitMargin || null
      };
      window.__MASSIMO_OZEL_OLCU_LAST__.edgeMetrics.visualDepth = shapeDepth;
      window.__MASSIMO_OZEL_OLCU_LAST__.edgeMetrics.ratioToBordur = referenceDepth ? shapeDepth / referenceDepth : null;

      paintPreviewStage(ctx, size.width, size.height);
      if (this.edgeType !== "overlok") {
        drawShapeShadow(ctx, geometry);
      }

      if (this.edgeType === "overlok") {
        drawShapeBodyPattern(ctx, geometry, 0, this.edgeType, this.patternImg, renderOptions);
        healOverlokShapeBodyEdges(ctx, geometry);
        if (!usesBodyRepeatSurface(renderOptions)) {
          repairBarcelonaOverlokShapeSideEdges(ctx, geometry, renderOptions, true);
        } else {
          repairBarcelonaOverlokShapeSideEdges(ctx, geometry, renderOptions);
        }
        drawShapeOverlok(ctx, geometry);
        window.__MASSIMO_OZEL_OLCU_LAST__.texture = this.canvas.__massimoTextureDebug || null;
        return this.markRendered(serial, shapeConfig);
      }

	      drawShapeBodyPattern(ctx, geometry, shapeDepth, this.edgeType, this.patternImg, renderOptions);
	      if (this.edgeType === "biye") {
	        healBiyeShapeBodyEdges(ctx, geometry, shapeDepth);
	      }
	      drawShapeEdgeBand(
        ctx,
        geometry,
        shapeDepthSeed,
        this.edgeType,
        this.fabric,
        this.fabricImg,
        this.edgeFabrics,
        this.edgeFabricImgs,
        renderOptions
      );
      if (this.edgeType === "biye") {
        healBiyeShapeBodyEdges(ctx, geometry, shapeDepth);
      }
      window.__MASSIMO_OZEL_OLCU_LAST__.texture = this.canvas.__massimoTextureDebug || null;
      return this.markRendered(serial, shapeConfig);
    }

    if (this.edgeType !== "bordur" && !isDoubleBordur(this.edgeType)) {
    if (!isDoubleBordur(this.edgeType)) {
      paintPreviewStage(ctx, size.width, size.height);
      drawCarpetShadow(ctx, bounds);
    }
    }

    if (this.edgeType === "overlok") {
      drawBodyPattern(ctx, bounds, 0, this.edgeType, this.patternImg, renderOptions);
      healOverlokRectBodyEdges(ctx, bounds);
      drawOverlok(ctx, bounds);
      window.__MASSIMO_OZEL_OLCU_LAST__.texture = this.canvas.__massimoTextureDebug || null;
      return this.markRendered(serial, shapeConfig);
    }

	    var bodyDepth = this.edgeType === "biye" || this.edgeType === "bordur" || isDoubleBordur(this.edgeType) ? visualDepth : depth;
	    drawBodyPattern(ctx, bounds, bodyDepth, this.edgeType, this.patternImg, renderOptions);
	    if (this.edgeType === "biye") {
	      healBiyeRectBodyEdges(ctx, bounds, bodyDepth);
	    }
	    if (isDoubleBordur(this.edgeType)) {
      clearEdgeBandToStage(ctx, bounds, bodyDepth);
      drawDoubleBordurMaskEdgeBand(ctx, bounds, depth, this.edgeFabrics, this.edgeFabricImgs);
	    } else if (this.fabric) {
	      drawMaskEdgeBand(ctx, bounds, depth, this.edgeType, swatchColor(this.fabric), this.fabricImg, renderOptions);
	    }
	    if (this.edgeType === "biye") {
	      healBiyeRectBodyEdges(ctx, bounds, bodyDepth);
	    }
	    window.__MASSIMO_OZEL_OLCU_LAST__.texture = this.canvas.__massimoTextureDebug || null;
    return this.markRendered(serial, shapeConfig);
  };

  window.MassimoOzelOlcuPreview = MassimoPreview;
})();

;
(function(){
  "use strict";
  var MARKER = "v289-cordoba-stale-cache-guard-1780516938272";
  if (window.__MASSIMO_CORDOBA_STALE_CACHE_GUARD__ === MARKER) return;
  window.__MASSIMO_CORDOBA_STALE_CACHE_GUARD__ = MARKER;
  if (!/\/products\/massimo-carpet-cordoba-110(?:$|[/?#])/.test(window.location.pathname || "")) return;
  var nativeSetTimeout = window.setTimeout;
  window.setTimeout = function(fn, delay) {
    try {
      if (delay === 650 && typeof fn === "function" && String(fn).indexOf("ensureAllDesignRegionsAssigned") >= 0) {
        return nativeSetTimeout(function(){}, delay);
      }
    } catch (e) {}
    return nativeSetTimeout.apply(window, arguments);
  };
  function activateDesignMode() {
    var toggle = document.querySelector("[data-massimo-design-toggle]");
    if (toggle && !toggle.disabled && !toggle.classList.contains("is-active")) {
      try { toggle.click(); } catch (e) {}
    }
  }
  [120, 600, 1500, 3000, 6000].forEach(function(delay) { nativeSetTimeout(activateDesignMode, delay); });
  if (window.MutationObserver) {
    var observer = new MutationObserver(activateDesignMode);
    try { observer.observe(document.documentElement, { childList: true, subtree: true }); } catch (e) {}
    nativeSetTimeout(function(){ try { observer.disconnect(); } catch (e) {} }, 8000);
  }
})();
